/**
 * Dashboard consolidé : une solution de mesure croisée avec les régies publicitaires.
 *
 * **La vue s'ouvre sur la synthèse** — une ligne par levier, une colonne par métrique, sur
 * toute la période. C'est la réponse à la question qu'on se pose en arrivant ; le découpage
 * par jour, lui, demande de parcourir trente colonnes avant de la trouver. Les autres vues
 * lisent les mêmes blocs, déployés autrement.
 *
 * Le tableau chronologique se lit de gauche à droite : les libellés des lignes d'abord, puis
 * une tranche de temps par colonne, et le total en dernier. Les deux colonnes de bord sont `sticky` —
 * sur quatre-vingt-dix jours, un chiffre sans son libellé ni son total ne veut plus rien
 * dire.
 *
 * **La forme du tableau est décidée par le serveur** : l'ordre des lignes, mais aussi les
 * lignes d'en-tête, qui dépendent de l'échelle — « Jour semaine » n'a pas de sens en face
 * d'un mois. L'écran les parcourt telles quelles ; les recomposer ici ferait diverger le
 * tableau de la définition qui fait foi.
 *
 * Changer d'échelle **ne regroupe rien à l'écran** : la demande repart au serveur, qui
 * réinterroge GA4 tranche par tranche. Additionner des sessions quotidiennes pour en faire
 * une semaine les surestimerait, GA4 les dédupliquant.
 *
 * Les blocs se suivent **dans le même tableau**,
 * sous une seule rangée d'en-têtes. Deux tableaux empilés auraient chacun leur barre de
 * défilement horizontale : elles se désynchroniseraient au premier glissement, et les deux
 * blocs cesseraient de porter sur les mêmes jours.
 *
 * Les blocs de détail sont des **sous-leviers** : le serveur les rattache à l'un des deux
 * totaux par `parent`, et ils suivent son repliement. Eux-mêmes ne se replient pas — un
 * sous-levier replié isolément laisserait un intertitre orphelin sous un total déplié,
 * sans rien dire de ce qu'il cache.
 *
 * Cette vue se passe de la barre latérale : le tableau prend toute la largeur, et les
 * deux seuls réglages qui le concernent — la période et la propriété — sont posés au-dessus
 * de lui. La période reste l'état global de l'application : la changer ici la change
 * partout, comme le faisait le menu de la barre latérale.
 *
 * **Les comptes publicitaires croisés ne se choisissent pas ici.** Ils découlent du site
 * qui contient la propriété GA4, et c'est le serveur qui fait cette lecture : l'écran ne
 * transmet qu'une propriété et se contente de rappeler ce qui a été croisé. Rouvrir une
 * sélection à cet endroit recréerait une seconde vérité, qui se périmerait au premier
 * compte ajouté au site.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import AnalyseIA from './AnalyseIA';
import axios from 'axios';
import config from '../config';
import { Loader, RefreshCw, Globe, ChevronDown, ChevronRight, Settings, X, Layers, Info, PencilLine, Wallet } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { PartialDataNotice } from './PartialDataNotice';
import PeriodSelector from './PeriodSelector';
import { BASES_A_DATES_EXPLICITES, bornesDeComparaison } from '../utils/comparaisonPeriode';
import { sujetDeNote, couleurDeNote } from '../constants/noteTopics';
import pianoLogo from '../logos/piano-analytics.png';
import matomoLogo from '../logos/matomo.png';
import ProprieteCombineeModal from './consolide/ProprieteCombineeModal';
import SaisiesModal from './consolide/SaisiesModal';

const API_URL = config.API_URL;

/** Motif affiché si le serveur refuse sans en donner : mieux vaut vague que muet. */
const REFUS_PAR_DEFAUT = "Ce dashboard ne vous a pas été ouvert par votre agence.";

/** Hauteur d'une ligne de métrique, en pixels. */
const HAUTEUR_LIGNE = 34;

/** Hauteur d'une ligne d'en-tête, en pixels. */
const HAUTEUR_ENTETE = 28;

/**
 * Teinte des bandeaux d'en-tête, **opaque**.
 *
 * C'est exactement `rgba(251, 146, 60, 0.12)` et `rgba(251, 146, 60, 0.35)` aplatis sur blanc :
 * à l'œil, rien ne change. Mais un bandeau figé ne repose plus sur la page, il repose sur les
 * lignes qui défilent dessous — une teinte translucide y laisserait voir les chiffres à travers
 * les dates.
 */
const FOND_ENTETE = '#fff2e8';
const FOND_ENTETE_WEEKEND = '#fed9bb';
/** `rgba(251, 146, 60, 0.28)` aplati : la colonne d'atterrissage de la projection. */
const FOND_ENTETE_FORT = '#fee0c8';

/**
 * Le gel des en-têtes : des barres tenues en haut de l'écran, et le corps qui défile dessous.
 *
 * ## Pourquoi l'en-tête vit **hors** du corps du tableau
 *
 * `overflow-x: auto` — dont aucun de ces tableaux ne peut se passer, ils font tous plusieurs
 * écrans de large — fait du corps un scrollport sur **les deux** axes, et un `position: sticky`
 * posé à l'intérieur se règle sur lui, jamais sur l'écran. Or le corps ne défile pas
 * verticalement de son côté : c'est `.main-content` qui défile. Un en-tête figé depuis
 * l'intérieur reste donc collé au haut du tableau, et part avec lui.
 *
 * Borner la hauteur du corps — ce que faisait `CADRE_FIGE`, supprimé avec ce constat — ne fait
 * que déplacer la limite : le gel tient tant qu'on défile **dans** le tableau, et cesse dès que
 * son ascenseur arrive en bout et que la page reprend la main. Mesuré sur un gabarit
 * reproduisant la structure réelle : la case d'angle, encore à 200 px du haut quand l'ascenseur
 * interne touche le fond, tombe ensuite à −40 px, hors cadre. C'est exactement ce qu'on voyait
 * en bas de chaque tableau.
 *
 * ## Le prix, et comment il est payé
 *
 * Deux scrollports distincts ne partagent pas leur position horizontale : le corps recopie la
 * sienne dans chaque bande marquée `data-suit-le-corps`, à chaque défilement. `overflow-x:
 * hidden` les garde manœuvrables par `scrollLeft` sans donner au lecteur un second ascenseur.
 *
 * ⚠️ L'ascenseur horizontal est désormais au **bas du corps** et non au bas de l'écran : sur
 * quatre-vingt-dix colonnes, il faut descendre pour l'attraper. La molette et le pavé tactile,
 * eux, défilent de n'importe où.
 */
const suivreDefilement = (e) => {
  const x = e.currentTarget.scrollLeft;
  e.currentTarget.parentElement?.querySelectorAll('[data-suit-le-corps]')
    .forEach(bande => { bande.scrollLeft = x; });
};

/**
 * Une rangée d'en-tête tenue en haut de l'écran pendant le défilement vertical.
 *
 * `rang` est son étage dans la pile, compté en lignes d'en-tête : le décalage se cumule pour
 * qu'une seconde barre se pose sous la première au lieu de la recouvrir. `null` vaut « suit le
 * tableau sans se figer » — ce que font les repères intermédiaires du tableau chronologique,
 * qui se déduisent tous de la date et ne valent pas la hauteur d'écran qu'ils coûteraient.
 *
 * `zIndex: 3` : au-dessus des colonnes de bord du corps, qui portent 2 et doivent recouvrir
 * les lignes de données sans recouvrir l'en-tête.
 */
const BarreFigee = ({ rang = 0, children }) => (
  <div data-suit-le-corps="" style={{
    overflowX: 'hidden',
    ...(rang === null ? {} : { position: 'sticky', top: rang * HAUTEUR_ENTETE, zIndex: 3 })
  }}>
    <div style={{ display: 'flex', width: 'max-content' }}>{children}</div>
  </div>
);

/**
 * Un bloc plus large que l'écran qui suit le corps sans ascenseur propre et sans se figer.
 *
 * `.main-content` étant en `overflow-x: hidden`, un bloc en `width: max-content` laissé hors
 * d'un scrollport serait rogné net et sa droite deviendrait inatteignable.
 */
const BandeSuivie = ({ children }) => (
  <div data-suit-le-corps="" style={{ overflowX: 'hidden' }}>{children}</div>
);

/** Le corps d'un tableau : seul à porter un ascenseur, et il entraîne les bandes au-dessus. */
const CorpsDefilant = ({ children }) => (
  <div style={{ overflowX: 'auto' }} onScroll={suivreDefilement}>{children}</div>
);

/**
 * Identifiant de cet onglet, stable tant qu'il reste ouvert.
 *
 * Il accompagne chaque demande de tableau pour que le serveur sache quelles requêtes se
 * succèdent : une nouvelle demande du **même onglet** périme la précédente et interrompt son
 * calcul. Sans cette distinction, deux onglets ouverts sur le même tableau s'annuleraient
 * l'un l'autre.
 *
 * `sessionStorage` plutôt qu'une variable : il survit à un rechargement de page, là où un
 * onglet rechargé repartirait sinon avec une identité neuve, laissant le serveur poursuivre
 * un calcul que plus personne n'attend.
 */
const ongletId = (() => {
  const nouveau = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
  try {
    const existant = sessionStorage.getItem('kokpit.ongletId');
    if (existant) return existant;
    const cree = nouveau();
    sessionStorage.setItem('kokpit.ongletId', cree);
    return cree;
  } catch {
    // Navigation privée ou stockage refusé : une identité par chargement reste préférable à
    // pas d'identité du tout.
    return nouveau();
  }
})();

/** Échelles de lecture. Les clés sont celles attendues par la route `/timeline`. */
const GRANULARITES = [
  { value: 'jour', label: 'Jour' },
  { value: 'semaine', label: 'Semaine' },
  { value: 'mois', label: 'Mois' },
  { value: 'annee', label: 'Année' }
];

/**
 * Sens de lecture d'une évolution, par ligne.
 *
 * `+1` : une hausse est une bonne nouvelle. `-1` : c'est une mauvaise. Les lignes absentes
 * ne sont pas colorées — une variation d'impressions ou de CPC n'est bonne ou mauvaise
 * qu'au regard du reste, et teinter à tort oriente la lecture plus sûrement qu'un chiffre
 * nu ne l'égare.
 */
const SENS_EVOLUTION = {
  revenue: 1,
  transactions: 1,
  // Modèle Lead : une conversion de plus est une bonne nouvelle, au même titre qu'une vente.
  conversions: 1,
  sessions: 1,
  cost: -1,
  cos: -1,
  cpa: -1,
  // Saisies à la main : le back-office se lit comme la mesure, et une réalisation qui progresse
  // est une bonne nouvelle. `objRevenue` et `objTransactions` restent absents à dessein — un
  // objectif relevé n'est ni une bonne ni une mauvaise nouvelle, le colorer trancherait à tort.
  boRevenue: 1,
  boTransactions: 1,
  realisationMesure: 1,
  realisationBo: 1,
  boIcCa: -1
};

const MOIS_LONGS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/**
 * Libellé d'une période, dans l'unité qu'elle épouse.
 *
 * Un mois entier s'écrit « août », une semaine ISO complète « S36 », et tout le reste en
 * plage de dates. Écrire « 01/08 → 31/08 » là où « août » suffit oblige le lecteur à
 * reconstituer lui-même ce que la période recouvre, et à le refaire pour la colonne d'à
 * côté avant de pouvoir les comparer.
 *
 * Tout est lu en UTC : les bornes arrivent en `YYYY-MM-DD`, sans heure.
 */
const libellePeriode = (debut, fin) => {
  if (!debut || !fin) return '';
  const d = new Date(`${debut}T00:00:00Z`);
  const f = new Date(`${fin}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime())) return `${debut} → ${fin}`;

  const dernierJourDuMois = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  const memeMois = d.getUTCFullYear() === f.getUTCFullYear() && d.getUTCMonth() === f.getUTCMonth();
  if (memeMois && d.getUTCDate() === 1 && f.getUTCDate() === dernierJourDuMois) {
    return MOIS_LONGS[d.getUTCMonth()];
  }

  // Semaine ISO complète : sept jours à partir d'un lundi
  const joursCouverts = Math.round((f - d) / 86400000) + 1;
  if (joursCouverts === 7 && d.getUTCDay() === 1) {
    const jeudi = new Date(d);
    jeudi.setUTCDate(d.getUTCDate() + 3);
    const premierJanvier = new Date(Date.UTC(jeudi.getUTCFullYear(), 0, 1));
    return `S${Math.ceil((((jeudi - premierJanvier) / 86400000) + 1) / 7)}`;
  }

  const court = (x) => `${String(x.getUTCDate()).padStart(2, '0')}/${String(x.getUTCMonth() + 1).padStart(2, '0')}`;
  return `${court(d)} → ${court(f)}`;
};

/**
 * Les règles de comparaison vivent dans `utils/comparaisonPeriode.js`.
 *
 * Le sélecteur de période les applique aussi, pour montrer la référence avant qu'on valide.
 * Deux implémentations auraient fini par diverger, et l'aperçu aurait alors promis une période
 * que ce tableau ne serait pas allé chercher.
 */

/**
 * Largeur de la colonne des libellés, en pixels.
 *
 * Alignée sur celle de la barre latérale du reste du site (`.app-sidebar` dans `App.css`,
 * 220 px) : cette vue s'affiche sans barre latérale, et la première colonne en tient lieu
 * visuellement. Une largeur différente ferait sauter le bord gauche d'un écran à l'autre.
 */
const LARGEUR_LIBELLES = 220;

/** Largeur d'une colonne de jour, en pixels. */
const LARGEUR_COLONNE = 108;

/** Largeur de la colonne des totaux, en dernière position. */
const LARGEUR_TOTAL = 120;

/**
 * Colonnes de la synthèse, dans l'ordre de lecture.
 *
 * `cle` désigne une ligne des blocs — la synthèse ne recalcule rien, elle transpose. Les
 * trois dernières colonnes n'existent qu'ici : elles rapportent un levier à l'ensemble, ce
 * qui n'a pas de sens dans un tableau qui ne montre qu'un levier à la fois.
 */
const COLONNES_SYNTHESE = [
  { cle: 'impressions', titre: 'Impressions' },
  { cle: 'clicks', titre: 'Clics' },
  { cle: 'sessions', titre: 'Sessions' },
  { cle: 'addToCarts', titre: 'Paniers' },
  { cle: 'transactions', titre: 'Ventes' },
  { cle: 'cost', titre: 'Budget' },
  { cle: 'revenue', titre: 'CA', fort: true },
  { cle: 'cos', titre: 'COS', ratio: true },
  { cle: 'cpa', titre: 'CPA', ratio: true },
  { cle: 'sessionsParClic', titre: 'Sessions / clics', ratio: true },
  { cle: 'tauxAjoutPanier', titre: '% Panier', ratio: true },
  { cle: 'cpc', titre: 'CPC', ratio: true },
  { cle: 'ctr', titre: 'CTR', ratio: true },
  { cle: 'pm', titre: 'PM', ratio: true },
  { cle: 'tt', titre: 'TT', ratio: true },
  { cle: 'poidsCa', titre: 'Poids CA', derivee: true, ratio: true },
  { cle: 'poidsBudget', titre: 'Poids Budget', derivee: true, ratio: true },
  { cle: 'coutVisite', titre: 'Coût visite', derivee: true, ratio: true },
  // Saisies à la main. Seul le bloc « Résultats globaux » les porte : les autres lignes de cette
  // vue — un levier, une régie — n'ont pas de back-office, et leurs cellules restent vides, ce
  // qui est exact. Trois colonnes seulement, celles qu'on vient chercher : le réalisé, l'objectif
  // et l'écart entre les deux.
  { cle: 'boTransactions', titre: 'Ventes BO' },
  { cle: 'boRevenue', titre: 'CA BO' },
  { cle: 'boIcCa', titre: 'COS BO', ratio: true },
  { cle: 'ecartCa', titre: 'Mesure / BO', ratio: true },
  { cle: 'objRevenue', titre: 'Objectif CA' },
  { cle: 'objTransactions', titre: 'Objectif ventes' },
  { cle: 'realisationMesure', titre: 'Réal. mesure', ratio: true },
  { cle: 'realisationBo', titre: 'Réal. BO', ratio: true }
];

/**
 * Les colonnes de la synthèse, selon le modèle de métriques.
 *
 * ## Pourquoi cette fonction existe
 *
 * `COLONNES_SYNTHESE` est une **seconde écriture** du jeu de métriques : le serveur tient la
 * sienne — `LIGNES_GLOBAL`, transposée par `versLead` —, cette vue tient la sienne parce qu'elle
 * met les métriques en colonnes et les leviers en lignes. Les deux doivent dire la même chose.
 *
 * Elles ne le disaient plus : passer le tableau en modèle Lead changeait toutes les vues sauf
 * celle-ci, qui continuait d'afficher Paniers, Ventes, CA, COS, % Panier et PM — des colonnes
 * que le serveur ne remplit plus, donc vides, et sans la colonne Conversions qu'il remplit.
 *
 * ## Les trois colonnes dérivées
 *
 * Elles sont calculées ici et non par le serveur. `poidsCa` rapporte le CA d'un levier au CA
 * total : sans CA, il n'a plus de base et disparaît avec lui. `poidsBudget` et `coutVisite` ne
 * dépendent que du budget et des sessions, et restent.
 *
 * ## Les colonnes de saisie
 *
 * Inchangées, comme les lignes correspondantes côté serveur : elles viennent du back-office,
 * et les retirer effacerait des chiffres saisis à la main. Le filtre qui les masque quand
 * aucune n'est renseignée s'applique comme avant.
 */
const colonnesDuModele = (modele, libelleConversions) => {
  if (modele !== 'lead') return COLONNES_SYNTHESE;

  const retirees = new Set(['addToCarts', 'transactions', 'revenue', 'cos', 'tauxAjoutPanier', 'pm', 'poidsCa']);
  const sortie = [];

  for (const colonne of COLONNES_SYNTHESE) {
    if (retirees.has(colonne.cle)) continue;
    sortie.push(colonne);
    // La conversion prend la place des ventes : juste après les sessions, avant le budget.
    if (colonne.cle === 'sessions') {
      sortie.push({ cle: 'conversions', titre: libelleConversions || 'Conversions', fort: true });
    }
  }

  return sortie;
};

/**
 * Deux natures de colonnes, deux fonds.
 *
 * Dix-huit colonnes à la suite se lisent comme une seule bande, et rien ne dit qu'on passe
 * d'un dénombrement à un rapport : le lecteur additionne mentalement une colonne de CPC
 * comme il le ferait d'une colonne de clics. Un fond légèrement plus soutenu marque le
 * passage, en tête comme dans le corps du tableau.
 *
 * La marque suit la **nature** de la colonne, pas sa position. Les colonnes de saisie
 * manuelle alternent volumes et rapports — « Ventes BO » puis « COS BO » — et les traiter
 * en bloc dirait le contraire de ce que la couleur promet partout ailleurs.
 *
 * Un fond, et non une bordure : la bordure sépare, alors qu'on veut regrouper. Elle aurait
 * aussi buté sur les colonnes de saisie, qui apparaissent et disparaissent.
 */
/**
 * `rgba(251, 146, 60, 0.26)` aplati sur blanc. Opaque pour la même raison que `FOND_ENTETE` :
 * l'en-tête de la synthèse est figé, et repose donc sur les leviers qui défilent dessous.
 */
const FOND_RATIO_ENTETE = '#fee3cc';
const FOND_RATIO_CELLULE = 'rgba(251, 146, 60, 0.07)';

/**
 * Colonnes de synthèse nourries par la saisie manuelle, seules à disparaître quand elles sont
 * vides : les colonnes standard tiennent leur place du tableau lui-même, et leur vide se lit.
 */
const CLES_SAISIES_SYNTHESE = new Set([
  'boTransactions', 'boRevenue', 'boIcCa', 'ecartCa',
  'objRevenue', 'objTransactions', 'realisationMesure', 'realisationBo'
]);

/**
 * Division qui rend `null` plutôt que zéro quand il n'y a rien à mesurer — même règle que
 * côté serveur. Un « poids CA » à 0 % faute de CA total se lirait comme un levier inerte.
 */
const ratioSimple = (numerateur, denominateur, facteur = 1) => (
  numerateur === null || numerateur === undefined || !denominateur
    ? null
    : (numerateur / denominateur) * facteur
);

/**
 * Couleur unique des courbes.
 *
 * Un graphe par métrique, une seule série dans chacun : l'identité ne passe jamais par la
 * couleur, elle est dans le titre. Ce qui dispense de palette catégorielle — et du risque
 * de deux teintes indistinguables pour un daltonien. Reste le contraste sur fond clair,
 * vérifié (≥ 3:1).
 */
const COULEUR_COURBE = '#c2410c';

/** Largeur d'une colonne de la synthèse, en pixels. */
const LARGEUR_SYNTHESE = 96;

/** Largeurs propres au comparatif : deux périodes, puis leur écart. */
const LARGEUR_COMPARAISON = 150;
const LARGEUR_EVOLUTION = 110;

/**
 * Nom d'affichage des solutions de mesure.
 *
 * Le tableau se comporte de la même façon avec l'une ou l'autre, mais leurs chiffres ne
 * sont pas comparables : une « visite » Piano ou Matomo et une « session » GA4 ne se
 * comptent pas pareil. Nommer la source évite de croire qu'on lit deux fois la même mesure.
 */
const MESURES = {
  GOOGLE_ANALYTICS: 'Google Analytics',
  PIANO_ANALYTICS: 'Piano',
  MATOMO: 'Matomo'
};

/** Ordre d'affichage des solutions, le même que la barre latérale et l'écran de choix. */
const ORDRE_MESURES = ['GOOGLE_ANALYTICS', 'MATOMO', 'PIANO_ANALYTICS'];

/**
 * Une liste de propriétés en groupes par solution de mesure.
 *
 * Une solution sans propriété ne produit pas de groupe vide : elle disparaît simplement.
 */
const grouperParMesure = (liste) => ORDRE_MESURES
  .map(type => ({ type, libelle: MESURES[type] || type, proprietes: liste.filter(p => p.type === type) }))
  .filter(groupe => groupe.proprietes.length > 0);

/** Même nom, à partir de la clé que le serveur renvoie dans `mesure`. */
const NOM_MESURE = { ga4: 'Google Analytics', piano: 'Piano', matomo: 'Matomo' };

/**
 * Logo de chaque solution de mesure, comme les dashboards Analytics les affichent.
 *
 * L'écran de sélection est le même geste que celui de GA4, Piano ou Matomo : on y choisit
 * une propriété d'une solution nommée. Le logo le dit avant le titre, et évite d'avoir à
 * lire pour savoir sur quelle source on vient d'entrer.
 */
const LOGOS_MESURE = {
  GOOGLE_ANALYTICS: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png',
  PIANO_ANALYTICS: pianoLogo,
  MATOMO: matomoLogo
};

/** Motifs de refus rendus lisibles ; le brut resterait incompréhensible à l'écran. */
const RAISONS = {
  connexion_absente: 'connecteur absent',
  jeton_expire: 'connexion expirée',
  cle_illisible: 'clé illisible',
  compte_sans_account_id: 'compte mal configuré',
  compte_sans_customer_id: 'compte mal configuré',
  compte_sans_advertiser_id: 'compte mal configuré',
  regie_non_prise_en_charge: 'régie non prise en charge',
  erreur_api: 'erreur de l’API'
};

const entier = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const devise = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
// Les montants du tableau s'arrondissent à l'euro — un budget au centime près est
// illisible. Le CPC fait exception : il se joue précisément dans les centimes.
const deviseCentimes = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const decimal = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Une valeur absente rend une cellule vide, jamais « 0 ».
 * Le serveur envoie `null` quand la métrique n'a pas de sens (division par zéro) ;
 * afficher 0 ferait lire « CPA parfait » là où il n'y a rien à mesurer.
 */
/**
 * Couleur du libellé d'une ligne selon sa provenance.
 *
 * Trois teintes et non deux : une donnée saisie à la main n'est ni une mesure rapportée par un
 * connecteur, ni un calcul dérivé de ces mesures. Rien d'autre à l'écran ne dirait qu'un chiffre
 * a été tapé — et un back-office oublié depuis trois mois se lirait comme une source en panne.
 */
const couleurLibelle = (source) => {
  if (source === 'calcul') return '#64748b';
  if (source === 'saisie') return '#7c3aed';
  return '#1f2937';
};

/**
 * Libellé d'une ligne, suivi d'un marqueur d'aide quand le serveur en fournit une.
 *
 * Le `title` natif plutôt que la bulle des blocs : celle-ci porte quatre informations et vit sur
 * un état partagé, là où une ligne n'a qu'une phrase à dire. Trop d'appareillage pour une note
 * de bas de page — et une bulle qui suit la souris gênerait la lecture d'un tableau dense.
 */
const libelleDeLigne = (l) => (
  <>
    {/* `minWidth: 0` sans quoi rien ne s'affiche : un enfant de flexbox ne rétrécit pas sous la
        largeur de son contenu, le libellé chassait donc le marqueur hors du conteneur — qui
        masque ce qui déborde. L'icône existait, hors de l'écran, et rien ne se survolait. */}
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.libelle}</span>
    {l.aide && (
      <span style={{ display: 'inline-flex', flexShrink: 0, marginLeft: '5px', cursor: 'help', opacity: 0.65 }}>
        <Info size={11} />
      </span>
    )}
  </>
);

/**
 * Les jours réellement couverts par une colonne de mois : « du 5 au 18 ».
 *
 * Le serveur borne chaque colonne aux dates de la période demandée — `debut` et `fin` —, si
 * bien qu'une colonne « Septembre » peut ne porter que deux semaines. Sans ce repère, un
 * budget mensuel tronqué se lit comme un mois complet.
 *
 * Le mois n'est répété que si la colonne est à cheval sur deux mois, ce qui n'arrive pas
 * dans un découpage mensuel mais protège l'écriture d'un autre usage.
 */
function joursCouverts(colonne) {
  const { debut, fin } = colonne || {};
  if (!debut || !fin) return '';

  const jour = (d) => String(Number(d.slice(8, 10)));
  const mois = (d) => MOIS_COURTS[Number(d.slice(5, 7)) - 1] || '';

  if (debut === fin) return `le ${jour(debut)}`;
  if (debut.slice(0, 7) === fin.slice(0, 7)) return `du ${jour(debut)} au ${jour(fin)}`;
  return `du ${jour(debut)} ${mois(debut)} au ${jour(fin)} ${mois(fin)}`;
}

const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function formater(valeur, format) {
  if (valeur === null || valeur === undefined || Number.isNaN(valeur)) return '';
  switch (format) {
    case 'devise': return devise.format(valeur);
    case 'deviseCentimes': return deviseCentimes.format(valeur);
    case 'pourcentage': return `${decimal.format(valeur)}%`;
    case 'decimal': return decimal.format(valeur);
    default: return entier.format(valeur);
  }
}

/**
 * Les deux vues **bâties** sur la comparaison : elles rendent deux colonnes côte à côte.
 *
 * À distinguer de `comparaisonUtile` : toutes les vues affichent désormais l'écart de leur
 * période, mais seules ces deux-là changent de structure pour le montrer. Confondre les deux
 * ferait, par exemple, garder dans la synthèse les lignes tombées à zéro d'un seul côté — une
 * règle qui n'a de sens que face à une colonne de référence.
 *
 * YTD n'est pas une vue de plus : c'est le comparatif sur une période imposée, du 1er janvier
 * à hier. Le rendu est le même.
 */
/**
 * Les périodes qu'un choix rapide propose, dans le comparatif.
 *
 * ## Pourquoi une liste ici plutôt que le sélecteur de période
 *
 * Comparer deux semaines, ou deux opérations, demandait d'ouvrir le panneau de période, poser
 * une plage, le rouvrir, poser la référence — quatre gestes pour une lecture qu'on refait
 * vingt fois de suite. Un menu au-dessus de chaque colonne fait le même travail en un clic, et
 * dit du même coup ce que la colonne montre.
 *
 * Le panneau de période reste maître : ce menu écrit dedans, et ce qu'il pose s'y relit.
 *
 * ## Ce que la liste contient
 *
 * Les douze derniers mois et les douze dernières semaines — au-delà, on cherche une date
 * précise, et c'est le calendrier qu'il faut. Les opérations commerciales viennent s'y ajouter,
 * puisque c'est le cas qui a motivé ce menu.
 *
 * Les semaines sont ISO, du lundi au dimanche : c'est la seule définition que le reste de ce
 * tableau emploie, et en changer ici ferait diverger l'en-tête « S39 » de ce qu'elle recouvre.
 */
const choixRapides = (operations = []) => {
  const jour = (d) => d.toISOString().split('T')[0];
  const aujourdhui = new Date();
  const aujourdUtc = new Date(Date.UTC(aujourdhui.getFullYear(), aujourdhui.getMonth(), aujourdhui.getDate()));

  const mois = [];
  for (let recul = 0; recul < 12; recul++) {
    const premier = new Date(Date.UTC(aujourdUtc.getUTCFullYear(), aujourdUtc.getUTCMonth() - recul, 1));
    const dernier = new Date(Date.UTC(premier.getUTCFullYear(), premier.getUTCMonth() + 1, 0));
    mois.push({
      cle: `mois-${jour(premier)}`,
      libelle: `${MOIS_LONGS[premier.getUTCMonth()]} ${premier.getUTCFullYear()}`,
      debut: jour(premier),
      // Le mois en cours s'arrête à hier : ses journées à venir n'ont pas de données, et les
      // inclure ferait comparer un mois entier à une poignée de jours.
      fin: jour(dernier > aujourdUtc ? new Date(aujourdUtc.getTime() - 86400000) : dernier)
    });
  }

  const semaines = [];
  const lundiCourant = new Date(aujourdUtc);
  lundiCourant.setUTCDate(aujourdUtc.getUTCDate() - ((aujourdUtc.getUTCDay() + 6) % 7));
  for (let recul = 0; recul < 12; recul++) {
    const lundi = new Date(lundiCourant);
    lundi.setUTCDate(lundiCourant.getUTCDate() - recul * 7);
    const dimanche = new Date(lundi);
    dimanche.setUTCDate(lundi.getUTCDate() + 6);
    const jeudi = new Date(lundi);
    jeudi.setUTCDate(lundi.getUTCDate() + 3);
    const premierJanvier = new Date(Date.UTC(jeudi.getUTCFullYear(), 0, 1));
    const numero = Math.ceil((((jeudi - premierJanvier) / 86400000) + 1) / 7);
    const court = (d) => `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    semaines.push({
      cle: `semaine-${jour(lundi)}`,
      libelle: `S${numero} · ${court(lundi)}→${court(dimanche)}`,
      debut: jour(lundi),
      fin: jour(dimanche > aujourdUtc ? new Date(aujourdUtc.getTime() - 86400000) : dimanche)
    });
  }

  /**
   * Une période dont aucun jour n'est encore écoulé n'a rien à montrer.
   *
   * Les données s'arrêtant à hier, la semaine commencée ce matin rendait une plage à l'envers —
   * du lundi 28 au dimanche 27. Le 1er du mois pose le même problème. Ces entrées sont écartées
   * plutôt que réduites à un jour : proposer « S40 » dans un menu alors qu'elle ne contient
   * rien ferait chercher des chiffres qui n'existent pas encore.
   */
  const aDesJours = (e) => e.fin >= e.debut;

  return [
    { titre: 'Mois', entrees: mois.filter(aDesJours) },
    { titre: 'Semaines', entrees: semaines.filter(aDesJours) },
    {
      titre: 'Opérations commerciales',
      entrees: operations.map(o => ({
        cle: `operation-${o.id}`,
        libelle: o.nom,
        debut: o.debut,
        fin: o.fin,
        operationId: o.id
      }))
    }
  ].filter(groupe => groupe.entrees.length > 0);
};

const estComparaison = (v) => v === 'comparatif' || v === 'ytd';

/**
 * Les vues qui ont quelque chose à faire d'une période de référence.
 *
 * Les autres ne la demandent pas, et la seconde requête leur est épargnée : **Graphes** trace
 * des courbes et n'a pas de total à confronter, **Budgets** oppose déjà la dépense à
 * l'enveloppe saisie, et **Projection** porte sa propre comparaison — son atterrissage face au
 * budgété. Leur faire payer un appel dont rien ne lirait le résultat allongerait le chargement
 * sans rien ajouter à l'écran.
 *
 * Au niveau du module, comme `estComparaison` : ce sont des fonctions de la vue et de rien
 * d'autre, et les garder dans le composant obligeait `chargerTableau` à les déclarer en
 * dépendance — une identité qui change à chaque rendu, donc un rechargement à chaque frappe.
 */
const comparaisonUtile = (v) => estComparaison(v)
  || v === 'synthese' || v === 'tranches' || v === 'operations';

/**
 * Ce que la comparaison vaut pour une vue donnée, réglage de l'utilisateur compris.
 *
 * ## Deux défauts, parce que les vues n'ont pas le même propos
 *
 * **Comparatif et YTD sont bâtis dessus** : sans référence, ils n'affichent qu'une colonne
 * vide. Ils comparent donc d'emblée, et à l'année précédente — c'est ce qu'on vient y lire, et
 * ce qu'ils faisaient avant que la base soit réglable.
 *
 * **Les autres ne comparent pas tant qu'on ne le demande pas.** Elles répondent d'abord à
 * « combien », et l'écart est une lecture qu'on ajoute. Le leur imposer ferait payer une
 * seconde requête à chaque arrivée sur l'écran — jusqu'à une trentaine de secondes sur une
 * source Piano et une période large — pour une information que personne n'a réclamée.
 *
 * ## Le réglage prime, sauf là où la vue s'effondrerait
 *
 * Une fois la base choisie dans le sélecteur, elle vaut partout. Seul l'interrupteur est
 * ignoré sur Comparatif et YTD : l'éteindre y viderait la moitié du tableau sans que rien
 * n'explique pourquoi.
 */
const comparaisonEffective = (vue, reglage) => {
  const parDefaut = estComparaison(vue)
    ? { active: true, base: 'previous_year' }
    : { active: false, base: 'previous_period' };

  if (!reglage) return parDefaut;
  return {
    ...reglage,
    base: reglage.base || parDefaut.base,
    active: estComparaison(vue) ? true : reglage.active !== false
  };
};

/**
 * D'où vient une métrique, en clair.
 *
 * Le serveur range chaque ligne par `source` ; l'écran la traduit. La distinction n'est pas
 * décorative : une métrique de régie reste vide sur un canal gratuit, une métrique calculée ne
 * vaut que ce que valent ses deux termes, et une métrique saisie n'existe que si quelqu'un l'a
 * renseignée dans le back-office.
 */
const ORIGINE_METRIQUE = {
  ads: { libelle: 'Régies publicitaires', couleur: '#7c2d12', fond: '#fdf7f3' },
  ga4: { libelle: 'Solution de mesure', couleur: '#1d4ed8', fond: '#eff6ff' },
  calcul: { libelle: 'Calculée', couleur: '#047857', fond: '#ecfdf5' },
  saisie: { libelle: 'Saisie manuelle', couleur: '#92400e', fond: '#fffbeb' }
};

/** L'ordre d'affichage des origines : d'abord ce qui est mesuré, ensuite ce qui s'en déduit. */
const ORDRE_ORIGINES = ['ads', 'ga4', 'saisie', 'calcul'];

/**
 * L'onglet « Configurer les métriques ».
 *
 * ## Ce qu'il fait aujourd'hui
 *
 * Il **liste** les métriques que le tableau affiche, groupées par origine. Rien n'y est encore
 * modifiable : le jeu actuel correspond au modèle E-commerce, et le choix d'un modèle n'a de
 * sens qu'à partir du moment où il en existe un second — le modèle Lead, à venir.
 *
 * ## Pourquoi le dire à l'écran
 *
 * Un écran intitulé « Configurer » qui n'offre aucun réglage se lit comme une panne : on cherche
 * la case à cocher, on conclut qu'elle ne s'affiche pas. L'encadré de tête annonce donc ce que
 * l'écran fait et ce qu'il fera, plutôt que de laisser deviner.
 */
const ParametresMetriques = ({ releve, modele, token, proprieteId, onModeleChange, onFermer }) => {
  const { liste, nombreBlocs } = releve;

  const actif = modele?.actif || 'ecommerce';
  const choixPossible = modele?.choixPossible !== false;

  /**
   * Le modèle **choisi**, qui n'est pas encore celui qui s'applique.
   *
   * Cliquer une carte enregistrait et rechargeait le tableau sur-le-champ : un simple coup d'œil
   * au modèle Lead coûtait un rechargement complet, et il fallait recliquer E-commerce pour
   * revenir — second rechargement. Le choix est donc retenu ici, et le bouton de validation est
   * le seul geste qui écrit.
   */
  const [modeleChoisi, setModeleChoisi] = useState(actif);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState('');

  /*
   * Les événements de conversion, chargés à la demande.
   *
   * L'appel coûte une requête GA4 : on ne le passe qu'une fois l'onglet ouvert, et seulement
   * quand le modèle Lead peut servir. Sur une propriété Piano ou Matomo, il n'aurait rien à
   * rendre.
   */
  const [evenements, setEvenements] = useState([]);
  const [chargementEvenements, setChargementEvenements] = useState(false);
  const [erreurEvenements, setErreurEvenements] = useState('');
  /**
   * Les événements comptés comme conversion — **plusieurs**.
   *
   * Une propriété distingue souvent « demande de devis », « rappel formulaire » et « contact
   * bouton » : la ligne Conversions doit pouvoir les réunir, sinon il faudrait lire trois
   * tableaux pour connaître le volume de contacts. Le serveur acceptait déjà une liste — le
   * filtre GA4 est un `inListFilter` — seule cette interface la réduisait à un élément.
   */
  const [evenementsRetenus, setEvenementsRetenus] = useState(modele?.evenements || []);
  const [libelleRetenu, setLibelleRetenu] = useState(modele?.libelleConversions || 'Conversions');

  /**
   * ⚠️ **Le drapeau de chargement ne doit pas être une dépendance de l'effet.**
   *
   * Il l'était, avec `evenements.length` : poser `chargementEvenements` à `true` relançait
   * l'effet, dont le **nettoyage** basculait `vivant` à `false` — pour la requête en cours, celle
   * qui venait d'être lancée. Sa réponse était donc jetée, `setChargementEvenements(false)` avec
   * elle, et le sélecteur restait sur « Chargement des événements… » pour toujours.
   *
   * Un `ref` porte le « déjà demandé » : il ne provoque pas de rendu, donc ne peut pas relancer
   * ce qu'il est censé empêcher. L'effet ne dépend plus que de ce qui change vraiment la
   * requête — la propriété, le jeton, la possibilité du choix.
   */
  const evenementsDemandes = useRef(false);

  useEffect(() => {
    if (!choixPossible || !token || !proprieteId || evenementsDemandes.current) return;

    evenementsDemandes.current = true;
    let vivant = true;
    setChargementEvenements(true);
    setErreurEvenements('');

    axios
      .get(`${API_URL}/api/dashboard/ga4/available-events/${proprieteId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: 'LAST_30_DAYS' }
      })
      .then(reponse => {
        if (!vivant) return;
        if (!reponse.data?.success) {
          setErreurEvenements(reponse.data?.error || 'Liste des événements indisponible.');
          return;
        }
        setEvenements(reponse.data.events || []);
        const config = reponse.data.currentConfig;
        if (config?.conversionEvents?.length) setEvenementsRetenus(config.conversionEvents);
        if (config?.conversionLabel) setLibelleRetenu(config.conversionLabel);
      })
      .catch(e => {
        // Un sélecteur vide et muet se lit comme une absence d'événements sur la propriété.
        if (vivant) setErreurEvenements(e.response?.data?.error || 'Liste des événements indisponible.');
      })
      .finally(() => { if (vivant) setChargementEvenements(false); });

    return () => { vivant = false; };
  }, [choixPossible, token, proprieteId]);

  /**
   * Enregistrer le modèle, et l'événement compté avec lui.
   *
   * Les deux partent **ensemble** quand on choisit Lead : un modèle Lead sans événement retenu
   * afficherait une colonne Conversions vide, et l'utilisateur chercherait l'erreur dans ses
   * données. L'ordre compte — l'événement d'abord, le modèle ensuite — pour que le rechargement
   * du tableau trouve la configuration déjà en place.
   */
  /** Y a-t-il quelque chose à enregistrer ? Le modèle, ou l'événement qu'il compte. */
  // Comparées triées et jointes : l'ordre de sélection n'est pas une modification.
  const empreinte = (liste) => [...(liste || [])].sort().join('|');
  const evenementChange = modeleChoisi === 'lead'
    && evenementsRetenus.length > 0
    && (empreinte(evenementsRetenus) !== empreinte(modele?.evenements)
      || libelleRetenu !== (modele?.libelleConversions || 'Conversions'));
  const aEnregistrer = modeleChoisi !== actif || Boolean(evenementChange);

  /**
   * Valider : écrire, refermer, recharger — dans cet ordre.
   *
   * L'événement part **avant** le modèle : le rechargement du tableau doit trouver la
   * configuration déjà en place, faute de quoi la première lecture afficherait une colonne
   * Conversions vide, puis se corrigerait au rechargement suivant.
   *
   * La fenêtre ne se referme qu'après l'écriture : la refermer d'abord laisserait croire au
   * succès alors que le serveur peut refuser — un modèle Lead hors Google Analytics, par
   * exemple.
   */
  const valider = async () => {
    setErreur('');
    setEnregistrement(true);
    try {
      if (modeleChoisi === 'lead' && evenementsRetenus.length > 0) {
        await axios.post(
          `${API_URL}/api/dashboard/ga4/conversion-settings`,
          {
            propertyId: proprieteId,
            conversionEvents: evenementsRetenus,
            conversionLabel: libelleRetenu || 'Conversions',
            includeValue: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      if (modeleChoisi !== actif) {
        await axios.put(
          `${API_URL}/api/dashboard/consolide/modele`,
          { propertyId: proprieteId, modele: modeleChoisi },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      onFermer?.();
      onModeleChange?.();
    } catch (e) {
      setErreur(e.response?.data?.error || "L'enregistrement a échoué.");
    } finally {
      setEnregistrement(false);
    }
  };


  if (liste.length === 0) {
    return (
      <div style={{ padding: '16px', fontSize: '0.875rem', color: '#64748b' }}>
        Aucune métrique à afficher : le tableau n'a pas encore chargé.
      </div>
    );
  }

  return (
    <div style={{ overflowY: 'auto' }}>
      {/*
        Le choix du modèle, en tête : c'est lui qui commande la liste du dessous.

        Deux cartes plutôt qu'une liste déroulante — elles tiennent chacune leur description, et
        le choix se fait sur ce que le modèle **compte**, pas sur son nom.
      */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
        {[
          {
            cle: 'ecommerce',
            titre: 'E-commerce',
            texte: 'Paniers, transactions et chiffre d\'affaires. COS, panier moyen, taux de transformation.'
          },
          {
            cle: 'lead',
            titre: 'Lead',
            texte: 'Conversions à la place du panier : formulaires, appels, rendez-vous. CPA et taux de transformation calculés sur les conversions.'
          }
        ].map(option => {
          const choisi = modeleChoisi === option.cle;
          const interdit = option.cle === 'lead' && !choixPossible;
          return (
            <button
              key={option.cle}
              onClick={() => !interdit && !enregistrement && setModeleChoisi(option.cle)}
              disabled={interdit || enregistrement}
              style={{
                flex: 1, textAlign: 'left', padding: '12px 14px', borderRadius: '10px',
                border: choisi ? '2px solid #7c2d12' : '1px solid #e5e7eb',
                backgroundColor: choisi ? '#fdf7f3' : 'white',
                cursor: interdit || enregistrement ? 'default' : 'pointer',
                opacity: interdit ? 0.55 : 1
              }}
            >
              <div style={{
                fontSize: '0.875rem', fontWeight: 600,
                color: choisi ? '#7c2d12' : '#1f2937', marginBottom: '4px'
              }}>
                {option.titre}
                {option.cle === actif && (
                  <span style={{ fontWeight: 400, color: '#64748b' }}> · en vigueur</span>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.45 }}>
                {option.texte}
              </div>
              {interdit && (
                <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '6px' }}>
                  Demande Google Analytics : compter une conversion nommée suppose d'interroger un
                  événement, ce que les rapports de canaux de Piano et Matomo ne permettent pas.
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/*
        L'événement compté, **sous** le choix du modèle et non dedans : il se règle aussi quand
        Lead est déjà en vigueur, pour changer de conversion sans rebasculer de modèle.

        C'est la configuration du dashboard Google Analytics classique — la même propriété, la
        même clé. Deux réglages séparés feraient compter deux choses différentes sous le même
        nom, d'un écran à l'autre, sans que rien ne le dise.
      */}
      {/* Rien à régler tant que le modèle E-commerce est retenu : il ne compte pas d'événement. */}
      {choixPossible && modeleChoisi === 'lead' && (
        <div style={{
          padding: '12px 14px', marginBottom: '14px', borderRadius: '10px',
          backgroundColor: '#f8fafc', border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '8px'
          }}>
            <span>Événements comptés comme conversion</span>
            <span style={{ fontWeight: 400, color: '#64748b' }}>
              {evenementsRetenus.length > 0
                ? `${evenementsRetenus.length} retenu${evenementsRetenus.length > 1 ? 's' : ''}`
                : 'aucun retenu'}
            </span>
            <input
              value={libelleRetenu}
              onChange={(e) => setLibelleRetenu(e.target.value)}
              placeholder="Nom affiché dans le tableau"
              style={{
                marginLeft: 'auto', padding: '6px 10px', borderRadius: '8px',
                border: '1px solid #e5e7eb', fontSize: '0.8125rem', fontWeight: 400,
                minWidth: '220px'
              }}
            />
          </div>

          {/*
            Une liste de cases, et non un menu déroulant.

            Plusieurs événements peuvent nourrir la même mesure — « demande de devis » et
            « rappel formulaire » sont deux chemins vers le même contact —, et un menu
            n'en laisse choisir qu'un. La liste défile : une propriété en expose vingt.

            Les comptes sont affichés : « demande_de_devis (60) » se choisit en connaissance
            de cause, là où un nom seul ne dit pas si l'événement pèse soixante ou six.
          */}
          {chargementEvenements && (
            <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>Chargement des événements…</div>
          )}
          {!chargementEvenements && evenements.length === 0 && (
            <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>Aucun événement disponible.</div>
          )}
          {evenements.length > 0 && (
            <div style={{
              maxHeight: '190px', overflowY: 'auto',
              border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white'
            }}>
              {evenements.map((evenement, index) => {
                const coche = evenementsRetenus.includes(evenement.eventName);
                return (
                  <label
                    key={evenement.eventName}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '7px 10px', fontSize: '0.8125rem', cursor: 'pointer',
                      borderTop: index === 0 ? 'none' : '1px solid #f1f5f9',
                      backgroundColor: coche ? '#fdf7f3' : 'white'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={coche}
                      onChange={() => setEvenementsRetenus(precedents => (
                        precedents.includes(evenement.eventName)
                          ? precedents.filter(e => e !== evenement.eventName)
                          : [...precedents, evenement.eventName]
                      ))}
                    />
                    <span style={{ color: '#1f2937' }}>{evenement.label || evenement.eventName}</span>
                    {evenement.eventCount != null && (
                      <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: '0.75rem' }}>
                        {evenement.eventCount.toLocaleString('fr-FR')}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
          {/* Un modèle Lead sans événement affiche une colonne vide : le dire ici évite de
              chercher l'erreur dans les données. */}
          {erreurEvenements && (
            <div style={{ fontSize: '0.75rem', color: '#991b1b', marginTop: '8px' }}>
              {erreurEvenements}
            </div>
          )}
          {modeleChoisi === 'lead' && evenementsRetenus.length === 0 && (
            <div style={{ fontSize: '0.75rem', color: '#92400e', marginTop: '8px' }}>
              Aucun événement retenu : la colonne Conversions resterait vide.
            </div>
          )}
        </div>
      )}

      {erreur && (
        <div style={{
          padding: '10px 12px', marginBottom: '12px', borderRadius: '8px',
          backgroundColor: '#fef2f2', border: '1px solid #fca5a5',
          color: '#991b1b', fontSize: '0.8125rem'
        }}>
          {erreur}
        </div>
      )}

      {/*
        La validation, posée **au-dessus** de la liste et non sous elle : la liste fait plusieurs
        écrans, et un bouton relégué en bas serait hors de vue au moment du choix.
      */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px 0 14px', borderBottom: '1px solid #e5e7eb', marginBottom: '14px'
      }}>
        <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
          {aEnregistrer
            ? 'Le tableau sera rechargé avec ce modèle.'
            : 'Aucun changement à appliquer.'}
        </span>
        <button
          onClick={valider}
          disabled={!aEnregistrer || enregistrement}
          style={{
            marginLeft: 'auto',
            padding: '8px 14px', borderRadius: '8px', border: 'none',
            backgroundColor: '#7c2d12', color: 'white', fontSize: '0.875rem',
            cursor: !aEnregistrer || enregistrement ? 'default' : 'pointer',
            opacity: !aEnregistrer || enregistrement ? 0.5 : 1
          }}
        >
          {enregistrement ? 'Enregistrement…' : 'Appliquer et recharger'}
        </button>
      </div>

      <div style={{ fontSize: '0.8125rem', color: '#64748b', marginBottom: '10px' }}>
        Les {liste.length} métriques ci-dessous sont celles que le tableau affiche aujourd'hui
        {modeleChoisi !== actif && ' — elles changeront à l\'application du modèle retenu'}.
      </div>

      {ORDRE_ORIGINES.map(origine => {
        const metriques = liste.filter(m => m.source === origine);
        if (metriques.length === 0) return null;
        const descripteur = ORIGINE_METRIQUE[origine];

        return (
          <div key={origine} style={{ marginBottom: '16px' }}>
            <div style={{
              fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.04em', color: descripteur.couleur, marginBottom: '6px'
            }}>
              {descripteur.libelle}
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              {metriques.map((metrique, index) => (
                <div
                  key={metrique.cle}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '9px 12px', fontSize: '0.875rem',
                    borderTop: index === 0 ? 'none' : '1px solid #f1f5f9'
                  }}
                >
                  <span style={{ fontWeight: 500, color: '#1f2937', minWidth: '160px' }}>
                    {metrique.libelle}
                  </span>
                  <span style={{
                    fontSize: '0.6875rem', padding: '2px 8px', borderRadius: '999px',
                    backgroundColor: descripteur.fond, color: descripteur.couleur
                  }}>
                    {metrique.cle}
                  </span>
                  {/*
                    « Sur certains blocs » n'est pas un détail d'affichage : un canal gratuit n'a
                    ni impressions ni CPC, et les lignes de back-office ne rejoignent que le bloc
                    global. Annoncer une couverture uniforme ferait chercher des colonnes vides.
                  */}
                  <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8' }}>
                    {metrique.blocs >= nombreBlocs
                      ? 'Tous les blocs'
                      : `${metrique.blocs} bloc${metrique.blocs > 1 ? 's' : ''} sur ${nombreBlocs}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ConsolidatedDashboardView = ({
  token,
  selectedPeriod,
  customStartDate,
  customEndDate,
  onPeriodChange,
  selectedClientId,
  selectedSiteId,
  userSites = [],
  // Propriété demandée par l'appelant — la page d'accueil ouvre le dashboard sur le compte
  // de la ligne cliquée. `null` laisse la présélection habituelle jouer.
  initialPropertyId = null,
  /**
   * Solution de mesure par laquelle on est entré (`GOOGLE_ANALYTICS`, `PIANO_ANALYTICS`,
   * `MATOMO`). Le sélecteur s'y tient : on a cliqué une solution, pas « le consolidé ».
   * `null` — une arrivée directe par l'URL — les propose toutes.
   */
  /**
   * L'analyse IA est-elle ouverte à cet utilisateur ?
   *
   * Elle tient au plan — `analysisLimit` vaut 0 pour un accès client, et le serveur répond 403.
   * Sans cette borne, le bouton s'afficherait pour quelqu'un à qui l'analyse est fermée, et
   * n'échouerait qu'une fois la question posée.
   */
  analyseAutorisee = false,
  sourceMesure = null,
  /**
   * Base de comparaison retenue : `{ active, base, debut, fin, operationId }`.
   *
   * Portée par l'appelant comme la période l'est déjà — c'est un réglage de période, et le
   * garder ici l'aurait perdu à chaque changement de propriété. `null` vaut « année
   * précédente, activée » : le comportement d'avant que cette base soit réglable.
   */
  comparaison = null,
  onComparaisonChange
}) => {
  const [proprietes, setProprietes] = useState([]);
  const [chargementSources, setChargementSources] = useState(true);
  /**
   * Motif du refus, tel que le serveur le formule — et non un message unique écrit ici.
   *
   * Les deux 403 de la route ne disent pas la même chose : l'outil n'a pas été accordé, ou la
   * propriété demandée n'est pas ouverte à ce client. L'écran les rendait tous deux par « Ce
   * dashboard ne vous a pas été ouvert par votre agence », ce qui envoyait le client réclamer un
   * droit qu'il avait déjà — et l'agence chercher un réglage qui n'était pas en cause.
   */
  const [accesRefuse, setAccesRefuse] = useState(null);

  const [proprieteId, setProprieteId] = useState(initialPropertyId);

  const [granularite, setGranularite] = useState('jour');
  /**
   * Vue courante. Les trois lisent les mêmes blocs, mais les déploient différemment :
   * `tranches` une colonne par jour/semaine/mois, `comparatif` deux périodes et leur écart,
   * `synthese` une ligne par levier et une colonne par métrique.
   *
   * **La synthèse ouvre le dashboard.** C'est la vue qui répond à la question qu'on se pose
   * en arrivant — combien chaque levier a coûté et rapporté sur la période —, là où le
   * découpage par jour demande de parcourir trente colonnes avant de le savoir. Elle ne
   * réclame qu'une colonne au serveur, ce qui rend aussi le premier affichage plus court.
   */
  const [vue, setVue] = useState('synthese');
  /**
   * La projection lue face aux budgets, plutôt que ligne à ligne.
   *
   * Un réglage de la vue Projection, et non une vue de plus : on y regarde les mêmes chiffres
   * — l'atterrissage du mois — sous un autre angle, destination par destination au lieu de
   * métrique par métrique. En faire un onglet l'aurait éloigné de ce qu'il commente.
   */
  const [faceAuBudget, setFaceAuBudget] = useState(false);

/**
 * Vues rangées derrière la flèche de la barre d'onglets.
 *
 * Onze onglets à la suite débordaient de la barre sur un écran d'ordinateur portable et
 * repoussaient les sélecteurs de période et de propriété. Celles-ci se consultent
 * ponctuellement, là où les échelles de temps se parcourent : ce sont elles qui rentrent
 * dans un menu, et non les premières.
 */
const VUES_SECONDAIRES = [
  // YTD rejoint le menu alors que Comparatif reste en vue : les deux rendent le même tableau,
  // mais celui-ci impose sa période — du 1er janvier à hier — et ne se combine donc avec aucun
  // réglage de la barre. On l'ouvre pour lui-même, ponctuellement, là où le comparatif suit la
  // période qu'on est en train de regarder.
  { cle: 'ytd', label: 'YTD' },
  { cle: 'graphes', label: 'Graphes' },
  // Le budget consommé, régie par régie et mois par mois. Toujours au mois, quelle que soit
  // l'échelle choisie ailleurs : c'est la maille d'un budget.
  { cle: 'budgets', label: 'Budgets' }
];
  // Sens de lecture des leviers payants : la régie porte ses typologies, ou l'inverse.
  const [classement, setClassement] = useState('regie');
  // Levier tracé par la vue Graphes. Un seul à la fois : superposer vingt leviers sur un
  // même graphe les rendrait tous illisibles.
  const [levierTrace, setLevierTrace] = useState('global');
  /**
   * Note survolée et l'endroit où l'afficher.
   *
   * Une vraie infobulle plutôt que l'attribut `title` : celui-ci n'apparaît qu'après une
   * seconde, ne se déclenche pas toujours dans un conteneur qui défile, et tronque les
   * retours à la ligne selon le navigateur. Pour un contenu qu'on vient justement lire,
   * c'est trois raisons de ne rien voir.
   */
  const [noteSurvolee, setNoteSurvolee] = useState(null);
  /**
   * Bloc dont on survole le titre, et l'endroit où poser son détail.
   *
   * « Autres leviers gratuits » ne dit pas ce qu'il contient : son détail source / support
   * est la seule façon de savoir d'où vient ce trafic. Même infobulle que celle des notes,
   * et pour les mêmes raisons — l'attribut `title` tarde, se perd dans un conteneur qui
   * défile, et rend mal les retours à la ligne.
   */
  const [sourcesSurvolees, setSourcesSurvolees] = useState(null);
  /** Position de l'infobulle des comptes croisés — mêmes raisons que les deux précédentes. */
  const [tableauPrecedent, setTableauPrecedent] = useState(null);

  /**
   * Les opérations commerciales proposées comme période ou comme référence.
   *
   * **Chargées à part, et non tirées de `tableau.notes`.** Celles-ci ne contiennent que les
   * notes qui chevauchent la période affichée — c'est-à-dire, précisément, jamais celle qu'on
   * cherche : comparer septembre au Black Friday de l'an dernier suppose de proposer une
   * opération qui n'est pas dans la fenêtre regardée.
   *
   * La fenêtre est donc large et fixe — trois ans en arrière, un an en avant pour les
   * opérations préparées — plutôt qu'indexée sur la période : elle changerait alors à chaque
   * réglage, et la liste se viderait sous le curseur.
   */
  const [operations, setOperations] = useState([]);
  const [parametresOuverts, setParametresOuverts] = useState(false);
  /**
   * Onglet courant de la fenêtre de paramètres.
   *
   * **La catégorisation reste le premier**, et la fenêtre y revient à chaque ouverture : elle
   * s'ouvre parfois d'elle-même, quand des campagnes arrivent sans rattachement reconnu. Garder
   * l'onglet précédemment consulté ferait alors apparaître les métriques en réponse à une
   * question portant sur les campagnes.
   */
  const [ongletParametres, setOngletParametres] = useState('categorisation');

  /**
   * Menu des vues secondaires.
   *
   * Onze onglets à la suite débordaient de la barre sur un écran d'ordinateur portable, et
   * repoussaient les sélecteurs de période et de propriété. Les deux dernières — Graphes et
   * Budgets — se consultent ponctuellement, là où les échelles de temps se parcourent : ce
   * sont elles qui rentrent dans un menu.
   *
   * `position` est calculée au clic à partir du bouton : la barre d'onglets porte un
   * `overflow: hidden` pour ses coins arrondis, qui découperait un menu posé dedans. Le
   * menu est donc porté par un portail, comme les modales du projet.
   */
  /**
   * L'onglet survolé.
   *
   * Un état plutôt qu'une pseudo-classe `:hover` : la barre est écrite en styles en ligne,
   * qui ne connaissent pas les pseudo-classes. Une clé unique et non un booléen par bouton —
   * un seul onglet est survolé à la fois, et onze drapeaux se désynchroniseraient au premier
   * mouvement rapide.
   */
  const [ongletSurvole, setOngletSurvole] = useState(null);

  const [menuVuesOuvert, setMenuVuesOuvert] = useState(false);
  const [positionMenuVues, setPositionMenuVues] = useState(null);
  const boutonMenuVues = useRef(null);
  const [saisiesOuvertes, setSaisiesOuvertes] = useState(false);
  // Corrections en cours d'édition, par nom de campagne. Séparées de celles du serveur :
  // la modale doit pouvoir être annulée sans avoir rien changé au tableau.
  const [brouillonCategories, setBrouillonCategories] = useState({});
  const [enregistrement, setEnregistrement] = useState(false);
  /**
   * Erreur de l'enregistrement de la catégorisation, affichée **dans** la modale.
   *
   * `erreur` ne pouvait pas servir : son bandeau est rendu dans la vue, donc sous la
   * modale, qui est un portail plein écran. Un enregistrement refusé ne disait donc rien —
   * la modale restait ouverte, inchangée, et l'échec se lisait comme un oubli de la base.
   */
  const [erreurCategorisation, setErreurCategorisation] = useState('');
  // Totaux repliés, par clé. Quinze blocs de huit lignes tiennent mal sur un écran :
  // replier ce qu'on ne lit pas est le seul moyen de comparer deux blocs éloignés.
  const [replies, setReplies] = useState(() => new Set());
  /**
   * Blocs déjà rencontrés, pour ne les replier qu'à leur première apparition.
   *
   * Sans cette mémoire, chaque rechargement — un changement de période, d'échelle, de
   * propriété — refermerait ce que l'utilisateur venait d'ouvrir. Elle n'est pas dans l'état
   * de rendu : la connaître ne change rien à l'écran, seulement à ce qu'on replie ensuite.
   */
  const blocsDejaVus = useRef(new Set());
  /**
   * Campagnes dont la catégorisation a déjà été proposée d'elle-même, par propriété.
   *
   * La mémoire porte sur les **campagnes** et non sur la propriété : changer de période fait
   * apparaître des campagnes qui n'étaient pas là, et ce sont précisément celles qu'il faut
   * signaler. Retenir la propriété entière les aurait tues.
   *
   * Une campagne déjà proposée ne rouvre plus la modale, même laissée non catégorisée : le
   * tableau se recharge à chaque changement d'échelle ou de vue, et la modale se rouvrirait
   * alors sous les doigts, y compris juste après avoir été fermée.
   */
  const categorisationProposee = useRef(new Set());
  const [tableau, setTableau] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');

  // ---------------------------------------------------------------- Sources

  /**
   * Sorti de son effet pour être rejouable : créer une propriété combinée doit la faire
   * apparaître dans le sélecteur sans recharger l'écran.
   */
  const chargerProprietes = useCallback(async () => {
    setChargementSources(true);
    try {
      const reponse = await axios.get(`${API_URL}/api/dashboard/consolide/sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProprietes(reponse.data.properties || []);
    } catch (e) {
      // 403 : l'agence n'a pas ouvert le dashboard à cet accès client
      if (e.response?.status === 403) setAccesRefuse(e.response.data?.error || REFUS_PAR_DEFAUT);
      else setErreur(e.response?.data?.error || 'Impossible de charger les sources');
    } finally {
      setChargementSources(false);
    }
  }, [token]);

  useEffect(() => { chargerProprietes(); }, [chargerProprietes]);

  /**
   * Propriétés proposées, filtrées par la solution de mesure d'entrée puis par le client
   * choisi dans l'en-tête.
   *
   * Le filtre passe par les sites, comme le croisement lui-même : un client possède des
   * sites, un site contient une propriété. Les `accountIds` d'un site mêlent identifiants
   * internes et identifiants de régie selon son époque — les deux sont comparés, sinon des
   * sites entiers disparaîtraient du sélecteur sans raison visible.
   */
  const [modaleCombinee, setModaleCombinee] = useState(false);

  /** Toutes les propriétés accessibles, quelle que soit leur solution de mesure. */
  const proprietesTriees = useMemo(() => {
    const triees = [...proprietes].sort((a, b) => a.name.localeCompare(b.name));
    if (!selectedClientId) return triees;

    const clesDuClient = new Set(
      userSites
        .filter(site => site.clientId === selectedClientId)
        .flatMap(site => site.accountIds || [])
        .map(String)
    );

    /**
     * Les écritures sous lesquelles un site peut avoir rattaché cette propriété.
     *
     * Matomo et Piano s'y inscrivent sous la forme `matomo-{connexion}-{numéro}`, leur numéro
     * de site n'étant unique que dans une instance. Ne chercher que `id` et `sourceId` ne
     * trouvait donc aucune de leurs propriétés : le sélecteur de comptes restait vide alors
     * que le client avait bien les siennes rattachées.
     */
    const identitesDe = (p) => {
      const prefixe = p.type === 'MATOMO' ? 'matomo'
        : p.type === 'PIANO_ANALYTICS' ? 'piano'
          : null;
      return [
        p.id,
        p.sourceId,
        ...(prefixe && p.sourceId ? [`${prefixe}-${p.connectionId || 'default'}-${p.sourceId}`] : [])
      ].filter(Boolean).map(String);
    };

    // Une propriété combinée n'a pas d'identifiant propre dans les sites : elle se reconnaît
    // par ses membres, dont il suffit qu'un seul appartienne au client — le serveur a déjà
    // vérifié, lui, que l'accès porte sur *tous* les membres.
    return triees.filter(p => identitesDe(p).some(i => clesDuClient.has(i))
      || (p.membreIds || []).some(m => clesDuClient.has(String(m))));
  }, [proprietes, selectedClientId, userSites]);

  /**
   * Deux listes, parce que les deux sélecteurs ne répondent pas à la même question.
   *
   * L'**écran de choix** prolonge le sélecteur franchi : entré par Piano, on n'y voit que
   * Piano. Y proposer les trois solutions contredirait la porte qu'on vient de passer.
   *
   * Le **sélecteur de la barre d'outils** les propose toutes, groupées par solution : une
   * fois le tableau ouvert, comparer deux solutions est un geste légitime, et l'imposer par
   * un aller-retour dans la barre latérale n'apportait rien.
   */
  const proprietesDeSelection = useMemo(
    () => (sourceMesure ? proprietesTriees.filter(p => p.type === sourceMesure) : proprietesTriees),
    [proprietesTriees, sourceMesure]
  );

  const groupesDeSelection = useMemo(() => grouperParMesure(proprietesDeSelection), [proprietesDeSelection]);
  const proprietesGroupees = useMemo(() => grouperParMesure(proprietesTriees), [proprietesTriees]);

  /**
   * Une propriété demandée par l'appelant l'emporte sur la présélection.
   *
   * L'effet ne se rejoue qu'au changement de la demande : une fois la vue ouverte, un choix
   * fait dans le sélecteur tient, sans quoi le tableau reviendrait sans cesse à la propriété
   * par laquelle on est entré.
   */
  useEffect(() => {
    if (initialPropertyId) setProprieteId(initialPropertyId);
  }, [initialPropertyId]);

  // Préselection : la propriété du site courant s'il n'y en a qu'une, sinon rien. Elle se
  // fait dans la solution d'entrée : entré par Piano, on n'ouvre pas Google Analytics au
  // motif qu'il est la seule propriété du client.
  useEffect(() => {
    if (proprieteId || proprietesDeSelection.length === 0) return;

    if (selectedSiteId && selectedSiteId !== 'all') {
      const site = userSites.find(s => (s._id || s.id) === selectedSiteId);
      const duSite = site && proprietesDeSelection.find(p => (site.accountIds || []).includes(p.id));
      if (duSite) { setProprieteId(duSite.id); return; }
    }
    if (proprietesDeSelection.length === 1) setProprieteId(proprietesDeSelection[0].id);
  }, [proprietesDeSelection, selectedSiteId, userSites, proprieteId]);

  /**
   * Une propriété que le filtre client vient d'exclure est abandonnée.
   *
   * Sans cela, changer de client dans l'en-tête laisserait le tableau afficher les données
   * du client précédent sous un sélecteur devenu vide — le pire des deux mondes : l'écran
   * ne dit plus ce qu'il montre.
   */
  useEffect(() => {
    if (!proprieteId || proprietesTriees.length === 0) return;
    if (!proprietesTriees.some(p => p.id === proprieteId)) {
      setProprieteId(proprietesDeSelection.length === 1 ? proprietesDeSelection[0].id : null);
      setTableau(null);
    }
  }, [proprietesTriees, proprietesDeSelection, proprieteId]);

  /**
   * Les sous-blocs arrivent repliés.
   *
   * Un bloc de deuxième niveau qui porte lui-même des enfants — le SEO et ses moteurs, les
   * assistants IA, les réseaux sociaux, Google Ads et ses typologies — s'ouvre fermé : le
   * tableau tiendrait sinon sur plusieurs écrans avant qu'on ait rien demandé. Les deux
   * totaux de tête restent dépliés, faute de quoi il ne resterait que quatre lignes.
   *
   * Chaque bloc n'est replié qu'une fois, à sa première apparition : rouvrir le SEO doit
   * tenir au changement de période suivant.
   */
  useEffect(() => {
    const blocs = tableau?.blocs;
    if (!blocs?.length) return;

    const porteursDEnfants = new Set(blocs.filter(b => b.parent).map(b => b.parent));
    // « Résultats globaux » se replie aussi, bien qu'il n'ait pas de parent : ses enfants sont le
    // back-office et les objectifs, qu'on ne consulte pas à chaque lecture. Replier un bloc ne
    // masque que ses enfants — ses quinze lignes restent à l'écran, la référence reste lisible.
    const aReplier = blocs
      .filter(b => porteursDEnfants.has(b.cle) && (b.parent || b.cle === 'global') && !blocsDejaVus.current.has(b.cle))
      .map(b => b.cle);

    blocs.forEach(b => blocsDejaVus.current.add(b.cle));
    if (aReplier.length > 0) setReplies(actuels => new Set([...actuels, ...aReplier]));
  }, [tableau]);

  /**
   * La catégorisation s'ouvre d'elle-même quand une campagne attend d'être rattachée.
   *
   * Un simple avertissement se lisait puis s'oubliait, et les sessions concernées
   * manquaient aux sous-leviers sans que personne n'aille les y remettre. Poser la question
   * à l'arrivée, c'est la poser au moment où elle se règle.
   *
   * Le test porte sur ce que la modale signale en rouge — les campagnes qu'aucune règle ni
   * correction ne rattache —, et non sur les seules campagnes vues par la solution de
   * mesure : ce sont bien celles-là qui appellent une décision.
   *
   * Elle se rouvre dès qu'**une campagne encore jamais proposée** apparaît : élargir la
   * période fait surgir des campagnes que la précédente ne couvrait pas, et les taire sous
   * prétexte que la question a déjà été posée une fois reviendrait à les perdre.
   */
  useEffect(() => {
    const inventaire = tableau?.inventaireCampagnes;
    if (!proprieteId || !inventaire) return;
    // Un accès client n'a pas la main sur ces règles : lui ouvrir la fenêtre d'office lui
    // poserait une question à laquelle il ne peut pas répondre, le serveur refusant
    // l'enregistrement. La question revient à l'agence, propriétaire des données.
    if (!tableau?.categorisationAutorisee) return;

    // La clé porte la propriété : deux propriétés peuvent héberger une campagne homonyme
    // sans que la décision prise sur l'une vaille pour l'autre.
    const attendues = new Set(
      inventaire.filter(c => !c.sousLevier).map(c => `${proprieteId}|${String(c.nom).toLowerCase()}`)
    );
    const nouvelles = [...attendues].filter(cle => !categorisationProposee.current.has(cle));
    if (nouvelles.length === 0) return;

    nouvelles.forEach(cle => categorisationProposee.current.add(cle));
    setOngletParametres('categorisation');
    setParametresOuverts(true);
  }, [tableau, proprieteId]);

  /**
   * Les opérations commerciales du périmètre, pour le sélecteur de période.
   *
   * Un échec est silencieux : la liste sert un réglage facultatif, et une bannière d'erreur
   * pour une base de comparaison qu'on n'allait peut-être pas choisir détournerait du tableau.
   * Le sélecteur dit alors simplement qu'aucune opération n'est enregistrée.
   */
  useEffect(() => {
    if (!token) return;
    const annee = new Date().getFullYear();
    const parametres = {
      topic: 'OPERATION_COMMERCIALE',
      startDate: `${annee - 3}-01-01`,
      endDate: `${annee + 1}-12-31`,
      ...(selectedClientId ? { clientId: selectedClientId } : {})
    };

    axios.get(`${API_URL}/api/notes`, {
      headers: { Authorization: `Bearer ${token}` },
      params: parametres
    })
      .then(r => {
        const brutes = Array.isArray(r.data) ? r.data : (r.data?.notes || r.data?.data || []);
        const jour = (v) => (typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10));
        setOperations(
          brutes
            .map(n => ({
              id: n.id,
              nom: n.content || n.contenu || 'Opération',
              debut: jour(n.startDate || n.debut),
              // Une opération sans date de fin ne couvre que son jour de début — la même
              // règle que côté serveur, où `fin` vaut `endDate || startDate`.
              fin: jour(n.endDate || n.fin || n.startDate || n.debut)
            }))
            .filter(o => o.debut)
            // Les plus récentes d'abord : c'est à l'opération de l'an dernier qu'on compare,
            // pas à celle d'il y a trois ans.
            .sort((a, b) => b.debut.localeCompare(a.debut))
        );
      })
      .catch(() => setOperations([]));
  }, [token, selectedClientId]);

  // ---------------------------------------------------------------- Tableau

  /**
   * La demande de tableau en cours, pour pouvoir l'abandonner.
   *
   * Un `ref` et non un état : sa valeur sert à l'intérieur d'un `async` déjà lancé, où une
   * variable d'état resterait figée sur le rendu qui l'a capturée.
   */
  const demandeEnCours = useRef(null);

  const chargerTableau = useCallback(async () => {
    if (!proprieteId) return;

    /**
     * Composition inachevée : on ne demande rien encore.
     *
     * Choisir la période comparée vide la référence, et la requête partait aussitôt — pour un
     * tableau que l'écran n'affiche pas, et que le second choix fera de toute façon recharger.
     * Deux calculs complets au lieu d'un, jusqu'à une minute perdue sur une source lente.
     *
     * La condition tient à l'état lui-même et non au menu : une base à dates choisies dont les
     * dates manquent, c'est exactement une composition en cours. Elle ne vaut que pour le
     * comparatif — ailleurs, un tableau sans référence a tout son sens.
     */
    const referenceIncomplete = BASES_A_DATES_EXPLICITES.includes(comparaison?.base)
      && !(comparaison?.debut && comparaison?.fin);
    if (vue === 'comparatif' && referenceIncomplete) {
      // La référence précédente est retirée en même temps : la garder afficherait l'écart de la
      // nouvelle période contre l'ancien point de comparaison, sans que rien ne le signale.
      setTableauPrecedent(null);
      return;
    }

    /**
     * Le chargement en cours est abandonné dès qu'un autre est demandé.
     *
     * Les onglets portaient `disabled={chargement}` : ils devenaient inertes pendant le
     * calcul, précisément quand on veut en changer. La garde protégeait d'un état incohérent
     * — deux réponses arrivant dans le désordre — que l'abandon règle mieux, en ne laissant
     * qu'une demande en vol.
     *
     * Ces calculs durent plusieurs secondes — jusqu'à deux minutes sur les périodes larges.
     * Changer d'onglet pendant ce temps mettait la nouvelle demande à la queue de l'ancienne :
     * on attendait un tableau qu'on ne voulait plus, puis celui qu'on voulait.
     *
     * L'abandon ne dispense pas de ranger l'état : la requête abandonnée traverse quand même
     * son `catch` et son `finally`, et sans le garde ci-dessous elle éteindrait le voyant de
     * celle qui vient de la remplacer — l'écran paraîtrait figé sur un tableau vide.
     */
    demandeEnCours.current?.abort();
    const controleur = new AbortController();
    demandeEnCours.current = controleur;
    const estLaCourante = () => demandeEnCours.current === controleur;

    setChargement(true);
    setErreur('');
    try {
      /**
       * Ce qui permet au serveur d'abandonner le calcul précédent.
       *
       * L'abandon côté navigateur ne suffit pas : mesuré en production, le proxy d'edge ne
       * transmet pas la déconnexion, et le serveur continue de calculer pour personne. Il
       * faut donc le lui dire autrement — en lui montrant que cette demande-ci remplace la
       * précédente.
       *
       * Les deux requêtes du comparatif partagent le **même** identifiant : elles forment une
       * seule demande, et la seconde ne doit surtout pas périmer la première.
       */
      const demandeId = Math.random().toString(36).slice(2) + Date.now().toString(36);

      const demander = (corps) => axios.post(
        `${API_URL}/api/dashboard/consolide/timeline`,
        { propertyId: proprieteId, ongletId, demandeId, ...corps },
        { headers: { Authorization: `Bearer ${token}` }, signal: controleur.signal }
      );

      const reponse = await demander({
        // Comparatif, synthèse et projection ne lisent que les totaux de période : une seule
        // colonne suffit, et elle évite de rapatrier trois cents jours pour n'en afficher
        // aucun.
        granularite: vue === 'operations'
          ? 'operation'
          // Le budget se lit au mois, quelle que soit l'échelle choisie pour les autres
          // vues : une colonne par jour sur un trimestre serait illisible, et une colonne
          // unique ne dirait rien de la répartition dans le temps.
          : (vue === 'budgets' ? 'mois'
            : (vue === 'tranches' || vue === 'graphes' ? granularite : 'annee')),
        classement,
        // Un levier sans trafic ni dépense sort du tableau — sauf en comparatif, où sa
        // chute à zéro est précisément ce qu'on vient y lire.
        masquerVides: !estComparaison(vue),
        // Projection et YTD imposent leur période, quel que soit le sélecteur de dates :
        // « atterrissage » et « depuis le 1er janvier » ne veulent rien dire ailleurs.
        ...(vue === 'projection' ? { projection: true } : {}),
        ...(vue === 'ytd' ? { period: 'THIS_YEAR' } : {}),
        ...(vue === 'projection' || vue === 'ytd'
          ? {}
          : { period: selectedPeriod, customStartDate, customEndDate })
      });
      setTableau(reponse.data);

      /**
       * La référence est demandée pour **toutes** les vues, pas seulement le comparatif.
       *
       * Elle tient en une colonne — `granularite: 'annee'` — et porte donc exactement ce que
       * chaque vue a besoin de savoir : le total de la période de référence, bloc par bloc et
       * ligne par ligne. La synthèse y lit l'écart de chacune de ses métriques, les vues
       * chronologiques celui de leur colonne Total.
       *
       * Il n'y a rien à comparer **tranche par tranche** : la question posée à ce tableau est
       * « cette période, face à la précédente », pas « ce mardi face au mardi d'avant ». C'est
       * aussi ce qui rend l'ajout gratuit — une référence à la granularité des colonnes
       * rouvrirait le croisement date × campagne que `detailParJour` écarte côté serveur.
       *
       * Le coût reste celui d'une seconde requête : sur les périodes les plus larges, le toggle
       * « Comparer » du sélecteur est ce qui permet de s'en passer.
       */
      const reference = comparaisonEffective(vue, comparaison);

      if (!comparaisonUtile(vue) || !reference.active) {
        setTableauPrecedent(null);
      } else {
        /**
         * La référence est demandée sur des **dates**, jamais sur une période nommée.
         *
         * « 30 derniers jours » de l'an dernier ne recouvrirait pas les mêmes jours de
         * semaine, et la saisonnalité hebdomadaire fausserait l'écart.
         *
         * Deux provenances, et l'ordre compte : une base à dates choisies — « Personnalisée »,
         * une opération commerciale — donne ses bornes telles quelles ; les autres se
         * déduisent de ce que le **serveur** a réellement couvert, et non de ce que le
         * sélecteur avait calculé de son côté. Lui seul sait où une période nommée s'arrête.
         */
        const { debut, fin } = reponse.data.period;
        const bornes = BASES_A_DATES_EXPLICITES.includes(reference.base)
          ? (reference.debut && reference.fin ? { debut: reference.debut, fin: reference.fin } : null)
          : bornesDeComparaison(debut, fin, reference.base);

        // Une base à dates choisies dont les dates manquent n'a rien à comparer. Afficher un
        // second tableau vide se lirait comme une absence de données, pas comme un réglage
        // incomplet — l'écran préfère n'en montrer aucun.
        if (!bornes) {
          setTableauPrecedent(null);
          return;
        }

        const precedent = await demander({
          /**
           * La référence suit l'échelle de la vue chronologique, et elle seule.
           *
           * Les autres vues ne lisent que des totaux : une colonne leur suffit, et c'est ce qui
           * rend leur comparaison gratuite. Les tranches, elles, montrent un écart **sur chaque
           * colonne** — le 26 septembre face au 19, quand on compare aux sept jours d'avant —,
           * ce qui demande une référence découpée de la même façon.
           *
           * Ce n'est pas sans prix : côté serveur, une référence à plusieurs colonnes rallume
           * `detailParJour`, donc le croisement date × campagne chez Piano — celui qui avait
           * rendu la vue YTD inutilisable. Le coût est alors doublé, contre un plafond de deux
           * minutes. Le toggle « Comparer » est ce qui permet de s'en affranchir, et le message
           * de dépassement le dit déjà.
           */
          granularite: vue === 'tranches' ? granularite : 'annee',
          // Même classement des deux côtés, sans quoi les blocs ne s'apparieraient plus
          classement,
          // Et les mêmes blocs des deux côtés : en masquer d'un seul laisserait des lignes
          // de référence sans vis-à-vis.
          masquerVides: false,
          period: 'CUSTOM',
          customStartDate: bornes.debut,
          customEndDate: bornes.fin
        });
        setTableauPrecedent(precedent.data);
      }
    } catch (e) {
      // Un abandon n'est pas une panne : la demande suivante est déjà partie, et afficher
      // une erreur — ou vider le tableau — ferait clignoter l'écran entre les deux.
      if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;

      /**
       * Le 502 de l'intermédiaire, dit pour ce qu'il est.
       *
       * Railway coupe toute requête à cent vingt secondes et répond un 502 dont le corps est du
       * texte, pas du JSON : `data.error` est alors vide, et le message générique laissait croire
       * à une panne du tableau. Sur les périodes les plus larges, c'est un dépassement de délai —
       * une information qui oriente vers une période plus courte au lieu d'un rechargement.
       */
      const delaiDepasse = e.response?.status === 502 || e.response?.status === 504
        || e.code === 'ECONNABORTED' || (!e.response && e.request);

      if (e.response?.status === 403) setAccesRefuse(e.response.data?.error || REFUS_PAR_DEFAUT);
      else if (delaiDepasse) {
        setErreur("Le calcul a dépassé le délai autorisé par l'hébergeur (deux minutes). "
          + 'Réessayez sur une période plus courte, ou sans comparaison.');
      } else setErreur(e.response?.data?.error || 'Impossible de charger le tableau consolidé');
      setTableau(null);
      setTableauPrecedent(null);
    } finally {
      // Seule la demande courante éteint le voyant : une ancienne, abandonnée, le laisserait
      // allumé sur un écran vide ou l'éteindrait sur un chargement encore en cours.
      if (estLaCourante()) setChargement(false);
    }
  }, [proprieteId, granularite, vue, classement, selectedPeriod, customStartDate, customEndDate, token, comparaison]);

  /**
   * Comparatif et YTD retrouvent l'année précédente **à chaque arrivée**.
   *
   * `comparaisonEffective` fixe bien ce défaut, mais seulement quand aucun réglage n'existe.
   * Or la base est commune à toutes les vues : régler « période précédente » en Synthèse, puis
   * ouvrir Comparatif, y amenait cette base-là. L'onglet s'ouvrait donc sur autre chose que ce
   * qu'on vient y lire, et sans que rien ne dise d'où venait ce choix.
   *
   * Le rétablissement ne vaut qu'en **entrant** dans ces vues, d'où le passage par un `ref` :
   * une base changée une fois sur place tient jusqu'à ce qu'on en sorte. Passer de Comparatif à
   * YTD ne la rétablit pas non plus — les deux sont des vues de comparaison, on ne quitte pas
   * le sujet.
   */
  const vuePrecedente = useRef(vue);
  useEffect(() => {
    const venaitDAilleurs = !estComparaison(vuePrecedente.current);
    vuePrecedente.current = vue;

    if (!estComparaison(vue) || !venaitDAilleurs) return;
    if (comparaison?.base === 'previous_year') return;

    // Les bornes explicites de l'ancienne base ne sont pas reprises : « l'an dernier » calcule
    // les siennes, et garder un couple de dates figé les lui ferait contredire.
    onComparaisonChange?.({ active: true, base: 'previous_year' });
  }, [vue, comparaison?.base, onComparaisonChange]);

  useEffect(() => { chargerTableau(); }, [chargerTableau]);

  /**
   * Les métriques que le tableau affiche, relevées sur les blocs reçus.
   *
   * ## Pourquoi les relever plutôt que les écrire
   *
   * La liste fait foi côté serveur — `LIGNES_GLOBAL` et `lignesDeCanal` dans
   * `routes/dashboard/consolide.js` —, et chaque bloc porte déjà les siennes dans sa réponse.
   * En recopier une liste ici créerait une seconde vérité, qui dériverait au premier ajout :
   * l'écran annoncerait des métriques que le tableau n'affiche pas, ou tairait celles qu'il
   * affiche. On lit donc ce qui est arrivé.
   *
   * ## Toutes les métriques ne sont pas sur tous les blocs
   *
   * Un canal gratuit n'a ni impressions ni CPC — les afficher à zéro se lirait comme une
   * performance parfaite —, et les lignes de back-office ne rejoignent que le bloc global.
   * `blocs` compte donc sur combien de blocs chaque métrique apparaît, pour que l'écran dise
   * « partout » ou « sur certains blocs » au lieu de laisser croire à une couverture uniforme.
   */
  const metriquesDuTableau = useMemo(() => {
    const blocs = tableau?.blocs || [];
    const parCle = new Map();

    blocs.forEach(bloc => {
      (bloc.lignes || []).forEach(ligne => {
        if (!ligne || !ligne.cle) return;
        const connue = parCle.get(ligne.cle);
        if (connue) { connue.blocs += 1; return; }
        parCle.set(ligne.cle, {
          cle: ligne.cle,
          libelle: ligne.libelle,
          source: ligne.source,
          format: ligne.format,
          blocs: 1
        });
      });
    });

    return { liste: [...parCle.values()], nombreBlocs: blocs.length };
  }, [tableau]);


  // Quitter la vue abandonne ce qui était en vol : la réponse n'a plus de destinataire, et
  // React avertirait d'une écriture d'état sur un composant démonté.
  useEffect(() => () => demandeEnCours.current?.abort(), []);

  /**
   * À l'ouverture, les régies se suivent : leurs sous-ensembles sont repliés.
   *
   * Déplié, Google Ads pousse Microsoft Ads huit lignes plus bas, et les deux régies ne se
   * comparent plus d'un coup d'œil. Seuls les blocs **intermédiaires** — ceux qui ont un
   * parent et des enfants — partent repliés : les deux totaux restent ouverts, sans quoi le
   * tableau s'ouvrirait sur trois lignes.
   *
   * Le repli n'est réappliqué qu'au **changement de classement**, pas à chaque rechargement :
   * changer de période ou d'échelle ne doit pas refermer ce que l'utilisateur vient
   * d'ouvrir. Le classement, lui, renomme les niveaux — ce qui était déplié n'existe plus.
   */
  const classementApplique = useRef(null);

  useEffect(() => {
    if (!tableau || classementApplique.current === tableau.classement) return;
    classementApplique.current = tableau.classement;

    const parents = new Set(tableau.blocs.filter(b => b.parent).map(b => b.parent));
    setReplies(new Set(
      tableau.blocs.filter(b => b.parent && parents.has(b.cle)).map(b => b.cle)
    ));
  }, [tableau]);

  /**
   * Enregistre les corrections de catégorisation, puis recharge le tableau.
   *
   * Le rechargement n'est pas cosmétique : le rattachement d'une campagne change les
   * chiffres de deux sous-leviers à la fois, celui qu'elle quitte et celui qu'elle
   * rejoint. Laisser le tableau en l'état afficherait une répartition périmée.
   */
  const enregistrerCategories = async () => {
    const corrections = Object.entries(brouillonCategories)
      .map(([campaignName, choix]) => ({
        campaignName,
        subLever: choix?.sousLevier || null,
        // La régie n'est transmise que si elle diffère de celle du marquage : une valeur
        // égale à l'origine n'est pas une correction, c'est un retour à l'état normal.
        bloc: choix?.bloc || null
      }));

    if (corrections.length === 0) { setParametresOuverts(false); return; }

    setEnregistrement(true);
    setErreurCategorisation('');
    try {
      const reponse = await axios.put(
        `${API_URL}/api/dashboard/consolide/categorisation`,
        { propertyId: proprieteId, corrections },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Le serveur dit ce qu'il a réellement écrit ; s'il n'a rien écrit alors qu'on lui a
      // envoyé des corrections, mieux vaut le dire que refermer sur un succès imaginaire.
      const { corrections: ecrites = 0, retraits = 0 } = reponse.data || {};
      if (ecrites + retraits === 0 && corrections.length > 0) {
        setErreurCategorisation('Le serveur n\'a enregistré aucune correction. Réessayez, ou signalez-le si cela persiste.');
        return;
      }
      setBrouillonCategories({});
      setParametresOuverts(false);
      await chargerTableau();
    } catch (e) {
      setErreurCategorisation(
        e.response?.data?.error
        || (e.response ? `Erreur ${e.response.status} lors de l'enregistrement` : 'Serveur injoignable : la catégorisation n\'a pas été enregistrée')
      );
    } finally {
      setEnregistrement(false);
    }
  };

  // ---------------------------------------------------------------- Rendu

  if (accesRefuse) {
    return (
      <div className="view-content">
        <div className="account-selection-prompt">
          <div className="account-selection-card">
            <h2>Dashboard consolidé</h2>
            <p>{accesRefuse}</p>
          </div>
        </div>
      </div>
    );
  }

  if (chargementSources) {
    return (
      <div className="view-content">
        <div className="loading-state" style={{ padding: '2rem' }}>
          <Loader className="spin" size={16} />
          <span>Chargement des sources…</span>
        </div>
      </div>
    );
  }

  // Repris des dashboards Analytics : la loupe quand la solution n'est pas nommée, son logo
  // sinon. C'est le même geste et le même écran, il doit se reconnaître au premier coup d'œil.
  const nomMesure = MESURES[sourceMesure] || 'analytique';

  const iconeMesure = LOGOS_MESURE[sourceMesure]
    ? (
      <img
        src={LOGOS_MESURE[sourceMesure]}
        alt={MESURES[sourceMesure] || ''}
        style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'contain' }}
      />
    )
    : <Layers size={48} color="#6b7280" />;


  if (proprietesDeSelection.length === 0) {
    return (
      <div className="view-content">
        <div className="account-selection-prompt">
          <div className="account-selection-card">
            <div className="account-selection-icon">
              {proprietes.length === 0 ? iconeMesure : <span style={{ fontSize: '48px' }}>🔍</span>}
            </div>
            <h2>Aucune propriété {nomMesure}</h2>
            {/* Trois causes distinctes, trois messages : rien de connecté, rien dans cette
                solution, ou rien pour ce client. Les confondre ferait chercher une panne là
                où il n'y a qu'un filtre d'en-tête à changer. */}
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
              {proprietes.length === 0
                ? 'Le dashboard consolidé croise une solution de mesure avec vos régies publicitaires : connectez une propriété Google Analytics, Matomo ou Piano pour l\'utiliser.'
                : proprietesTriees.length === 0
                  ? "Aucune propriété n'est rattachée à un site de ce client. Choisissez un autre client dans l'en-tête, ou ajoutez la propriété à l'un de ses sites."
                  : `Aucune propriété ${nomMesure} n'est rattachée à un site de ce client. Choisissez une autre solution de mesure dans la barre latérale.`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!proprieteId) {
    return (
      <div className="view-content">
        <div className="account-selection-prompt">
          <div className="account-selection-card">
            <div className="account-selection-icon">{iconeMesure}</div>
            <h2>Sélectionnez une propriété {nomMesure}</h2>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
              {proprietesDeSelection.length} propriété{proprietesDeSelection.length > 1 ? 's' : ''} disponible{proprietesDeSelection.length > 1 ? 's' : ''}
            </p>
            <select
              className="account-selection-dropdown"
              value=""
              onChange={(e) => e.target.value && setProprieteId(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="">Choisir une propriété…</option>
              {/* Une seule solution ici dans le cas courant : la titrer répéterait le titre
                  de l'écran. Le regroupement ne sert que si l'on est entré sans solution. */}
              {groupesDeSelection.length > 1
                ? groupesDeSelection.map(groupe => (
                  <optgroup key={groupe.type} label={groupe.libelle}>
                    {groupe.proprietes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                ))
                : proprietesDeSelection.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>

            {/* Combiner ne se propose que s'il y a de quoi : deux propriétés au moins, et
                pas Google Analytics, dont les sessions se dédupliquent. */}
            {proprietesDeSelection.filter(p => !p.combinee && p.type !== 'GOOGLE_ANALYTICS').length > 1 && (
              <button
                onClick={() => setModaleCombinee(true)}
                style={{
                  marginTop: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: '6px',
                  backgroundColor: 'white', fontSize: '13px', color: '#374151', cursor: 'pointer'
                }}
              >
                <Layers size={14} />
                Combiner des propriétés
              </button>
            )}
          </div>
        </div>

        <ProprieteCombineeModal
          ouverte={modaleCombinee}
          onFermer={() => setModaleCombinee(false)}
          onCreee={chargerProprietes}
          token={token}
          proprietes={proprietesDeSelection}
          typeMesure={sourceMesure}
        />
      </div>
    );
  }

  /** Solution de la propriété ouverte ; à défaut, celle par laquelle on est entré. */
  const typeAffiche = proprietes.find(p => p.id === proprieteId)?.type || sourceMesure;

  const colonnes = tableau?.colonnes || [];
  const blocs = tableau?.blocs || [];
  const entetes = tableau?.entetes || [];

  /** Hauteur d'un intertitre de bloc, à réserver à l'identique dans chaque colonne. */
  const HAUTEUR_TITRE = 40;

  const estReplie = (cle) => replies.has(cle);

  /**
   * Propriétés partagées par les cellules de titre de bloc, dans les quatre vues.
   *
   * Elles s'écrivaient à l'identique quatre fois ; ajouter le survol au même endroit
   * quatre fois de suite était le meilleur moyen d'en oublier une.
   */
  /**
   * `repliable` est paramétrable : la synthèse écarte certains blocs de sa liste, et un titre
   * n'y doit être cliquable que si le dépliage révèle réellement quelque chose. Par défaut, la
   * règle du tableau entier.
   */
  const proprietesTitreBloc = (bloc, repliable = estRepliable) => ({
    onClick: repliable(bloc) ? () => basculer(bloc.cle) : undefined,
    // L'infobulle native ne porte plus que l'avertissement — un chiffre incomplet doit se
    // signaler sans qu'on ait à viser quoi que ce soit. Ce que le bloc contient est passé
    // au repère d'information, pour ne pas surgir au moindre passage sur la colonne.
    title: bloc.avertissement || (repliable(bloc) ? (estReplie(bloc.cle) ? 'Déplier' : 'Replier') : undefined),
  });

  /**
   * Le repère d'information d'un bloc, et lui seul, ouvre la bulle.
   *
   * Attaché à l'icône plutôt qu'à la cellule : un titre qui déclenche une bulle au moindre
   * passage la fait surgir pendant qu'on parcourt la colonne, sans l'avoir demandée. La
   * bulle s'ancre sous l'icône, là où l'œil est déjà.
   *
   * Écrit une fois pour les quatre tableaux — l'oublier dans l'un d'eux ne se serait vu
   * qu'en survolant précisément le bon bloc de la bonne vue.
   */
  const marqueurAide = (bloc) => (
    (bloc.aide || bloc.technique || bloc.rattachement || bloc.sources?.length > 0) ? (
      <span
        onMouseEnter={(e) => {
          const zone = e.currentTarget.getBoundingClientRect();
          // Le haut **et** le bas : la bulle bascule au-dessus quand la place manque
          // dessous, et s'ancrer par le bas évite d'avoir à connaître sa hauteur avant
          // qu'elle ne soit rendue.
          setSourcesSurvolees({ bloc, x: zone.left, haut: zone.top, bas: zone.bottom });
        }}
        onMouseLeave={() => setSourcesSurvolees(null)}
        // Le clic n'ouvre ni ne replie : on vise l'icône pour lire, pas pour agir.
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', flexShrink: 0, cursor: 'help', padding: '2px' }}
      >
        <Info size={12} style={{ opacity: 0.7 }} />
      </span>
    ) : null
  );

  /**
   * Un bloc se replie s'il a des enfants ; une feuille, non — il n'y aurait rien à cacher
   * qu'elle-même, et son intertitre resterait seul à l'écran.
   *
   * La règle se lit dans l'arbre reçu plutôt que dans le rang du bloc : les niveaux ne sont
   * pas les mêmes d'un classement à l'autre. Par régie, « Google Ads » porte ses typologies ;
   * par typologie, c'est « Brand (search) » qui porte ses régies. Les deux se replient, sans
   * que le rendu ait à savoir lequel des deux classements est affiché.
   */
  const cellesQuiOntDesEnfants = new Set(blocs.filter(b => b.parent).map(b => b.parent));
  const estRepliable = (bloc) => cellesQuiOntDesEnfants.has(bloc.cle);

  /**
   * Replier masque **les sous-ensembles**, jamais les lignes du bloc lui-même.
   *
   * C'est ce qui distingue un intertitre d'un accordéon : replier « Google Ads » cache ses
   * typologies mais laisse ses quinze métriques à l'écran, sans quoi le geste ferait
   * disparaître le résultat qu'on cherchait justement à lire. Les lignes d'un bloc ne
   * dépendent donc que de la visibilité du bloc — de ses ancêtres, jamais de lui-même.
   *
   * Effet de bord bienvenu : une clé qui s'attarde dans l'ensemble après un changement de
   * classement ne peut plus rendre un bloc illisible, seulement replier des enfants qu'un
   * chevron rouvre.
   */

  const basculer = (cle) => setReplies(actuels => {
    const suivants = new Set(actuels);
    if (suivants.has(cle)) suivants.delete(cle); else suivants.add(cle);
    return suivants;
  });

  // Index simple, et non un `useMemo` : ce code vit après les retours anticipés du rendu,
  // où un hook ne serait pas appelé à chaque passage. Une vingtaine d'entrées, sans enjeu.
  /**
   * Notes qui recoupent une colonne.
   *
   * Le test porte sur un chevauchement d'intervalles, et non sur l'égalité des dates : une
   * note de trois jours doit apparaître au-dessus des trois colonnes concernées, et une
   * colonne « semaine » ou « mois » doit montrer les notes de n'importe lequel de ses jours.
   */
  /**
   * Le tableau n'est affichable qu'entre deux chargements.
   *
   * Ce n'est pas qu'une politesse d'attente : pendant un chargement, `tableau` porte encore
   * la réponse **précédente**, calculée pour une autre vue. La projection y lirait un champ
   * absent et annoncerait qu'aucune journée n'est écoulée ; le comparatif s'apparierait à
   * une période qui n'est plus la sienne. Mieux vaut ne rien montrer que montrer l'ancien
   * sous le nouveau nom.
   */
  const donneesAffichables = Boolean(tableau) && !chargement;

  /**
   * Le fond d'un onglet : sélectionné, survolé, ou rien.
   *
   * Le survol ne s'applique qu'aux onglets inactifs — teinter celui qui est déjà sombre
   * n'apprendrait rien et ferait clignoter la sélection au passage de la souris.
   */
  const fondOnglet = (actif, cle) => {
    if (actif) return '#7c2d12';
    return ongletSurvole === cle ? 'rgba(124, 45, 18, 0.08)' : 'white';
  };

  /** La vue secondaire en cours, s'il y en a une : c'est elle qui nomme et teinte le bouton. */
  const vueSecondaireActive = VUES_SECONDAIRES.find(v => v.cle === vue) || null;

  /** Le nom de la propriété regardée, pour nommer la conversation d'analyse. */
  const nomPropriete = proprietes.find(p => p.id === proprieteId)?.name || null;

  /**
   * Ce qu'on envoie à l'analyse : **ce que le tableau affiche**, réduit à ses totaux.
   *
   * ## Pourquoi une réduction
   *
   * Un bloc porte ses lignes — une par métrique —, et chaque ligne porte une valeur par colonne.
   * Sur une période trimestrielle au jour, cela fait quatre-vingt-dix valeurs par métrique et par
   * bloc : plusieurs mégaoctets à téléverser pour une question. On ne garde que le total de
   * période de chaque métrique, qui est ce que l'analyse commente.
   *
   * La série reste disponible pour qui la demandera : la vue Graphes et le découpage par tranche
   * sont nommés dans la charge, et le modèle sait qu'il regarde une période agrégée.
   *
   * ## Appelée à l'envoi, pas à l'ouverture
   *
   * Même raison que sur les autres dashboards, et une de plus ici : le panneau reste ouvert
   * quand on change d'onglet. La question suivante lit donc la vue en cours, et non celle
   * d'où le panneau a été ouvert.
   */
  const construireChargeAnalyse = () => {
    const blocs = (tableau?.blocs || [])
      .map(bloc => ({
        cle: bloc.cle,
        titre: bloc.titre,
        parent: bloc.parent || null,
        totaux: Object.fromEntries(
          (bloc.lignes || [])
            .filter(ligne => ligne && ligne.total != null)
            .map(ligne => [ligne.libelle || ligne.cle, ligne.total])
        )
      }))
      .filter(bloc => Object.keys(bloc.totaux).length > 0);

    return {
      dashboardData: {
        sources: [proprieteId].filter(Boolean),
        dashboardType: 'consolide',
        // La vue décide de ce que le tableau montre : sans elle, « projection » et « réalisé »
        // se liraient de la même façon.
        vue,
        granularite,
        classement,
        mesure: tableau?.mesure || null,
        periode: tableau?.period || null,
        period: selectedPeriod,
        ...(selectedPeriod === 'CUSTOM' ? { customStartDate, customEndDate } : {}),
        blocs,
        // Les notes de la période : elles expliquent les écarts que l'analyse va relever.
        notes: (tableau?.notes || []).slice(0, 50)
      },
      dashboardType: 'consolide',
      accountId: proprieteId || null,
      accountName: nomPropriete,
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' ? { customStartDate, customEndDate } : {})
    };
  };

  /**
   * Une colonne d'opération porte un intitulé, pas une date.
   *
   * Cent huit pixels suffisent à « 12/08 », pas à « Black Friday anticipé ». Les opérations
   * étant peu nombreuses — quelques-unes par période, là où un trimestre compte quatre-vingt-dix
   * jours —, la colonne peut être large sans que le tableau déborde.
   */
  const largeurColonne = vue === 'operations' ? 184 : LARGEUR_COLONNE;

  const notesDeLaColonne = (colonne) => (tableau?.notes || []).filter(
    n => n.debut <= (colonne.fin || colonne.date) && n.fin >= (colonne.debut || colonne.date)
  );

  const blocParCle = new Map(blocs.map(b => [b.cle, b]));

  /**
   * Profondeur d'un bloc dans la hiérarchie, en remontant ses parents.
   *
   * Elle en compte trois : les totaux, leurs leviers (Google Ads, SEO…), et les sous-leviers
   * de Google Ads (Brand, Shopping…). Le retrait la donne à lire ; sans lui, « Brand » et
   * « Microsoft Ads » sembleraient de même rang.
   */
  const profondeur = (bloc) => {
    let n = 0;
    let cle = bloc.parent;
    while (cle && n < 5) { n++; cle = blocParCle.get(cle)?.parent; }
    return n;
  };

  /**
   * Un bloc est masqué si **n'importe lequel** de ses ancêtres est replié.
   *
   * Ne regarder que le parent direct laisserait les sous-leviers de Google Ads à l'écran
   * après avoir replié « TOTAL Leviers payants » : ils flotteraient sans leur levier,
   * rattachés à un titre devenu invisible.
   */
  const estMasque = (bloc) => {
    let cle = bloc.parent;
    let n = 0;
    while (cle && n < 5) {
      if (replies.has(cle)) return true;
      cle = blocParCle.get(cle)?.parent;
      n++;
    }
    return false;
  };

  // ---------------------------------------------------------------- Comparatif

  const lignesPrecedentesParBloc = new Map(
    (tableauPrecedent?.blocs || []).map(b => [b.cle, new Map(b.lignes.map(l => [l.cle, l]))])
  );

  /**
   * Une ligne ne s'affiche que si elle porte une valeur — vide et zéro comptent pour rien.
   *
   * Le zéro a d'abord été conservé, au motif que « Transactions : 0 » sous mille sessions dit
   * qu'aucune n'a converti. À l'usage, non : un bloc alignait six lignes à zéro sous sa seule
   * ligne de sessions, et il fallait les parcourir toutes pour s'apercevoir qu'aucune
   * n'apprenait rien. L'absence de la ligne le dit plus vite.
   *
   * **Au comparatif, la règle porte sur les deux périodes à la fois.** Le tableau y demande
   * `masquerVides: false` au serveur, et il a raison : un levier tombé à zéro est précisément
   * ce qu'on vient y lire. Il garde une valeur non nulle du côté de la référence, et reste
   * donc affiché — seul ce qui vaut zéro *des deux côtés* disparaît.
   *
   * Le tri se fait ici et non côté serveur : lui ne voit qu'une période à la fois, et ne peut
   * pas distinguer « tombé à zéro » de « zéro depuis toujours ». Hors comparatif il ne retire
   * d'ailleurs que les lignes de saisie manuelle restées vides ; « Coût », « COS » et « CPA »
   * d'un canal sans coût traversaient son filtre.
   *
   * La contrepartie, assumée : on ne lit plus « aucune transaction » sur la ligne, mais à son
   * absence.
   */
  const porteUneValeur = (ligne) => ligne
    && ((typeof ligne.total === 'number' && ligne.total !== 0)
      || (ligne.valeurs || []).some(v => typeof v === 'number' && v !== 0));

  const lignesRetenues = (bloc) => {
    // Hors comparatif, une seule période est à l'écran : c'est elle qui décide.
    if (!estComparaison(vue) || !tableauPrecedent) return bloc.lignes.filter(porteUneValeur);
    const precedentes = lignesPrecedentesParBloc.get(bloc.cle);
    return bloc.lignes.filter(l => porteUneValeur(l) || porteUneValeur(precedentes?.get(l.cle)));
  };

  /**
   * Un bloc dont aucune ligne ne porte de valeur, et dont aucun descendant n'en porte.
   *
   * Un parent peut n'avoir lui-même aucune ligne retenue tout en portant des enfants qui en
   * ont : le retirer couperait la branche. D'où la descente, qui reprend la règle appliquée
   * par le serveur aux blocs vides.
   */
  const enfantsDuBloc = new Map();
  for (const b of blocs) {
    if (!b.parent) continue;
    if (!enfantsDuBloc.has(b.parent)) enfantsDuBloc.set(b.parent, []);
    enfantsDuBloc.get(b.parent).push(b);
  }

  const porteDesDonnees = (bloc) => lignesRetenues(bloc).length > 0
    || (enfantsDuBloc.get(bloc.cle) || []).some(porteDesDonnees);

  /**
   * Blocs effectivement rendus, calculés une fois pour les trois groupes de colonnes.
   *
   * Les trois doivent parcourir exactement la même liste : si un seul d'entre eux omettait
   * un bloc, tout ce qui suit glisserait d'autant de lignes et le tableau se mettrait à
   * afficher des valeurs en face des mauvais libellés, sans que rien ne le signale.
   *
   * `porteDesDonnees` s'applique à **toutes** les vues, et pas seulement au comparatif. Le
   * serveur retire déjà les blocs sans aucune métrique brute, mais son test et celui-ci ne
   * portent pas sur la même chose : un bloc peut lui paraître plein et n'avoir, une fois les
   * lignes à zéro retirées, plus rien à montrer. Il afficherait alors son titre seul.
   */
  const blocsVisibles = blocs.filter(b => !estMasque(b) && porteDesDonnees(b));

  /**
   * Évolution entre les deux périodes, en pourcentage.
   *
   * Rend `null` plutôt que l'infini quand la période de référence est vide : « +∞ % » ne
   * dit rien de plus que « il n'y avait rien avant », et une cellule vide le dit mieux.
   */
  const evolution = (avant, apres) => {
    if (avant === null || avant === undefined || apres === null || apres === undefined) return null;
    if (!Number.isFinite(avant) || !Number.isFinite(apres) || avant === 0) return null;
    return ((apres - avant) / Math.abs(avant)) * 100;
  };

  /** Vert si l'évolution va dans le bon sens, rouge sinon, neutre si la ligne n'en a pas. */
  const couleurEvolution = (cleLigne, valeur) => {
    const sens = SENS_EVOLUTION[cleLigne];
    if (!sens || valeur === null || Math.abs(valeur) < 0.5) return null;
    return (valeur > 0) === (sens > 0)
      ? { backgroundColor: '#a7d7c5', color: '#14532d' }
      : { backgroundColor: '#f6b8b8', color: '#7f1d1d' };
  };

  /**
   * Le choix rapide des deux périodes du comparatif.
   *
   * ## Une barre, et non deux menus dans l'en-tête du tableau
   *
   * Ils y étaient d'abord, au-dessus de leur colonne. Mais le tableau ne s'affiche pas tant
   * qu'il manque une référence — et les menus disparaissaient avec lui, au moment précis où
   * l'on en avait besoin pour la choisir. Ils vivent donc au-dessus, où ils restent atteignables
   * dans tous les états.
   *
   * ## L'ordre est imposé
   *
   * On désigne d'abord la période qu'on regarde, ensuite ce à quoi on la compare : le second
   * menu reste inerte jusque-là. Sans cet ordre, on posait une référence avant de savoir à quoi,
   * et le tableau se rechargeait deux fois pour rien.
   *
   * ## Choisir à droite remet la référence à zéro
   *
   * Changer la période comparée sans vider la référence laisserait le tableau afficher un écart
   * entre la nouvelle période et l'ancienne référence, l'espace d'un chargement — un chiffre
   * faux, et personne pour dire qu'il l'est. Le tableau attend donc le second choix.
   */
  // Recalculée à chaque rendu plutôt que mémorisée : ce bloc suit des retours anticipés, où un
  // `useMemo` s'appellerait un coup sur deux — ce que React refuse. Le coût est de quelques
  // dizaines d'objets.
  const groupesDeChoix = choixRapides(operations);

  const entreeCorrespondante = (debut, fin) => {
    if (!debut || !fin) return '';
    for (const groupe of groupesDeChoix) {
      const trouvee = groupe.entrees.find(e => e.debut === debut && e.fin === fin);
      if (trouvee) return trouvee.cle;
    }
    return '';
  };

  const datesCourantes = selectedPeriod === 'CUSTOM' && customStartDate && customEndDate
    ? { debut: customStartDate, fin: customEndDate }
    : { debut: '', fin: '' };

  const choixCourante = entreeCorrespondante(datesCourantes.debut, datesCourantes.fin);
  const choixReference = entreeCorrespondante(comparaison?.debut, comparaison?.fin);

  const choisirPeriodeRapide = (cote, entree) => {
    if (cote === 'courante') {
      // La référence est vidée : elle sera choisie au menu suivant, et le tableau attend.
      onComparaisonChange?.({ active: true, base: 'custom', debut: '', fin: '', operationId: '' });
      onPeriodChange?.('CUSTOM', entree.debut, entree.fin);
      return;
    }
    onComparaisonChange?.({
      active: true,
      base: entree.operationId ? 'operation' : 'custom',
      debut: entree.debut,
      fin: entree.fin,
      operationId: entree.operationId || ''
    });
  };

  const menuRapide = (cote, valeur, desactive) => (
    <select
      value={valeur}
      disabled={desactive}
      onChange={(e) => {
        const entree = groupesDeChoix.flatMap(g => g.entrees).find(x => x.cle === e.target.value);
        if (entree) choisirPeriodeRapide(cote, entree);
      }}
      style={{
        padding: '6px 10px', borderRadius: '8px',
        border: '1px solid ' + (desactive ? '#e5e7eb' : '#e5e7eb'),
        backgroundColor: desactive ? '#f9fafb' : 'white',
        color: desactive ? '#9ca3af' : '#1f2937',
        fontSize: '0.8125rem', cursor: desactive ? 'default' : 'pointer', minWidth: '190px'
      }}
      title={desactive
        ? "Choisissez d'abord la période à comparer"
        : (cote === 'courante' ? 'Période comparée' : 'Période de référence')}
    >
      <option value="">{cote === 'courante' ? 'Période à comparer…' : 'Comparer à…'}</option>
      {groupesDeChoix.map(groupe => (
        <optgroup key={groupe.titre} label={groupe.titre}>
          {groupe.entrees.map(entree => (
            <option key={entree.cle} value={entree.cle}>{entree.libelle}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );

  /**
   * Le style d'un écart : gris par défaut, badge pour les fortes variations.
   *
   * ## Pourquoi presque tout reste gris
   *
   * Un écart accompagne ici un chiffre, et il y en a un par cellule : colorer chacun ferait un
   * damier, où l'œil suivrait les teintes au lieu de lire les montants. Or ce qu'on cherche
   * dans un tableau de cette densité, ce n'est pas le signe de chaque variation — c'est celles
   * qui sortent du lot. Le gris dit « voici l'écart » ; le badge dit « regarde celui-là ».
   *
   * ## Le seuil
   *
   * Absolu et non relatif au tableau : une même variation doit se signaler de la même façon
   * d'un écran à l'autre, sinon la règle est impossible à apprendre. À vingt-cinq pour cent, on
   * écarte le bruit ordinaire d'une semaine à l'autre sans laisser passer un décrochage.
   *
   * ## Ce qui ne prend jamais de badge
   *
   * Les métriques dont `SENS_EVOLUTION` ne dit rien — impressions, CPC, un objectif saisi :
   * aucune direction n'y est bonne ou mauvaise, et un fond vert ou rouge y ferait lire un
   * jugement qui n'existe pas. Elles restent grises quelle que soit l'ampleur.
   */
  const SEUIL_BADGE = 25;

  const styleEcart = (cleLigne, valeur) => {
    const teinte = couleurEvolution(cleLigne, valeur);
    // Le gris reste discret ; le badge, lui, doit accrocher — c'est son seul rôle.
    if (!teinte || Math.abs(valeur) < SEUIL_BADGE) return { color: '#9ca3af', fontWeight: 500 };
    return { ...teinte, fontWeight: 600, padding: '1px 5px', borderRadius: '4px' };
  };

  /** Plus le bloc est profond, plus son filet est discret. */
  const bordureBloc = (bloc) => (profondeur(bloc) === 0 ? '2px solid #cbd5e1' : '1px solid #e5e7eb');

  const cellule = (contenu, { largeur, weekend, fort, entete }) => (
    <div style={{
      width: largeur,
      minWidth: largeur,
      height: entete ? HAUTEUR_ENTETE : HAUTEUR_LIGNE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '0 10px',
      boxSizing: 'border-box',
      fontSize: entete ? '0.75rem' : '0.8125rem',
      fontWeight: fort || entete ? 600 : 400,
      color: entete ? '#7c2d12' : '#1f2937',
      backgroundColor: weekend ? 'rgba(251, 146, 60, 0.16)' : 'transparent',
      borderBottom: '1px solid #f1f5f9',
      whiteSpace: 'nowrap'
    }}>
      {contenu}
    </div>
  );

  /**
   * Une valeur, et sous elle son écart avec la période de référence.
   *
   * ## Pourquoi sous la valeur et non à côté
   *
   * La synthèse porte vingt-six colonnes de quatre-vingt-seize pixels. « 12 450 € −12,4 % »
   * n'y tient pas sur une ligne : il aurait fallu élargir, et le tableau passait de un écran
   * et demi à près de trois. En dessous, l'écart ne coûte que de la hauteur — la même que
   * celle qu'une deuxième ligne d'en-tête prend déjà ailleurs.
   *
   * ## Ce qui reste muet, et pourquoi
   *
   * Pas d'écart sans référence, ni sur une métrique dont `SENS_EVOLUTION` ne dit rien — les
   * impressions, le CPC, un objectif saisi : rien n'y désigne une bonne direction, et une
   * teinte y ferait lire un jugement qui n'existe pas. `couleurEvolution` rend alors `null`,
   * et l'écart s'inscrit en gris plutôt que de disparaître : « +4,2 % » reste une information,
   * c'est seulement le vert et le rouge qui n'en sont pas une ici.
   */
  const celluleComparee = (contenu, ecart, cleLigne, options) => {
    const teinte = styleEcart(cleLigne, ecart);
    const { largeur, weekend, fort } = options;

    return (
      <div style={{
        width: largeur,
        minWidth: largeur,
        height: HAUTEUR_LIGNE,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: '1px',
        padding: '0 10px',
        boxSizing: 'border-box',
        fontSize: '0.8125rem',
        fontWeight: fort ? 600 : 400,
        color: '#1f2937',
        backgroundColor: weekend ? 'rgba(251, 146, 60, 0.16)' : 'transparent',
        borderBottom: '1px solid #f1f5f9',
        whiteSpace: 'nowrap',
        lineHeight: 1.15
      }}>
        <span>{contenu}</span>
        {ecart !== null && ecart !== undefined && (
          <span style={{ fontSize: '0.6875rem', ...teinte }}>
            {ecart > 0 ? '+' : ''}{decimal.format(ecart)} %
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="view-content">
      {/* Barre de réglages : période puis propriété. Ce que le tableau croise se lit
          en dessous, en clair, mais ne se règle pas ici. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        // Pas de marge haute : la vue s'ouvre sur ses boutons, et l'espace au-dessus est
        // déjà celui que `.main-content` réserve à tous les écrans.
        padding: '0 0 12px', flexWrap: 'wrap'
      }}>
        <PeriodSelector
          selectedPeriod={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          comparaison={comparaisonEffective(vue, comparaison)}
          operations={operations}
          onChange={(periode, debut, fin, reference) => {
            // La comparaison part en premier : la période déclenche le rechargement, et
            // l'inverse aurait lancé une requête avec l'ancienne référence.
            if (reference) onComparaisonChange?.(reference);
            onPeriodChange?.(periode, debut, fin);
          }}
        />

        {/* Sélecteur de propriété, précédé du logo de sa solution comme dans les
            dashboards Analytics. La barre latérale étant masquée sur cette vue, c'est le
            seul endroit qui dise sur quelle source le tableau est ouvert. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '4px 10px 4px 8px', borderRadius: '8px',
          border: '1px solid #e5e7eb', backgroundColor: 'white'
        }}>
          {/* Le logo suit la propriété **affichée** : le sélecteur les propose toutes, et
              garder celui de la porte d'entrée dirait le contraire de ce qu'on regarde. */}
          {LOGOS_MESURE[typeAffiche]
            ? (
              <img
                src={LOGOS_MESURE[typeAffiche]}
                alt={MESURES[typeAffiche] || ''}
                style={{ width: '20px', height: '20px', borderRadius: '4px', objectFit: 'contain' }}
              />
            )
            : <Layers size={18} color="#6b7280" />}
          <select
            value={proprieteId}
            onChange={(e) => setProprieteId(e.target.value)}
            style={{
              padding: '4px 0', border: 'none', outline: 'none',
              fontSize: '0.875rem', backgroundColor: 'transparent', cursor: 'pointer'
            }}
          >
            {proprietesGroupees.map(groupe => (
              <optgroup key={groupe.type} label={groupe.libelle}>
                {groupe.proprietes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Échelle de lecture. Le regroupement se fait au serveur, pas ici. */}
        <div style={{
          display: 'flex', border: '1px solid #e5e7eb', borderRadius: '8px',
          overflow: 'hidden', backgroundColor: 'white'
        }}>
          {/* La synthèse ouvre le groupe parce qu'elle ouvre le dashboard : c'est la vue
              par défaut, et la première place est celle où on la cherche. */}
          <button
            onClick={() => setVue('synthese')}
            onMouseEnter={() => setOngletSurvole('synthese')}
            onMouseLeave={() => setOngletSurvole(null)}
            style={{
              padding: '8px 14px', border: 'none', borderLeft: 'none',
              backgroundColor: fondOnglet(vue === 'synthese', 'synthese'),
              transition: 'background-color 0.12s ease',
              color: vue === 'synthese' ? 'white' : '#374151',
              fontSize: '0.875rem', fontWeight: vue === 'synthese' ? 600 : 400,
              cursor: 'pointer'
            }}
          >
            Synthèse
          </button>
          {GRANULARITES.map((g) => (
            <button
              key={g.value}
              onClick={() => { setVue('tranches'); setGranularite(g.value); }}
              onMouseEnter={() => setOngletSurvole(`tranches:${g.value}`)}
              onMouseLeave={() => setOngletSurvole(null)}
              style={{
                padding: '8px 14px', border: 'none',
                borderLeft: '1px solid #e5e7eb',
                backgroundColor: fondOnglet(vue === 'tranches' && granularite === g.value, `tranches:${g.value}`),
                transition: 'background-color 0.12s ease',
                color: vue === 'tranches' && granularite === g.value ? 'white' : '#374151',
                fontSize: '0.875rem', fontWeight: vue === 'tranches' && granularite === g.value ? 600 : 400,
                cursor: 'pointer'
              }}
            >
              {g.label}
            </button>
          ))}
          {/* Opérations, comparatif et projection sont d'autres façons de lire les mêmes blocs :
              elles remplacent le découpage temporel plutôt que de s'y ajouter, d'où leur place
              dans ce groupe. */}
          {[
            // Une colonne par opération commerciale — c'est-à-dire par note dont le sujet
            // est « opération commerciale » — au lieu d'une colonne par journée. Placé juste
            // après les échelles de temps, dont il est une variante : un découpage du même
            // tableau, par événement plutôt que par calendrier.
            { cle: 'operations', label: 'Opérations' },
            { cle: 'comparatif', label: 'Comparatif' },
            { cle: 'projection', label: 'Projection' }
          ].map(autre => (
            <button
              key={autre.cle}
              onClick={() => setVue(autre.cle)}
              onMouseEnter={() => setOngletSurvole(autre.cle)}
              onMouseLeave={() => setOngletSurvole(null)}
              style={{
                padding: '8px 14px', border: 'none', borderLeft: '1px solid #e5e7eb',
                backgroundColor: fondOnglet(vue === autre.cle, autre.cle),
                transition: 'background-color 0.12s ease',
                color: vue === autre.cle ? 'white' : '#374151',
                fontSize: '0.875rem', fontWeight: vue === autre.cle ? 600 : 400,
                cursor: 'pointer'
              }}
            >
              {autre.label}
            </button>
          ))}

          {/**
            * Les vues secondaires, derrière une flèche.
            *
            * Le bouton porte le nom de la vue active quand elle est dans le menu, et non un
            * libellé fixe : sans cela, la barre n'indiquerait plus ce qu'on regarde dès qu'on
            * quitte les onglets visibles. Il se teinte alors comme un onglet sélectionné, pour
            * la même raison.
            */}
          <button
            ref={boutonMenuVues}
            onClick={() => {
              if (menuVuesOuvert) { setMenuVuesOuvert(false); return; }
              // Position lue au clic : le menu vit dans un portail, hors du flux de la barre.
              const r = boutonMenuVues.current?.getBoundingClientRect();
              if (r) setPositionMenuVues({ haut: r.bottom + 4, gauche: r.left, largeur: r.width });
              setMenuVuesOuvert(true);
            }}
            onMouseEnter={() => setOngletSurvole('menu')}
            onMouseLeave={() => setOngletSurvole(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 12px', border: 'none', borderLeft: '1px solid #e5e7eb',
              /**
               * Le menu ouvert tient le bouton teinté, comme s'il était survolé : sans cela,
               * il redeviendrait blanc dès que la souris descend vers la liste, et le lien
               * entre le bouton et le menu qu'il a ouvert se perdrait.
               */
              backgroundColor: vueSecondaireActive
                ? '#7c2d12'
                : (menuVuesOuvert || ongletSurvole === 'menu' ? 'rgba(124, 45, 18, 0.08)' : 'white'),
              transition: 'background-color 0.12s ease',
              color: vueSecondaireActive ? 'white' : '#374151',
              fontSize: '0.875rem', fontWeight: vueSecondaireActive ? 600 : 400,
              cursor: 'pointer', whiteSpace: 'nowrap'
            }}
          >
            {vueSecondaireActive ? vueSecondaireActive.label : 'Plus'}
            <ChevronDown size={14} />
          </button>
        </div>

        {/* Porté par un portail : la barre d'onglets découpe ses débordements. */}
        {menuVuesOuvert && positionMenuVues && ReactDOM.createPortal(
          <>
            {/* Un clic n'importe où referme, comme les autres menus du projet. */}
            <div
              onClick={() => setMenuVuesOuvert(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 1200 }}
            />
            <div style={{
              position: 'fixed', top: positionMenuVues.haut, left: positionMenuVues.gauche,
              minWidth: Math.max(positionMenuVues.largeur, 140),
              background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)', zIndex: 1201, overflow: 'hidden'
            }}>
              {VUES_SECONDAIRES.map(autre => (
                <button
                  key={autre.cle}
                  onClick={() => { setVue(autre.cle); setMenuVuesOuvert(false); }}
                  onMouseEnter={() => setOngletSurvole(`menu:${autre.cle}`)}
                  onMouseLeave={() => setOngletSurvole(null)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '9px 14px', border: 'none',
                    // L'entrée en cours est déjà teintée ; le survol la fonce d'un cran, de
                    // sorte que le pointeur se voie même sur la ligne sélectionnée.
                    backgroundColor: ongletSurvole === `menu:${autre.cle}`
                      ? 'rgba(124, 45, 18, 0.16)'
                      : (vue === autre.cle ? 'rgba(124, 45, 18, 0.08)' : 'white'),
                    color: '#374151',
                    fontSize: '0.875rem', fontWeight: vue === autre.cle ? 600 : 400,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                    transition: 'background-color 0.12s ease'
                  }}
                >
                  {autre.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}


        {/* Sens de lecture des leviers payants. Il vaut pour toutes les vues : les trois
            se contentent de déployer les blocs que le serveur envoie. */}
        <select
          value={classement}
          onChange={(e) => setClassement(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb',
            fontSize: '0.875rem', backgroundColor: 'white', cursor: 'pointer'
          }}
        >
          <option value="regie">Par régie</option>
          <option value="typologie">Par type</option>
        </select>

        {/* Saisie manuelle : n'apparaît qu'aux comptes qui peuvent écrire. Le serveur tranche —
            l'écran ne connaît pas le rôle de l'appelant, et le déduire du contenu du tableau
            reviendrait à deviner. */}
        {tableau?.saisieAutorisee && (
          <button
            onClick={() => setSaisiesOuvertes(true)}
            disabled={!tableau}
            title="Back-office et objectifs"
            aria-label="Back-office et objectifs"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '8px', borderRadius: '8px', border: '1px solid #e5e7eb',
              backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem'
            }}
          >
            <PencilLine size={15} />
          </button>
        )}

        {/* Catégorisation : même garde que la saisie, et pour la même raison. Le serveur
            refuse déjà l'écriture à un accès client par un 403 ; sans cette condition, le
            bouton restait visible et ouvrait une fenêtre dont l'enregistrement ne pouvait
            qu'échouer. Les règles de rattachement appartiennent à l'agence propriétaire des
            données, pas à celui qui lit le tableau. */}
        {tableau?.categorisationAutorisee && (
          <button
            onClick={() => { setBrouillonCategories({}); setOngletParametres('categorisation'); setParametresOuverts(true); }}
            disabled={!tableau}
            title="Catégorisation"
            aria-label="Catégorisation"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '8px', borderRadius: '8px', border: '1px solid #e5e7eb',
              backgroundColor: 'white', cursor: tableau ? 'pointer' : 'default', fontSize: '0.875rem'
            }}
          >
            <Settings size={15} />
          </button>
        )}

        {/*
          L'analyse IA, le même panneau que sur les autres dashboards.

          Elle vaut pour **toutes les vues** : la charge est construite à l'envoi de la question
          et porte la vue en cours. Changer d'onglet pendant que le panneau est ouvert change
          donc ce que la question suivante lira, sans rien avoir à rebrancher.

          `pret` attend le tableau : un panneau ouvert sur un dashboard encore vide n'a rien à
          lire, et l'analyse partirait sur des blocs absents.
        */}
        <AnalyseIA
          token={token}
          compte={{ id: proprieteId, name: nomPropriete }}
          pret={Boolean(tableau)}
          autorise={analyseAutorisee}
          construireCharge={construireChargeAnalyse}
          iconeSeule
        />

        {/* Réduit à son icône : l'action se devine, et le bandeau porte déjà quatre boutons.
            Le `title` la nomme au survol — et `aria-label` la nomme pour un lecteur d'écran,
            à qui une icône seule ne dit rien. */}
        <button
          onClick={chargerTableau}
          disabled={chargement}
          title="Actualiser"
          aria-label="Actualiser"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '8px', borderRadius: '8px', border: '1px solid #e5e7eb',
            backgroundColor: 'white', cursor: chargement ? 'default' : 'pointer', fontSize: '0.875rem'
          }}
        >
          <RefreshCw size={15} className={chargement ? 'spin' : ''} />
        </button>
      </div>

      {/* Le nom du site et le décompte des comptes ont quitté le haut du tableau : ils
          coûtaient une ligne pour une information qui se pose bloc par bloc, et que chaque
          bulle porte désormais. Ne reste que le cas où la propriété n'appartient à aucun
          site — il explique des colonnes vides partout, et aucune bulle ne le dirait. */}
      {donneesAffichables && !tableau.sites?.length && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
          marginBottom: '12px', fontSize: '0.8125rem', color: '#92400e'
        }}>
          <Globe size={15} />
          <span>
            Cette propriété n'appartient à aucun site : ajoutez-la à un site avec ses comptes
            publicitaires pour croiser les deux sources. Les lignes Impressions, Clics et Budget
            restent vides d'ici là.
          </span>
        </div>
      )}

      {erreur && (
        <div style={{
          padding: '12px', borderRadius: '8px', backgroundColor: '#fef2f2',
          color: '#991b1b', fontSize: '0.875rem', marginBottom: '12px'
        }}>
          {erreur}
        </div>
      )}

      {/* Comptes muets : dits explicitement, jamais comptés comme zéro */}
      {donneesAffichables && tableau.comptesEnEchec?.length > 0 && (
        <PartialDataNotice
          failedSources={tableau.comptesEnEchec.map(c => ({
            sourceId: c.accountId,
            /* ⚠️ `erreur_api` ne dit rien de ce qu'il faut faire : c'est `detail` qui porte le
               message de la régie — « Jeton Awin refusé (invalide ou révoqué) » —, et lui seul
               nomme le geste qui répare. Le serveur le rend depuis toujours dans
               `comptesEnEchec` ; le taire laissait le nom du compte seul à expliquer son
               absence. */
            reason: (c.raison === 'erreur_api' && c.detail) || RAISONS[c.raison] || c.raison
          }))}
          resolveName={(id) => tableau.comptesEnEchec.find(c => c.accountId === id)?.accountName}
        />
      )}

      {donneesAffichables && tableau.erreurGa4 && (
        <div style={{
          padding: '12px', borderRadius: '8px', backgroundColor: '#fffbeb',
          color: '#92400e', fontSize: '0.875rem', marginBottom: '12px'
        }}>
          Données {NOM_MESURE[tableau.mesure] || 'analytiques'} indisponibles ({RAISONS[tableau.erreurGa4.raison] || tableau.erreurGa4.raison}) :
          les lignes Sessions, Transactions et CA sont vides.
        </div>
      )}

      {chargement && (
        <div className="loading-state" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          minHeight: '320px', color: '#64748b', fontSize: '0.875rem'
        }}>
          <Loader className="spin" size={16} />
          <span>Calcul du tableau consolidé…</span>
        </div>
      )}

      {/* Saisie du back-office et des objectifs. `onEnregistre` recharge le tableau : les
          chiffres de huit lignes viennent de changer, et une répartition périmée ne se
          signalerait pas d'elle-même. */}
      <SaisiesModal
        ouverte={saisiesOuvertes}
        onFermer={() => setSaisiesOuvertes(false)}
        onEnregistre={chargerTableau}
        token={token}
        proprieteId={proprieteId}
        nomMesure={tableau?.mesure ? NOM_MESURE[tableau.mesure] : null}
      />

      {/* Paramétrage de la catégorisation. En portail, comme toutes les modales du projet :
          la barre latérale et le tableau collant créent sinon des conflits de z-index. */}
      {/* La condition porte aussi sur le rendu, et pas seulement sur les deux points
          d'ouverture : un tableau rechargé sous un autre compte laisserait sinon la fenêtre
          ouverte sur un écran qui n'y a plus droit. */}
      {parametresOuverts && tableau?.categorisationAutorisee && ReactDOM.createPortal(
        <div
          onClick={() => setParametresOuverts(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: '12px', padding: '20px',
              width: 'min(900px, 92vw)', maxHeight: '82vh', display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Paramètres du tableau</h3>
              <button
                onClick={() => setParametresOuverts(false)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={18} />
              </button>
            </div>

            {/*
              Deux onglets dans une seule fenêtre, et non deux fenêtres.

              Les deux réglages portent sur le même tableau et se consultent dans le même geste.
              Surtout, la catégorisation s'ouvre parfois d'elle-même : lui donner sa propre
              fenêtre obligerait à en fermer une pour en ouvrir une autre dès qu'on vient
              simplement voir les métriques.
            */}
            <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #e5e7eb', marginBottom: '14px' }}>
              {[
                { cle: 'categorisation', libelle: 'Catégorisation des campagnes' },
                { cle: 'metriques', libelle: 'Configurer les métriques' }
              ].map(onglet => (
                <button
                  key={onglet.cle}
                  onClick={() => setOngletParametres(onglet.cle)}
                  style={{
                    padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: ongletParametres === onglet.cle ? 600 : 400,
                    color: ongletParametres === onglet.cle ? '#7c2d12' : '#64748b',
                    borderBottom: ongletParametres === onglet.cle
                      ? '2px solid #7c2d12'
                      : '2px solid transparent',
                    marginBottom: '-1px'
                  }}
                >
                  {onglet.libelle}
                </button>
              ))}
            </div>

            {ongletParametres === 'metriques' && (
              <ParametresMetriques
                releve={metriquesDuTableau}
                modele={tableau?.modele}
                token={token}
                proprieteId={proprieteId}
                // Le tableau se recharge : les lignes changent, et une liste périmée
                // annoncerait l'ancien modèle.
                onModeleChange={chargerTableau}
                onFermer={() => setParametresOuverts(false)}
              />
            )}

            {ongletParametres === 'categorisation' && (<>

            <p style={{ margin: '0 0 12px', fontSize: '0.8125rem', color: '#64748b' }}>
              Le rattachement par défaut lit le type de campagne et le tag de son nom
              (<code>[BRAND]</code>, <code>[SHOP]</code>…). Corrigez ici les campagnes qui y
              échappent — une campagne de marque qui ne porte pas <code>[BRAND]</code>, par
              exemple. La correction vaut pour cette propriété, des deux côtés à la fois :
              régie et solution de mesure.
            </p>

            {(tableau?.inventaireCampagnes || []).some(c => !c.sousLevier) && (
              <div style={{ fontSize: '0.8125rem', color: '#b91c1c', marginBottom: '8px' }}>
                {(tableau.inventaireCampagnes || []).filter(c => !c.sousLevier).length} campagne(s)
                sans tag reconnu, en tête de liste.
              </div>
            )}

            {/* Une liste tronquée qui se présenterait comme complète ferait croire qu'il ne
                reste rien à catégoriser. */}
            {tableau?.inventaireTotal > (tableau?.inventaireCampagnes || []).length && (
              <div style={{ fontSize: '0.8125rem', color: '#92400e', marginBottom: '8px' }}>
                {(tableau.inventaireCampagnes || []).length} campagnes affichées sur {tableau.inventaireTotal} :
                celles qui attendent une décision d'abord, puis les plus grosses. Le tableau, lui,
                les compte toutes.
              </div>
            )}

            <div style={{ overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              {(tableau?.inventaireCampagnes || []).length === 0 && (
                <div style={{ padding: '16px', fontSize: '0.875rem', color: '#64748b' }}>
                  Aucune campagne sur la période sélectionnée.
                </div>
              )}
              {/* Les campagnes qu'aucun tag n'a rattachées passent en tête : ce sont les
                  seules qui appellent une décision, et les chercher au milieu de cinquante
                  lignes déjà classées est le plus sûr moyen de ne pas les traiter. Le tri
                  est stable — à l'intérieur de chaque groupe, l'ordre du serveur tient. */}
              {[...(tableau?.inventaireCampagnes || [])]
                .sort((a, b) => (a.sousLevier ? 1 : 0) - (b.sousLevier ? 1 : 0))
                .map((c, index) => {
                const cle = `${c.parent}-${c.origine}-${c.nom}`;
                const brouillon = brouillonCategories[c.nom];
                const valeur = brouillon?.sousLevier !== undefined ? brouillon.sousLevier : (c.sousLevier || '');
                /**
                 * Régie d'accueil : celle choisie, sinon celle déjà enregistrée, sinon celle
                 * où le marquage a rangé la campagne. Le sous-levier suit la régie affichée,
                 * faute de quoi on proposerait les typologies de Google Ads sous Meta.
                 */
                const regie = brouillon?.bloc !== undefined ? (brouillon.bloc || c.parent) : (c.bloc || c.parent);
                const choix = (tableau?.sousLeviersDisponibles || []).filter(sl => sl.parent === regie);
                const deplacee = regie !== c.parent;
                // Non rattachée tant que le brouillon ne lui donne pas de sous-levier :
                // le rouge doit s'éteindre dès qu'on la classe, sans attendre l'enregistrement.
                // Une campagne déplacée est traitée, même si sa nouvelle régie n'a pas de
                // sous-leviers où la ranger.
                const sansRattachement = !valeur && !deplacee;

                // Le brouillon porte les deux réglages : les séparer ferait perdre le
                // sous-levier au premier changement de régie, et réciproquement.
                /**
                 * Le brouillon porte les deux réglages : les séparer ferait perdre la
                 * typologie au premier changement de régie, et réciproquement.
                 *
                 * **Changer de régie n'efface la typologie que si elle n'existe pas chez la
                 * nouvelle.** L'effacer à chaque fois était destructeur : essayer une autre
                 * régie puis revenir à l'originale laissait les deux réglages vides, et
                 * l'enregistrement lit « ni typologie ni régie » comme un retour à la règle
                 * automatique — il supprimait donc la correction qu'on croyait seulement
                 * consulter.
                 */
                const modifier = (champ, v) => setBrouillonCategories(actuel => {
                  const courant = actuel[c.nom] || { sousLevier: valeur, bloc: c.bloc || '' };
                  const suivant = { ...courant, [champ]: v };

                  if (champ === 'bloc') {
                    const clesDeLaRegie = new Set((tableau?.sousLeviersDisponibles || [])
                      .filter(sl => sl.parent === (v || c.parent))
                      .map(sl => sl.cle));
                    if (!clesDeLaRegie.has(suivant.sousLevier)) {
                      // Revenir à la régie d'origine **restitue** le rattachement qu'elle
                      // portait. Sans quoi l'aller-retour laissait la ligne vide des deux
                      // réglages, ce que l'enregistrement lit comme un retour à la règle
                      // automatique : la correction était supprimée par un geste qui ne
                      // voulait que regarder ailleurs.
                      suivant.sousLevier = clesDeLaRegie.has(c.sousLevier) ? c.sousLevier : '';
                    }
                  }

                  return { ...actuel, [c.nom]: suivant };
                });

                return (
                  <div key={cle} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 12px', fontSize: '0.8125rem',
                    borderTop: index > 0 ? '1px solid #f1f5f9' : 'none',
                    backgroundColor: sansRattachement ? '#fef2f2' : 'transparent'
                  }}>
                    <span
                      style={{
                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        color: sansRattachement ? '#b91c1c' : 'inherit',
                        fontWeight: sansRattachement ? 600 : 400
                      }}
                      title={c.nom}
                    >
                      {c.nom}
                    </span>
                    {/* D'où la ligne est lue, et non à quelle régie elle est rattachée : ce
                        second point est déjà dit par la liste déroulante juste à côté, et le
                        répéter masquait la seule chose qu'on ne pouvait pas deviner.
                        L'API d'une régie sait de quel compte vient une campagne ; la solution
                        de mesure ne connaît que son marquage, d'où la provenance en dessous.
                        Sans elle, une campagne Meta marquée `google / cpc` se lit comme une
                        erreur du tableau plutôt que comme une erreur de marquage. */}
                    <span style={{ color: sansRattachement ? '#b91c1c' : '#94a3b8', fontSize: '0.75rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      Source : {c.origine === 'ga4'
                        ? `${NOM_MESURE[tableau?.mesure] || 'Analytics'}${c.sessions != null ? ` · ${c.sessions} sess.` : ''}`
                        : c.regie}
                      {c.provenances?.length > 0 && (
                        <span style={{ display: 'block', fontSize: '0.6875rem', opacity: 0.8 }}>
                          {c.provenances.join(', ')}
                        </span>
                      )}
                    </span>
                    {/* Régie d'accueil.
                        Une campagne remontée par l'API d'une régie n'est pas déplaçable, et
                        ce n'est pas une lacune : c'est **ce compte-là** qui a facturé, et la
                        déplacer emporterait sa dépense chez un annonceur qui n'a rien
                        dépensé. Seul le trafic identifié par son marquage UTM peut avoir été
                        rangé sous la mauvaise régie.
                        Affichée en clair plutôt qu'en liste grisée : un menu éteint se lit
                        comme une panne, un libellé accompagné de sa raison se lit comme une
                        règle. */}
                    {c.origine === 'ga4' ? (
                      <select
                        value={regie}
                        onChange={(e) => modifier('bloc', e.target.value === c.parent ? '' : e.target.value)}
                        title="Régie à laquelle rattacher ce trafic"
                        style={{
                          padding: '5px 8px', borderRadius: '6px',
                          border: deplacee ? '1px solid #fbbf24' : '1px solid #e5e7eb',
                          fontSize: '0.8125rem', backgroundColor: 'white', minWidth: '140px'
                        }}
                      >
                        {(tableau?.regiesDisponibles || []).map(r => (
                          <option key={r.cle} value={r.cle}>{r.titre}</option>
                        ))}
                      </select>
                    ) : (
                      // La place est tenue pour que les typologies restent alignées d'une
                      // ligne à l'autre. Un tiret plutôt qu'un libellé : la colonne d'à côté
                      // dit déjà de quelle régie il s'agit, et la raison du verrou tient au
                      // survol — la répéter sur chaque ligne noierait la liste.
                      <span
                        title="Campagne remontée par l'API de la régie : c'est ce compte qui a facturé, la déplacer emporterait sa dépense chez un annonceur qui n'a rien dépensé."
                        style={{
                          minWidth: '140px', fontSize: '0.8125rem', color: '#cbd5e1',
                          textAlign: 'center', cursor: 'help'
                        }}
                      >
                        —
                      </span>
                    )}
                    <select
                      value={valeur}
                      onChange={(e) => modifier('sousLevier', e.target.value)}
                      disabled={choix.length === 0}
                      style={{
                        padding: '5px 8px', borderRadius: '6px',
                        border: sansRattachement ? '1px solid #fca5a5' : '1px solid #e5e7eb',
                        fontSize: '0.8125rem',
                        backgroundColor: choix.length === 0 ? '#f8fafc' : 'white',
                        color: choix.length === 0 ? '#94a3b8' : 'inherit',
                        minWidth: '170px'
                      }}
                    >
                      {/* La valeur vide n'est pas « nulle part » : c'est le bloc fourre-tout
                          de la régie, où la campagne atterrit faute de tag reconnu. */}
                      <option value="">{choix.length === 0 ? 'Pas de typologie' : 'Non catégorisé'}</option>
                      {choix.map(sl => <option key={sl.cle} value={sl.cle}>{sl.titre}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>

            {erreurCategorisation && (
              <div style={{
                marginTop: '12px', padding: '10px 12px', borderRadius: '8px',
                backgroundColor: '#fef2f2', border: '1px solid #fca5a5',
                color: '#991b1b', fontSize: '0.8125rem'
              }}>
                {erreurCategorisation}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              {/* Ce qui part réellement au serveur, dit avant de cliquer : une liste
                  déroulante qu'on repose sur sa valeur d'origine ne compte pas. */}
              <span style={{ marginRight: 'auto', fontSize: '0.75rem', color: '#64748b' }}>
                {Object.keys(brouillonCategories).length === 0
                  ? 'Aucune modification à enregistrer'
                  : `${Object.keys(brouillonCategories).length} campagne(s) modifiée(s)`}
              </span>
              <button
                onClick={() => { setErreurCategorisation(''); setParametresOuverts(false); }}
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: '1px solid #e5e7eb',
                  backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem'
                }}
              >
                Annuler
              </button>
              <button
                onClick={enregistrerCategories}
                disabled={enregistrement}
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#7c2d12', color: 'white',
                  cursor: enregistrement ? 'default' : 'pointer', fontSize: '0.875rem'
                }}
              >
                {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
            </>)}
          </div>
        </div>,
        document.body
      )}

      {/* Comparatif : deux périodes et leur écart. Même charpente que le tableau
          chronologique — colonne de libellés collée à gauche, colonnes de valeurs au
          centre, colonne collée à droite — pour qu'on passe de l'un à l'autre sans
          réapprendre à lire. Seules les colonnes changent de nature : deux totaux et un
          écart, au lieu d'une colonne par tranche. */}
      {/* La composition des deux périodes, au-dessus du tableau qu'elle commande. */}
      {vue === 'comparatif' && !chargement && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          padding: '12px 14px', marginBottom: '16px',
          backgroundColor: '#fdf7f3', border: '1px solid #f0e0d6', borderRadius: '10px'
        }}>
          <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#7c2d12' }}>Comparer</span>
          {menuRapide('courante', choixCourante, false)}
          <span style={{ fontSize: '0.8125rem', color: '#a8785e' }}>à</span>
          {menuRapide('reference', choixReference, !choixCourante)}
          {!choixCourante && (
            <span style={{ fontSize: '0.75rem', color: '#a8785e' }}>
              Choisissez d'abord la période à comparer.
            </span>
          )}
        </div>
      )}

      {/* Les deux périodes sont composées, mais la seconde manque : le tableau attend plutôt
          que de montrer un écart contre une référence qui n'est plus celle qu'on vise. */}
      {vue === 'comparatif' && !chargement && choixCourante && !tableauPrecedent && (
        <div style={{
          border: '1px dashed #e5e7eb', borderRadius: '12px', padding: '32px',
          textAlign: 'center', color: '#6b7280', marginBottom: '48px'
        }}>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            Choisissez la période de référence
          </div>
          <div style={{ fontSize: '0.875rem' }}>
            Le tableau s'affichera dès que les deux périodes seront désignées.
          </div>
        </div>
      )}

      {estComparaison(vue) && donneesAffichables && tableauPrecedent && (() => {
        const anneePrecedente = tableauPrecedent.period.debut.slice(0, 4);
        const anneeCourante = tableau.period.debut.slice(0, 4);

        // Recalculées à chaque rendu : la liste dépend de la date du jour et des opérations
        // chargées, et sa construction ne coûte que quelques dizaines d'objets.

        const celluleEvolution = (ligne, lignePrecedente) => {
          const ecart = evolution(lignePrecedente?.total, ligne.total);
          const teinte = couleurEvolution(ligne.cle, ecart);
          return (
            <div style={{
              width: LARGEUR_EVOLUTION, minWidth: LARGEUR_EVOLUTION, height: HAUTEUR_LIGNE,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              padding: '0 10px', boxSizing: 'border-box',
              fontSize: '0.8125rem', fontWeight: teinte ? 600 : 400,
              color: '#1f2937', backgroundColor: 'transparent',
              borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap',
              ...(teinte || {})
            }}>
              {ecart === null ? '' : `${ecart > 0 ? '+' : '−'}${decimal.format(Math.abs(ecart))}%`}
            </div>
          );
        };

        /**
         * En-tête d'une colonne de valeurs, dans le gabarit des colonnes de tranches.
         *
         * La teinte est opaque : la barre qui le porte est figée, et repose donc sur les
         * lignes qui défilent dessous.
         */
        const enteteColonne = (libelle, largeur) => (
          <div style={{
            width: largeur, minWidth: largeur, height: HAUTEUR_ENTETE,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxSizing: 'border-box', fontSize: '0.75rem', fontWeight: 500,
            color: '#7c2d12', backgroundColor: FOND_ENTETE,
            whiteSpace: 'nowrap'
          }}>
            {libelle}
          </div>
        );

        /**
         * La seconde ligne d'en-tête d'une colonne comparée.
         *
         * Normalement la période — « septembre », « S36 », une plage. Mais quand la référence
         * est une opération commerciale, son **nom** en dit bien plus que ses dates : c'est par
         * lui qu'on l'a choisie, et « 05/03 → 12/03 » obligerait à se souvenir de quoi il
         * s'agit. Les dates restent lisibles en infobulle.
         */
        /**
         * La seconde ligne d'en-tête d'une colonne : son nom d'opération, sinon sa période.
         *
         * **Les deux colonnes, et non la seule référence.** Comparer « Opération étudiants » à
         * « black friday » n'affichait le nom que d'un côté : l'autre restait en dates, et il
         * fallait se souvenir de ce qu'on venait de choisir pour savoir ce qu'on lisait.
         *
         * La référence est reconnue par son identifiant, que le réglage porte. La période
         * comparée n'en a pas — elle est posée comme une plage — et se reconnaît donc à ses
         * bornes. Deux opérations aux dates identiques rendraient la première : le cas est
         * improbable, et afficher l'une des deux vaut mieux que de retomber sur des dates.
         */
        const sousTitreColonne = (colonne) => {
          const { debut, fin } = colonne.tableauDuBloc.period;
          const periode = libellePeriode(debut, fin);
          const reglee = comparaisonEffective(vue, comparaison);

          if (colonne.precedent) {
            if (reglee.base !== 'operation') return periode;
            const op = operations.find(o => o.id === reglee.operationId);
            return op ? op.nom : periode;
          }

          const op = operations.find(o => o.debut === debut && o.fin === fin);
          return op ? op.nom : periode;
        };

        /** Bande vide en face d'un intertitre, pour ne pas décaler les colonnes d'une ligne. */
        const bandeTitre = (bloc, largeur) => (
          <div style={{
            width: largeur, minWidth: largeur, height: HAUTEUR_TITRE,
            boxSizing: 'border-box', borderTop: bordureBloc(bloc)
          }} />
        );

        const global = tableau.blocs.find(b => b.cle === 'global');
        const globalPrecedent = lignesPrecedentesParBloc.get('global');
        /**
         * Les trois chiffres de tête du comparatif, selon le modèle.
         *
         * Même défaut que les colonnes de la synthèse, et même correction : écrits en dur, ils
         * annonçaient « TOTAL CA site » et « TOTAL COS site » sur un tableau Lead qui ne porte
         * plus ni l'un ni l'autre — deux bandeaux vides en tête d'écran.
         *
         * Sur Lead, le CA cède la place aux conversions et le COS au CPA : ce sont les deux
         * chiffres qu'on regarde en premier sur ce modèle, le troisième restant le budget.
         */
        const SYNTHESE = tableau?.modele?.actif === 'lead'
          ? [
            { cle: 'conversions', titre: `TOTAL ${tableau?.modele?.libelleConversions || 'Conversions'}` },
            { cle: 'cost', titre: 'TOTAL Budget' },
            { cle: 'cpa', titre: 'TOTAL CPA' }
          ]
          : [
            { cle: 'revenue', titre: 'TOTAL CA site' },
            { cle: 'cost', titre: 'TOTAL Budget' },
            { cle: 'cos', titre: 'TOTAL COS site' }
          ];

        /**
         * Les deux périodes comparées.
         *
         * La clé est le rang, non l'année : depuis que la référence se règle, les deux
         * colonnes peuvent tomber dans la même — septembre comparé à août, ou à une opération
         * du même exercice — et deux clés identiques feraient remonter React.
         *
         * La liste est parcourue deux fois, par l'en-tête figé et par le corps : la décrire
         * ici est ce qui garantit qu'ils portent sur les mêmes colonnes, dans le même ordre.
         */
        const colonnesComparees = [
          { rang: 'reference', annee: anneePrecedente, tableauDuBloc: tableauPrecedent, precedent: true },
          { rang: 'courante', annee: anneeCourante, tableauDuBloc: tableau, precedent: false }
        ];

        return (
          <div style={{ paddingBottom: '12px', marginBottom: '48px' }}>

            {/* Les trois chiffres qu'on regarde en premier, sur les mêmes largeurs que le
                tableau : les colonnes se répondent d'un bloc à l'autre. Dans une bande suivie
                plutôt qu'à l'air libre : le bloc est plus large que l'écran. */}
            <BandeSuivie>
              <div style={{ width: 'max-content', marginBottom: '24px' }}>
                {/* Deux lignes d'en-tête ici aussi, année puis période : le bandeau doit tenir
                    la même hauteur que celles du tableau pour que les colonnes se répondent. */}
                <div style={{ display: 'flex' }}>
                  <div style={{ width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_ENTETE * 2 }} />
                  {[tableauPrecedent, tableau].map((t, i) => (
                    <div key={i} style={{
                      width: LARGEUR_COMPARAISON, minWidth: LARGEUR_COMPARAISON, height: HAUTEUR_ENTETE * 2,
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center',
                      padding: '0 10px', boxSizing: 'border-box', whiteSpace: 'nowrap'
                    }}>
                      <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1f2937' }}>
                        {t.period.debut.slice(0, 4)}
                      </span>
                      <span style={{ fontSize: '0.6875rem', color: '#94a3b8', marginTop: '2px' }}>
                        {libellePeriode(t.period.debut, t.period.fin)}
                      </span>
                    </div>
                  ))}
                  <div style={{ width: LARGEUR_EVOLUTION, minWidth: LARGEUR_EVOLUTION, height: HAUTEUR_ENTETE * 2 }} />
                </div>
                {SYNTHESE.map(entree => {
                  const ligne = global?.lignes.find(l => l.cle === entree.cle);
                  if (!ligne) return null;
                  const lignePrecedente = globalPrecedent?.get(entree.cle);
                  return (
                    <div key={entree.cle} style={{ display: 'flex' }}>
                      <div style={{
                        width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_LIGNE,
                        display: 'flex', alignItems: 'center', padding: '0 10px', boxSizing: 'border-box',
                        fontSize: '0.8125rem', fontWeight: 700, color: '#1f2937',
                        borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap'
                      }}>
                        {entree.titre}
                      </div>
                      {cellule(lignePrecedente ? formater(lignePrecedente.total, lignePrecedente.format) : '', { largeur: LARGEUR_COMPARAISON, fort: true })}
                      {cellule(formater(ligne.total, ligne.format), { largeur: LARGEUR_COMPARAISON, fort: true })}
                      {celluleEvolution(ligne, lignePrecedente)}
                    </div>
                  );
                })}
              </div>
            </BandeSuivie>

            {/* L'en-tête : année puis période, les libellés à gauche et « Évolution » à
                droite, dans les gabarits des colonnes qu'ils coiffent. */}
            <BarreFigee>
              <div style={{
                position: 'sticky', left: 0, zIndex: 2,
                backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
              }}>
                {['Année', 'Période'].map(libelle => (
                  <div key={libelle} style={{
                    width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_ENTETE,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                    padding: '0 10px', boxSizing: 'border-box',
                    fontSize: '0.75rem', fontWeight: 600, color: '#7c2d12', whiteSpace: 'nowrap',
                    // Opaque : le libellé est figé, les lignes passent dessous.
                    backgroundColor: 'white'
                  }}>
                    {libelle}
                  </div>
                ))}
              </div>
              {colonnesComparees.map(colonne => (
                <div key={colonne.rang}>
                  {enteteColonne(colonne.annee, LARGEUR_COMPARAISON)}
                  {enteteColonne(sousTitreColonne(colonne), LARGEUR_COMPARAISON)}
                </div>
              ))}
              <div style={{
                position: 'sticky', right: 0, zIndex: 2,
                backgroundColor: 'white', borderLeft: '2px solid #e5e7eb'
              }}>
                <div style={{
                  width: LARGEUR_EVOLUTION, minWidth: LARGEUR_EVOLUTION, height: HAUTEUR_ENTETE,
                  backgroundColor: 'white'
                }} />
                <div style={{
                  width: LARGEUR_EVOLUTION, minWidth: LARGEUR_EVOLUTION, height: HAUTEUR_ENTETE,
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  padding: '0 10px', boxSizing: 'border-box',
                  fontSize: '0.6875rem', fontWeight: 600, color: '#9ca3af',
                  textTransform: 'uppercase', letterSpacing: '0.02em',
                  backgroundColor: 'white'
                }}>
                  Évolution
                </div>
              </div>
            </BarreFigee>

            <CorpsDefilant>
              <div style={{ display: 'flex', width: 'max-content' }}>

                {/* Colonne des libellés, tenue à gauche — comme dans le tableau chronologique */}
                <div style={{
                  position: 'sticky', left: 0, zIndex: 2,
                  backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
                }}>
                  {blocsVisibles.map(bloc => (
                    <React.Fragment key={bloc.cle}>
                      <div
                        {...proprietesTitreBloc(bloc)}
                        style={{
                          width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_TITRE,
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: `0 10px 0 ${10 + profondeur(bloc) * 18}px`,
                          boxSizing: 'border-box',
                          fontSize: '0.75rem', fontWeight: profondeur(bloc) === 0 ? 700 : 600,
                          color: profondeur(bloc) >= 2 ? '#a98a76' : (bloc.parent ? '#9a6b4f' : '#7c2d12'),
                          textTransform: 'uppercase', letterSpacing: '0.03em',
                          borderTop: bordureBloc(bloc),
                          cursor: estRepliable(bloc) ? 'pointer' : 'default', userSelect: 'none',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}
                      >
                        {estRepliable(bloc) && (estReplie(bloc.cle) ? <ChevronRight size={14} /> : <ChevronDown size={14} />)}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{bloc.titre}</span>
                        {/* Sans ce repère, rien ne dirait qu'il y a quelque chose à survoler. */}
                        {marqueurAide(bloc)}
                      </div>
                      {lignesRetenues(bloc).map(l => (
                        <div
                          key={l.cle}
                          title={l.aide || l.libelle}
                          style={{
                            width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_LIGNE,
                            display: 'flex', alignItems: 'center',
                            padding: '0 10px', boxSizing: 'border-box',
                            fontSize: '0.8125rem', fontWeight: l.fort ? 600 : 400,
                            color: couleurLibelle(l.source),
                            borderBottom: '1px solid #f1f5f9',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                          }}
                        >
                          {libelleDeLigne(l)}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>

                {colonnesComparees.map(colonne => (
                  <div key={colonne.rang}>
                    {blocsVisibles.map(bloc => {
                      const precedentes = lignesPrecedentesParBloc.get(bloc.cle);
                      return (
                        <React.Fragment key={bloc.cle}>
                          {bandeTitre(bloc, LARGEUR_COMPARAISON)}
                          {lignesRetenues(bloc).map(l => {
                            const source = colonne.precedent ? precedentes?.get(l.cle) : l;
                            return (
                              <div key={l.cle}>
                                {cellule(source ? formater(source.total, source.format) : '', {
                                  largeur: LARGEUR_COMPARAISON,
                                  fort: l.fort
                                })}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </div>
                ))}

                {/* Évolution, tenue à droite comme l'est la colonne des totaux */}
                <div style={{
                  position: 'sticky', right: 0, zIndex: 2,
                  backgroundColor: 'white', borderLeft: '2px solid #e5e7eb'
                }}>
                  {blocsVisibles.map(bloc => {
                    const precedentes = lignesPrecedentesParBloc.get(bloc.cle);
                    return (
                      <React.Fragment key={bloc.cle}>
                        {bandeTitre(bloc, LARGEUR_EVOLUTION)}
                        {lignesRetenues(bloc).map(l => (
                          <div key={l.cle}>{celluleEvolution(l, precedentes?.get(l.cle))}</div>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </CorpsDefilant>
          </div>
        );
      })()}

      {/* Synthèse : le tableau transposé. Une ligne par levier, une colonne par métrique,
          plus trois colonnes de poids qui n'ont de sens qu'ici — elles rapportent un levier
          à l'ensemble, ce qu'un tableau montrant un levier à la fois ne peut pas dire. */}
      {/* Infobulle des notes. En portail et en position fixe : dans le flux, elle serait
          rognée par le conteneur à défilement horizontal du tableau. */}
      {noteSurvolee && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          // Recalée si elle déborde à droite : une note collée au bord se lit mal
          left: Math.min(noteSurvolee.x, window.innerWidth - 340),
          top: noteSurvolee.bas + 6,
          width: '320px', zIndex: 10000,
          backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '10px 12px',
          fontSize: '0.8125rem', color: '#1f2937', pointerEvents: 'none'
        }}>
          {noteSurvolee.notes.map((n, i) => (
            <div key={n.id} style={{ marginTop: i > 0 ? '10px' : 0, paddingTop: i > 0 ? '10px' : 0, borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: couleurDeNote(n), flexShrink: 0 }} />
                {/* Le sujet en toutes lettres : la pastille de la ligne « Notes » en porte la
                    couleur, mais une cellule de cent pixels ne peut pas le nommer. */}
                {sujetDeNote(n.sujet) && (
                  <span style={{
                    padding: '1px 7px', borderRadius: '20px', fontSize: '0.625rem', fontWeight: 500,
                    color: sujetDeNote(n.sujet).couleur, backgroundColor: sujetDeNote(n.sujet).fond,
                    whiteSpace: 'nowrap'
                  }}>
                    {sujetDeNote(n.sujet).label}
                  </span>
                )}
                <span style={{ fontSize: '0.6875rem', color: '#94a3b8' }}>
                  {n.debut === n.fin ? n.debut : `${n.debut} → ${n.fin}`}
                </span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{n.contenu}</div>
            </div>
          ))}
        </div>,
        document.body
      )}

      {/* Identifiants des comptes croisés. En portail, comme les autres : la barre d'outils
          et le tableau collant la rogneraient dans le flux. */}
      {/* Détail source / support d'un bloc fourre-tout. Même infobulle que les notes, même
          raison : dans le flux, elle serait rognée par le conteneur à défilement. */}
      {sourcesSurvolees && (() => {
        /**
         * Où poser la bulle.
         *
         * Elle s'ouvre sous l'icône, puis **se recale une fois mesurée** : sa hauteur ne se
         * connaît pas avant le rendu, et l'estimer conduisait à la tronquer. Elle était
         * plafonnée à la moitié de l'écran et se repliait derrière un ascenseur alors que la
         * place existait — sur le détail d'un fourre-tout, qui est une longue liste de valeurs
         * et justement ce qu'on vient lire en entier, seul un tiers du texte était visible.
         *
         * Le recalage est impératif et sans état : mesurer dans un `useState` relancerait un
         * rendu, qui remesurerait, et ainsi de suite.
         */
        const MARGE = 12;

        const recaler = (element) => {
          if (!element) return;

          const hauteur = element.getBoundingClientRect().height;
          const debordement = element.getBoundingClientRect().bottom - (window.innerHeight - MARGE);
          if (debordement <= 0) return;

          // On remonte juste ce qu'il faut, sans jamais dépasser le haut de la fenêtre.
          element.style.top = `${Math.max(MARGE, window.innerHeight - MARGE - hauteur)}px`;
        };

        return ReactDOM.createPortal(
        <div ref={recaler} style={{
          position: 'fixed',
          left: Math.min(sourcesSurvolees.x, window.innerWidth - 360),
          top: sourcesSurvolees.bas + 6,
          width: '340px',
          // Toute la hauteur utile de la fenêtre, et non la moitié : l'ascenseur ne doit
          // apparaître que pour un contenu réellement plus grand que l'écran.
          maxHeight: `${window.innerHeight - MARGE * 2}px`,
          overflowY: 'auto', zIndex: 10000,
          backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '10px 12px',
          fontSize: '0.8125rem', color: '#1f2937', pointerEvents: 'none'
        }}>
          <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginBottom: '6px' }}>
            {sourcesSurvolees.bloc.titre}
          </div>
          {/* Deux niveaux de lecture. D'abord ce que le bloc contient, en français ; puis
              les valeurs exactes sur lesquelles il se coupe, dérivées du filtre lui-même.
              Le second répond à la question qui vient dès qu'un total ne tombe pas juste :
              « qu'est-ce qui est compté ici, au juste ? ». */}
          {sourcesSurvolees.bloc.aide && (
            <div style={{ lineHeight: 1.5, color: '#334155' }}>
              {sourcesSurvolees.bloc.aide}
            </div>
          )}
          {sourcesSurvolees.bloc.rattachement && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '6px',
              marginTop: sourcesSurvolees.bloc.aide ? '8px' : 0,
              lineHeight: 1.5,
              color: sourcesSurvolees.bloc.rattachement.connecte ? '#15803d' : '#b45309'
            }}>
              <span style={{
                width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0, marginTop: '6px',
                /* Gris quand des comptes sont écartés sans qu'aucun ne manque : ce n'est pas
                   un rattachement à corriger, c'est un choix qu'on a fait. */
                backgroundColor: sourcesSurvolees.bloc.rattachement.connecte ? '#22c55e'
                  : sourcesSurvolees.bloc.rattachement.ecarte ? '#94a3b8' : '#f59e0b'
              }} />
              <span>
                {sourcesSurvolees.bloc.rattachement.texte}
                {/* Les noms des comptes : c'est ce qui permet de vérifier qu'on croise le
                    bon, là où un type de régie ne dit pas lequel. */}
                {sourcesSurvolees.bloc.rattachement.comptes?.length > 0 && (
                  <span style={{ display: 'block', color: '#64748b', marginTop: '2px' }}>
                    {sourcesSurvolees.bloc.rattachement.comptes.join(' · ')}
                  </span>
                )}
                {/* Les écartés, nommés.

                    C'est le cas mixte qui l'exige — deux comptes de la même régie, un seul
                    compté : le bloc dit « rattaché », le budget ne tombe pas juste, et sans
                    cette ligne on chercherait une erreur de calcul pendant une heure. */}
                {sourcesSurvolees.bloc.rattachement.comptesEcartes?.length > 0 && (
                  <span style={{ display: 'block', color: '#92400e', marginTop: '4px' }}>
                    Écarté{sourcesSurvolees.bloc.rattachement.comptesEcartes.length > 1 ? 's' : ''} du
                    consolidé : {sourcesSurvolees.bloc.rattachement.comptesEcartes.join(' · ')}
                  </span>
                )}
              </span>
            </div>
          )}
          {sourcesSurvolees.bloc.technique && (
            <div style={{
              marginTop: (sourcesSurvolees.bloc.aide || sourcesSurvolees.bloc.rattachement) ? '8px' : 0,
              padding: '6px 8px', borderRadius: '6px', backgroundColor: '#f8fafc',
              fontSize: '0.6875rem', lineHeight: 1.5, color: '#64748b',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-word'
            }}>
              {sourcesSurvolees.bloc.technique}
            </div>
          )}
          {sourcesSurvolees.bloc.sources?.length > 0 && (
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }} />
          )}
          {/* Groupé par canal, et non en liste plate de couples source / support.
              Le couple ne suffit pas à dire d'où vient une session : le même
              `google / organic` se répartit entre « Organic Search » et « Organic
              Shopping », et un référent depuis un site marchand connu de Google part en
              « Organic Shopping » plutôt qu'en « Referral ». Sans le canal en tête, la
              ligne se lit comme un classement fautif. */}
          {[...(sourcesSurvolees.bloc.sources || [])
            .reduce((parCanal, s) => parCanal.set(s.canal, [...(parCanal.get(s.canal) || []), s]), new Map())
          ].map(([canal, lignes]) => (
            <div key={canal} style={{ marginBottom: '8px' }}>
              <div style={{ fontWeight: 600, fontSize: '0.75rem', marginBottom: '2px' }}>{canal}</div>
              {lignes.map((s, i) => (
                <div key={`${s.source}|${s.support}|${i}`} style={{
                  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                  gap: '10px', padding: '1px 0 1px 10px'
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.source} / {s.support}
                  </span>
                  <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                    {entier.format(s.sessions)}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {/* Le serveur plafonne la liste : le dire évite de lire un total tronqué comme
              un total complet. */}
          {sourcesSurvolees.bloc.sources?.length >= 20 && (
            <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginTop: '6px' }}>
              20 premiers couples, par sessions décroissantes.
            </div>
          )}
          {/* Chez Piano et Matomo, la liste et le total du bloc ne viennent pas du même
              relevé. Le serveur rédige la phrase, à côté du calcul qui la justifie. */}
          {sourcesSurvolees.bloc.sources?.length > 0 && sourcesSurvolees.bloc.sourcesNote && (
            <div style={{ fontSize: '0.6875rem', color: '#94a3b8', marginTop: '6px', lineHeight: 1.45 }}>
              {sourcesSurvolees.bloc.sourcesNote}
            </div>
          )}
        </div>,
        document.body
        );
      })()}

      {/* Graphes : un petit multiple par métrique du levier choisi.
          Une seule série par graphe — vingt leviers superposés ne se lisent pas, et le
          titre de chaque graphe porte l'identité que la couleur n'a pas à porter. */}
      {vue === 'graphes' && donneesAffichables && (() => {
        const bloc = tableau.blocs.find(b => b.cle === levierTrace) || tableau.blocs[0];
        if (!bloc) return null;

        const abscisse = (c) => (c.date ? `${c.date.slice(8, 10)}/${c.date.slice(5, 7)}` : c.cle);
        const points = colonnes.map((c, i) => ({
          x: abscisse(c),
          ...Object.fromEntries(bloc.lignes.map(l => [l.cle, l.valeurs[i]]))
        }));

        // Repères d'axe compacts : « 12 k » vaut mieux que « 12 345 » sur une graduation
        const abrege = (v) => {
          if (v === null || v === undefined) return '';
          const a = Math.abs(v);
          if (a >= 1000000) return `${decimal.format(v / 1000000)} M`;
          if (a >= 1000) return `${decimal.format(v / 1000)} k`;
          return entier.format(v);
        };

        return (
          <div style={{ marginBottom: '48px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>Levier</span>
              <select
                value={bloc.cle}
                onChange={(e) => setLevierTrace(e.target.value)}
                style={{
                  padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb',
                  fontSize: '0.875rem', backgroundColor: 'white', cursor: 'pointer'
                }}
              >
                {tableau.blocs.map(b => (
                  <option key={b.cle} value={b.cle}>
                    {'\u00A0'.repeat(profondeur(b) * 4)}{b.titre}
                  </option>
                ))}
              </select>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '16px'
            }}>
              {bloc.lignes.map(ligne => (
                <div key={ligne.cle} style={{
                  border: '1px solid #e5e7eb', borderRadius: '10px',
                  padding: '12px 12px 4px', backgroundColor: 'white'
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1f2937' }}>
                      {ligne.libelle}
                    </span>
                    <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                      {formater(ligne.total, ligne.format)}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={150}>
                    <LineChart data={points} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                      <CartesianGrid stroke="#f1f5f9" vertical={false} />
                      <XAxis
                        dataKey="x"
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        interval="preserveStartEnd"
                        minTickGap={24}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#94a3b8' }}
                        tickLine={false}
                        axisLine={false}
                        width={48}
                        tickFormatter={abrege}
                      />
                      <Tooltip
                        formatter={(valeur) => [formater(valeur, ligne.format), ligne.libelle]}
                        contentStyle={{ fontSize: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Line
                        type="monotone"
                        dataKey={ligne.cle}
                        stroke={COULEUR_COURBE}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        connectNulls={false}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Projection : le réalisé du mois en cours et son atterrissage. Même charpente que
          les autres vues, deux colonnes au lieu d'une par tranche. */}
      {vue === 'projection' && donneesAffichables && (() => {
        const info = tableau.projection;

        if (!info || info.impossible) {
          return (
            <div style={{
              padding: '12px', borderRadius: '8px', backgroundColor: '#fffbeb',
              color: '#92400e', fontSize: '0.875rem', marginBottom: '48px'
            }}>
              Aucune journée pleine n'est encore écoulée ce mois-ci : il n'y a rien à
              extrapoler. La projection sera disponible demain.
            </div>
          );
        }

        const part = (info.joursCouverts / info.joursDuMois) * 100;

        const celluleValeur = (contenu, largeur, fort) => (
          <div style={{
            width: largeur, minWidth: largeur, height: HAUTEUR_LIGNE,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            padding: '0 10px', boxSizing: 'border-box',
            fontSize: '0.8125rem', fontWeight: fort ? 600 : 400, color: '#1f2937',
            borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap'
          }}>
            {contenu}
          </div>
        );

        /**
         * L'en-tête d'une colonne : une seule bande, de double hauteur, qui porte le nom de la
         * colonne et sa période — là où le comparatif empile deux bandes distinctes.
         */
        const enteteColonne = (haut, bas, largeur, teinte) => (
          <div style={{
            width: largeur, minWidth: largeur, height: HAUTEUR_ENTETE * 2,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            boxSizing: 'border-box', fontSize: '0.75rem', fontWeight: 600,
            color: '#7c2d12', backgroundColor: teinte, whiteSpace: 'nowrap'
          }}>
            <span>{haut}</span>
            <span style={{ fontSize: '0.6875rem', fontWeight: 400, color: '#9a6b4f', marginTop: '2px' }}>{bas}</span>
          </div>
        );

        /**
         * L'atterrissage face aux budgets, destination par destination.
         *
         * ## Ce que chaque colonne dit, et ce qu'elle ne dit pas
         *
         * Le **budgété** est l'enveloppe du mois entier, telle qu'elle a été saisie. Il n'est
         * pas extrapolé : il n'y a rien à extrapoler dans une prévision.
         *
         * L'**atterrissage** est la dépense du mois entier telle qu'elle se profile, obtenue en
         * étendant les journées écoulées au mois complet. Le réalisé qui la fonde n'est pas
         * affiché — il se lit dans la vue détaillée, à côté, et le porter ici ferait quatre
         * colonnes dont deux disent la même chose à deux échelles.
         *
         * L'**écart** rapporte le second au premier. Positif, la dépense dépasse l'enveloppe.
         *
         * ## Ce qui n'est pas affiché
         *
         * Une destination sans budget **ni** dépense n'a pas de ligne. Les dix-neuf destinations
         * du catalogue tiennent l'écran entier alors que trois ou quatre portent des chiffres :
         * le tableau se lisait alors comme une liste de cases vides, et l'œil devait chercher
         * les deux lignes qui parlent. Le serveur les envoie toutes — c'est l'écran qui choisit
         * ce qu'il montre, et le filtre suit donc immédiatement la lecture de la charge.
         *
         * Les familles sans aucune ligne retenue disparaissent avec leur intertitre : un titre
         * de section suivi de rien se lit comme une donnée manquante.
         *
         * En revanche, **une seule des deux colonnes suffit** à retenir une destination : une
         * dépense sans budget est précisément ce qu'on veut voir, et une enveloppe sans dépense
         * aussi. Leur écart, lui, reste vide — il n'y a rien pour rapporter l'un à l'autre.
         */
        /**
         * Le bouton qui bascule entre le détail des métriques et la lecture budgétaire.
         *
         * Il vit dans la cellule d'en-tête de la colonne des libellés, au-dessus des intitulés
         * de blocs, et non dans la barre de réglages : il ne règle pas la période ni la
         * propriété, il change ce que **ce tableau-là** montre. Le placer parmi les réglages
         * de la vue l'éloignait de ce qu'il commande, et laissait croire qu'il valait pour
         * l'écran entier.
         */
        const boutonBudget = (
          <button
            onClick={() => setFaceAuBudget(v => !v)}
            title={faceAuBudget
              ? 'Revenir au détail des métriques'
              : 'Comparer l\'atterrissage du mois aux budgets saisis, destination par destination'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              padding: '4px 8px', borderRadius: '6px', cursor: 'pointer',
              border: '1px solid ' + (faceAuBudget ? '#7c2d12' : '#e5e7eb'),
              backgroundColor: faceAuBudget ? '#7c2d12' : 'white',
              color: faceAuBudget ? 'white' : '#7c2d12',
              fontSize: '0.75rem', fontWeight: faceAuBudget ? 600 : 400,
              whiteSpace: 'nowrap', lineHeight: 1.2
            }}
          >
            <Wallet size={13} /> Budgété
          </button>
        );

        if (faceAuBudget) {
          const destinations = (info.budgets || []).filter(
            d => typeof d.budget === 'number' || typeof d.atterrissage === 'number'
          );

          if (destinations.length === 0) {
            return (
              <div style={{
                padding: '12px', borderRadius: '8px', backgroundColor: '#fffbeb',
                color: '#92400e', fontSize: '0.875rem', marginBottom: '48px'
              }}>
                Aucune destination ne porte de budget ni de dépense sur ce mois. Les budgets se
                saisissent dans « Données saisies manuellement », onglet Budgété.
              </div>
            );
          }

          const ecartDe = (d) => (
            typeof d.budget === 'number' && d.budget > 0 && typeof d.atterrissage === 'number'
              ? ((d.atterrissage - d.budget) / d.budget) * 100
              : null
          );

          /** L'écart en euros, qui ne demande pas de budget non nul, lui. */
          const ecartEnValeurDe = (d) => (
            typeof d.budget === 'number' && typeof d.atterrissage === 'number'
              ? d.atterrissage - d.budget
              : null
          );

          /**
           * Le signe se décide sur ce qui sera **affiché**, pas sur la valeur brute.
           *
           * Un écart de deux millièmes d'euro s'arrondit à « 0 € » : le préfixer d'un moins
           * donnerait « −0 € », qui se lit comme une économie minuscule alors que le budget
           * est tenu. Le seuil est donc celui de l'arrondi de chaque format.
           */
          const signe = (valeur, seuil) => (valeur > seuil ? '+' : (valeur < -seuil ? '−' : ''));

          const joursRestants = info.joursDuMois - info.joursCouverts;

          /**
           * Ce qu'il reste à dépenser par jour pour finir sur l'enveloppe.
           *
           * Il se calcule sur le **réalisé**, jamais sur l'atterrissage : l'atterrissage est
           * une hypothèse sur la fin du mois, et diviser une hypothèse par les jours qui
           * restent reviendrait à demander de dépenser ce qu'on suppose déjà dépensé.
           *
           * Trois cas rendent une cellule vide plutôt qu'un chiffre : pas de budget, rien à
           * diviser ; plus un jour devant, la question ne se pose plus ; et l'enveloppe déjà
           * consommée, qui donnerait un résiduel négatif — ce n'est pas un rythme, c'est un
           * dépassement, et la colonne d'écart le dit déjà.
           */
          const resteDe = (d) => (
            typeof d.budget === 'number'
              ? d.budget - (typeof d.realise === 'number' ? d.realise : 0)
              : null
          );

          /**
           * Le rythme qu'il reste à tenir, sous le montant qui reste.
           *
           * Absent quand l'enveloppe est déjà dépassée : un « par jour » négatif n'est pas un
           * rythme, et le montant restant, lui, dit déjà le dépassement. Absent aussi le dernier
           * jour du mois, où il n'y a plus de jour à venir sur quoi répartir quoi que ce soit.
           */
          const parJourDe = (d) => {
            const reste = resteDe(d);
            if (reste === null || reste <= 0 || joursRestants <= 0) return null;
            return reste / joursRestants;
          };

          const totaux = destinations.reduce((somme, d) => ({
            budget: typeof d.budget === 'number' ? (somme.budget || 0) + d.budget : somme.budget,
            // Le réalisé porte sa colonne et le résiduel de la ligne de total.
            realise: typeof d.realise === 'number' ? (somme.realise || 0) + d.realise : somme.realise,
            atterrissage: typeof d.atterrissage === 'number'
              ? (somme.atterrissage || 0) + d.atterrissage
              : somme.atterrissage
          }), { budget: null, realise: null, atterrissage: null });

          /** Budgété sans rien en face : ce que le total additionne sans pouvoir le comparer. */
          const sansDepense = destinations.filter(
            d => typeof d.budget === 'number' && typeof d.atterrissage !== 'number'
          );

          const FAMILLES = [
            { cle: 'gratuit', titre: 'Canaux sans régie' },
            { cle: 'regie', titre: 'Régies' },
            { cle: 'global', titre: 'Hors canal' }
          ];

          const cellule = (contenu, { fort = false, teinte = null, centre = false, haut = false, largeur = LARGEUR_COMPARAISON } = {}) => (
            <div style={{
              width: largeur, minWidth: largeur, height: HAUTEUR_LIGNE,
              display: 'flex', alignItems: 'center', justifyContent: centre ? 'center' : 'flex-end',
              padding: '0 10px', boxSizing: 'border-box',
              fontSize: '0.8125rem', fontWeight: fort || teinte ? 600 : 400, color: '#1f2937',
              borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap',
              ...(haut ? { borderTop: '2px solid #e5e7eb' } : {}),
              ...(teinte || {})
            }}>
              {contenu}
            </div>
          );

          /**
           * @param haut  trait de séparation au-dessus — porté par les cellules, non par un
           *              bandeau qui les recouvre : un bandeau de 2 px interrompait le trait
           *              vertical, qui repartait deux pixels plus bas.
           */
          const ligneDestination = (d, fort, haut = false) => {
            const ecart = ecartDe(d);
            const ecartEuros = ecartEnValeurDe(d);
            const teinte = couleurEvolution('cost', ecart);
            return (
              <div key={d.cle} style={{ display: 'flex' }}>
                <div style={{
                  width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_LIGNE,
                  display: 'flex', alignItems: 'center', padding: '0 10px', boxSizing: 'border-box',
                  fontSize: '0.8125rem', fontWeight: fort ? 600 : 400, color: '#1f2937',
                  borderBottom: '1px solid #f1f5f9', borderRight: '2px solid #e5e7eb',
                  ...(haut ? { borderTop: '2px solid #e5e7eb' } : {}),
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {d.titre}
                </div>
                {cellule(formater(d.budget, 'devise'), { fort, haut })}
                {cellule(formater(d.realise, 'devise'), { fort, haut })}
                {cellule(formater(d.atterrissage, 'devise'), { fort, haut })}
                {/*
                  La teinte du comparatif, et sa fonction : `couleurEvolution` est appelée avec
                  la clé `cost`, dont le sens de lecture est « la hausse est une mauvaise
                  nouvelle » — ce qui est exactement celui d'un dépassement d'enveloppe.
                  Reprendre la fonction plutôt que ses deux couleurs garantit que le jour où
                  la palette du comparatif changera, cette colonne suivra.

                  Elle ne teinte rien en deçà d'un demi-point : pile sur le budget n'est ni un
                  dépassement ni une économie, et le peindre trancherait à tort.

                  Deux étages dans la même cellule : le pourcentage dit l'ampleur relative, les
                  euros disent ce que ça coûte. L'un sans l'autre se lit mal — 30 % de dépassement
                  ne se décide pas pareil sur une enveloppe de mille euros ou de cent mille.
                */}
                {cellule(
                  ecart === null && ecartEuros === null ? '' : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
                      <span>{ecart === null ? '' : `${signe(ecart, 0.05)}${decimal.format(Math.abs(ecart))}%`}</span>
                      {ecartEuros !== null && (
                        <span style={{ fontSize: '0.6875rem', fontWeight: 400, opacity: 0.85 }}>
                          {`${signe(ecartEuros, 0.5)}${devise.format(Math.abs(ecartEuros))}`}
                        </span>
                      )}
                    </div>
                  ),
                  { fort, teinte, centre: true, haut }
                )}
                {/*
                  Le budget restant ne se teinte pas. C'est une consigne — « il vous reste ceci,
                  à ce rythme » —, pas un jugement : la couleur y ferait lire une alerte là où il
                  n'y a qu'un chiffre à suivre, et elle est déjà portée par la colonne d'à côté.
                */}
                {cellule(
                  resteDe(d) === null ? '' : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
                      <span>{devise.format(resteDe(d))}</span>
                      {parJourDe(d) !== null && (
                        <span style={{ fontSize: '0.6875rem', fontWeight: 400, opacity: 0.85 }}>
                          {`${devise.format(parJourDe(d))} / jour`}
                        </span>
                      )}
                    </div>
                  ),
                  { fort, centre: true, haut }
                )}
              </div>
            );
          };

          return (
            <div style={{ overflowX: 'auto', paddingBottom: '12px', marginBottom: '48px' }}>
              <div style={{ fontSize: '0.8125rem', color: '#64748b', marginBottom: '12px' }}>
                {info.joursCouverts} jour{info.joursCouverts > 1 ? 's' : ''} sur {info.joursDuMois}
                {' '}({decimal.format(part)} % du mois) — le budgété est l'enveloppe du mois
                entier telle qu'elle a été saisie ; le réalisé est ce qui a été dépensé sur les
                jours couverts ; l'atterrissage suppose que le reste du mois ressemble à ces
                journées-là. Une destination sans budget saisi reste vide, jamais à zéro.
              </div>

              <div style={{ width: 'max-content' }}>
                <div style={{ display: 'flex' }}>
                  <div style={{
                    width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_ENTETE * 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '8px', padding: '0 10px', boxSizing: 'border-box',
                    fontSize: '0.75rem', fontWeight: 600, color: '#7c2d12',
                    borderRight: '2px solid #e5e7eb'
                  }}>
                    {boutonBudget}
                    <span>Destination</span>
                  </div>
                  {[
                    { haut: 'Budgété', bas: libellePeriode(tableau.period.debut, info.finDuMois), teinte: 'rgba(251, 146, 60, 0.12)' },
                    /*
                      Le réalisé se lit entre l'enveloppe et la projection, parce que c'est là
                      qu'il se comprend : ce qui est engagé à ce jour, entre ce qui était prévu
                      pour le mois entier et ce vers quoi la fin du mois tend.
                      Sa période n'est pas celle de ses voisines — elle s'arrête au dernier jour
                      couvert, pas à la fin du mois — et l'en-tête le dit, sans quoi les trois
                      colonnes se liraient comme trois mesures du même intervalle.
                    */
                    { haut: 'Réalisé', bas: libellePeriode(tableau.period.debut, tableau.period.fin), teinte: 'rgba(251, 146, 60, 0.20)' },
                    { haut: 'Atterrissage', bas: libellePeriode(tableau.period.debut, info.finDuMois), teinte: 'rgba(251, 146, 60, 0.28)' },
                    { haut: 'Écart', bas: 'atterrissage / budgété', teinte: 'rgba(251, 146, 60, 0.12)' },
                    {
                      haut: 'Budget restant',
                      bas: joursRestants > 0
                        ? `${joursRestants} jour${joursRestants > 1 ? 's' : ''} restant${joursRestants > 1 ? 's' : ''}`
                        : 'mois terminé',
                      teinte: 'rgba(251, 146, 60, 0.12)'
                    }
                  ].map(c => enteteColonne(c.haut, c.bas, LARGEUR_COMPARAISON, c.teinte))}
                </div>

                {FAMILLES.map(famille => {
                  const lignes = destinations.filter(d => d.famille === famille.cle);
                  if (lignes.length === 0) return null;
                  return (
                    <React.Fragment key={famille.cle}>
                      {/*
                        L'intertitre est découpé comme une ligne de données, et non posé en
                        travers du tableau : le trait qui sépare la colonne des destinations des
                        colonnes de chiffres court sur toute la hauteur, au lieu de s'interrompre
                        à chaque famille. Un trait discontinu se lit comme deux tableaux.
                      */}
                      <div style={{ display: 'flex' }}>
                        <div style={{
                          width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES,
                          height: HAUTEUR_TITRE, display: 'flex', alignItems: 'center',
                          padding: '0 10px', boxSizing: 'border-box',
                          fontSize: '0.75rem', fontWeight: 700, color: '#7c2d12',
                          textTransform: 'uppercase', letterSpacing: '0.03em',
                          borderTop: '2px solid #e5e7eb', borderRight: '2px solid #e5e7eb'
                        }}>
                          {famille.titre}
                        </div>
                        <div style={{
                          width: LARGEUR_COMPARAISON * 5, minWidth: LARGEUR_COMPARAISON * 5,
                          height: HAUTEUR_TITRE, boxSizing: 'border-box',
                          borderTop: '2px solid #e5e7eb'
                        }} />
                      </div>
                      {lignes.map(d => ligneDestination(d, false))}
                    </React.Fragment>
                  );
                })}

                {ligneDestination({ cle: '__total', titre: 'Total', ...totaux }, true, true)}
              </div>

              {/*
                Le total additionne des enveloppes dont certaines n'ont encore rien en face.
                Une régie non connectée, un canal sans dépense saisie : leur budget pèse dans
                la colonne de gauche et rien dans celle de droite, et l'écart total se lit
                alors comme une économie qui n'en est pas une. Les nommer vaut mieux que de
                retrancher leur budget en silence.
              */}
              {sansDepense.length > 0 && (
                <div style={{
                  marginTop: '10px', fontSize: '0.8125rem', color: '#92400e',
                  backgroundColor: '#fffbeb', borderRadius: '8px', padding: '10px 12px',
                  maxWidth: LARGEUR_LIBELLES + LARGEUR_COMPARAISON * 4
                }}>
                  {sansDepense.length === 1
                    ? `${sansDepense[0].titre} est budgété sans aucune dépense mesurée : son enveloppe pèse`
                    : `${sansDepense.length} destinations sont budgétées sans aucune dépense mesurée `
                      + `(${sansDepense.map(d => d.titre).join(', ')}) : leurs enveloppes pèsent`}
                  {' '}dans le total sans rien en face, qui y paraît donc plus économe qu'il ne l'est.
                </div>
              )}
            </div>
          );
        }


        /**
         * Les deux colonnes de la projection, parcourues par l'en-tête figé puis par le
         * corps : les décrire ici est ce qui garantit qu'ils portent sur les mêmes.
         */
        const colonnesProjetees = [
          { cle: 'total', haut: 'Réalisé', bas: libellePeriode(tableau.period.debut, tableau.period.fin), teinte: FOND_ENTETE },
          { cle: 'projection', haut: 'Atterrissage', bas: libellePeriode(tableau.period.debut, info.finDuMois), teinte: FOND_ENTETE_FORT }
        ];

        return (
          <div style={{ paddingBottom: '12px', marginBottom: '48px' }}>
            <div style={{ fontSize: '0.8125rem', color: '#64748b', marginBottom: '12px' }}>
              {info.joursCouverts} jour{info.joursCouverts > 1 ? 's' : ''} sur {info.joursDuMois}
              {' '}({decimal.format(part)} % du mois) — l'atterrissage suppose que le reste du
              mois ressemble aux journées écoulées. Les ratios (COS, CPA, CPC…) sont donc
              identiques dans les deux colonnes : seuls les volumes s'extrapolent.
            </div>

            <BarreFigee>
              <div style={{
                position: 'sticky', left: 0, zIndex: 2,
                backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
              }}>
                <div style={{
                  width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_ENTETE * 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '8px', padding: '0 10px', boxSizing: 'border-box',
                  fontSize: '0.75rem', fontWeight: 600, color: '#7c2d12',
                  // Opaque : la bande est figée, les lignes passent dessous. Elle porte aussi
                  // le bouton de bascule, qui reste donc atteignable sans remonter.
                  backgroundColor: 'white'
                }}>
                  {boutonBudget}
                  <span>Période</span>
                </div>
              </div>
              {colonnesProjetees.map(colonne => (
                <div key={colonne.cle}>
                  {enteteColonne(colonne.haut, colonne.bas, LARGEUR_COMPARAISON, colonne.teinte)}
                </div>
              ))}
            </BarreFigee>

            <CorpsDefilant>
              <div style={{ display: 'flex', width: 'max-content' }}>
                <div style={{
                  position: 'sticky', left: 0, zIndex: 2,
                  backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
                }}>
                  {blocsVisibles.map(bloc => (
                    <React.Fragment key={bloc.cle}>
                      <div
                        {...proprietesTitreBloc(bloc)}
                        style={{
                          width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_TITRE,
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: `0 10px 0 ${10 + profondeur(bloc) * 18}px`, boxSizing: 'border-box',
                          fontSize: '0.75rem', fontWeight: profondeur(bloc) === 0 ? 700 : 600,
                          color: profondeur(bloc) >= 2 ? '#a98a76' : (bloc.parent ? '#9a6b4f' : '#7c2d12'),
                          textTransform: 'uppercase', letterSpacing: '0.03em',
                          borderTop: bordureBloc(bloc),
                          cursor: estRepliable(bloc) ? 'pointer' : 'default', userSelect: 'none',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}
                      >
                        {estRepliable(bloc) && (estReplie(bloc.cle) ? <ChevronRight size={14} /> : <ChevronDown size={14} />)}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{bloc.titre}</span>
                        {/* Sans ce repère, rien ne dirait qu'il y a quelque chose à survoler. */}
                        {marqueurAide(bloc)}
                      </div>
                      {lignesRetenues(bloc).map(l => (
                        <div
                          key={l.cle}
                          title={l.aide || l.libelle}
                          style={{
                            width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_LIGNE,
                            display: 'flex', alignItems: 'center', padding: '0 10px', boxSizing: 'border-box',
                            fontSize: '0.8125rem', fontWeight: l.fort ? 600 : 400,
                            color: couleurLibelle(l.source),
                            borderBottom: '1px solid #f1f5f9',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                          }}
                        >
                          {libelleDeLigne(l)}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>

                {colonnesProjetees.map(colonne => (
                  <div key={colonne.cle}>
                    {blocsVisibles.map(bloc => (
                      <React.Fragment key={bloc.cle}>
                        <div style={{
                          width: LARGEUR_COMPARAISON, minWidth: LARGEUR_COMPARAISON, height: HAUTEUR_TITRE,
                          boxSizing: 'border-box', borderTop: bordureBloc(bloc)
                        }} />
                        {lignesRetenues(bloc).map(l => (
                          <div key={l.cle}>
                            {celluleValeur(formater(l[colonne.cle], l.format), LARGEUR_COMPARAISON, l.fort)}
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                ))}
              </div>
            </CorpsDefilant>
          </div>
        );
      })()}

      {vue === 'synthese' && donneesAffichables && (() => {
        const global = tableau.blocs.find(b => b.cle === 'global');

        /**
         * Où lire le budget, le COS et le CPA d'un bloc, selon qu'ils viennent d'une régie ou
         * d'une saisie.
         *
         * Un canal gratuit n'a pas de ligne « Budget » — il n'achète pas d'espace — mais il peut
         * porter un coût saisi à la main, sous `coutSaisi`. Cette vue n'interrogeait que les clés
         * de régie : un coût déclaré sur le SEO existait dans le détail et restait **invisible
         * ici**, sur la vue pourtant ouverte par défaut.
         */
        const EQUIVALENT_SAISI = { cost: 'coutSaisi', cos: 'cosSaisi', cpa: 'cpaSaisi' };
        const totalDe = (bloc, cle) => (
          bloc.lignes.find(l => l.cle === cle)?.total
          ?? (EQUIVALENT_SAISI[cle] ? bloc.lignes.find(l => l.cle === EQUIVALENT_SAISI[cle])?.total : undefined)
          ?? null
        );

        const caTotal = global ? totalDe(global, 'revenue') : null;
        const budgetTotal = global ? totalDe(global, 'cost') : null;

        /**
         * Valeur d'une cellule, avec son format.
         *
         * Les colonnes dérivées sont calculées ici, sur les totaux déjà rendus par le
         * serveur : les recalculer à partir des colonnes de tranches donnerait des ratios
         * de moyennes là où le reste du tableau tient des ratios de sommes.
         */
        /**
         * Les sous-blocs saisis à la main, dont les métriques se lisent sur la ligne du bloc
         * qu'ils complètent.
         *
         * La synthèse tient une ligne par bloc, et le back-office comme les objectifs sont des
         * enfants des résultats globaux : leurs colonnes seraient vides sur la ligne du parent
         * alors que c'est là qu'on les cherche. On les y ramène, plutôt que d'imposer un dépliage
         * pour lire trois chiffres.
         */
        const blocsSaisis = tableau.blocs.filter(b => b.cle === 'backOffice' || b.cle === 'objectifs');

        /**
         * Les blocs de la synthèse : les mêmes que partout, moins ceux que le serveur en écarte.
         *
         * Le back-office et les objectifs y figureraient comme des leviers, sous la ligne qui
         * porte déjà leurs chiffres en colonnes — deux lignes presque vides qui répètent ce qui
         * est juste au-dessus.
         *
         * Les deux groupes de colonnes doivent parcourir **cette même liste** : si l'un omettait
         * un bloc que l'autre garde, tout ce qui suit glisserait d'une ligne, et le tableau
         * afficherait des valeurs en face des mauvais leviers sans que rien ne le signale.
         */
        const blocsDeLaSynthese = blocsVisibles.filter(b => !b.horsSynthese);

        /**
         * Un bloc ne se replie ici que s'il a des enfants **dans cette vue**.
         *
         * `estRepliable` raisonne sur le tableau entier : « Résultats globaux » y porte le
         * back-office et les objectifs, qui n'entrent pas dans la synthèse. Le chevron s'affichait
         * donc devant une ligne dont le dépliage ne révélait rien.
         *
         * La parenté se lit sur **tous** les blocs de la synthèse, repliés compris, et non sur
         * les seuls blocs visibles : replier « TOTAL Leviers gratuits » retire ses enfants de la
         * liste affichée, donc de la parenté — le chevron disparaissait et la ligne cessait d'être
         * cliquable, sans plus aucun moyen de rouvrir ce qu'on venait de fermer.
         */
        const blocsDeLaSyntheseTous = tableau.blocs.filter(b => !b.horsSynthese);
        const clesDeLaSynthese = new Set(blocsDeLaSyntheseTous.map(b => b.cle));
        const aDesEnfantsIci = new Set(
          blocsDeLaSyntheseTous.filter(b => b.parent && clesDeLaSynthese.has(b.parent)).map(b => b.parent)
        );
        const replieIci = (bloc) => aDesEnfantsIci.has(bloc.cle);


        const valeurDeCellule = (bloc, colonne) => {
          if (!colonne.derivee) {
            const ligne = bloc.lignes.find(l => l.cle === colonne.cle)
              || (EQUIVALENT_SAISI[colonne.cle]
                ? bloc.lignes.find(l => l.cle === EQUIVALENT_SAISI[colonne.cle])
                : null);
            if (ligne) return { valeur: ligne.total, format: ligne.format };

            // Seul le bloc parent hérite : sur un levier, une colonne de back-office reste vide,
            // ce qui est exact — un levier n'en a pas.
            if (bloc.cle === 'global') {
              for (const saisi of blocsSaisis) {
                const heritee = saisi.lignes.find(l => l.cle === colonne.cle);
                if (heritee) return { valeur: heritee.total, format: heritee.format };
              }
            }
            return null;
          }

          const ca = totalDe(bloc, 'revenue');
          const budget = totalDe(bloc, 'cost');
          const sessions = totalDe(bloc, 'sessions');

          if (colonne.cle === 'poidsCa') {
            return { valeur: ratioSimple(ca, caTotal, 100), format: 'pourcentage' };
          }
          if (colonne.cle === 'poidsBudget') {
            return { valeur: ratioSimple(budget, budgetTotal, 100), format: 'pourcentage' };
          }
          return { valeur: ratioSimple(budget, sessions), format: 'deviseCentimes' };
        };

        /**
         * L'écart d'une cellule de synthèse avec la période de référence.
         *
         * Le même chemin de lecture que `valeurDeCellule` — équivalent saisi compris, héritage
         * des blocs de saisie sur `global` compris — mais dans le tableau de référence. Rejouer
         * ces deux règles ici n'est pas une duplication gratuite : c'est ce qui garantit que les
         * deux chiffres comparés désignent bien la même chose. Un `cost` courant face à un
         * `coutSaisi` de référence produirait un écart entre deux grandeurs différentes.
         */
        const ecartDeSynthese = (bloc, colonne, contenu) => {
          if (!tableauPrecedent) return null;

          const blocPrecedent = tableauPrecedent.blocs?.find(b => b.cle === bloc.cle);
          if (!blocPrecedent) return null;

          const lignePrecedente = blocPrecedent.lignes?.find(l => l.cle === colonne.cle)
            || (EQUIVALENT_SAISI[colonne.cle]
              ? blocPrecedent.lignes?.find(l => l.cle === EQUIVALENT_SAISI[colonne.cle])
              : null);

          if (lignePrecedente) return evolution(lignePrecedente.total, contenu.valeur);

          if (bloc.cle === 'global') {
            const saisisPrecedents = (tableauPrecedent.blocs || [])
              .filter(b => b.cle === 'backOffice' || b.cle === 'objectifs');
            for (const saisi of saisisPrecedents) {
              const heritee = saisi.lignes?.find(l => l.cle === colonne.cle);
              if (heritee) return evolution(heritee.total, contenu.valeur);
            }
          }
          return null;
        };

        /**
         * Une colonne de saisie sans la moindre valeur ne s'affiche pas.
         *
         * Renseigner un objectif de chiffre d'affaires sans objectif de ventes est un usage
         * courant ; garder les colonnes vides ferait payer à tout le monde la largeur de deux
         * métriques que personne ne remplit. Les colonnes standard, elles, restent : leur place
         * dans le tableau est acquise, et leur vide dit quelque chose.
         */
        const colonnesDuTableau = colonnesDuModele(
          tableau?.modele?.actif,
          tableau?.modele?.libelleConversions
        );
        const colonnesDeLaSynthese = colonnesDuTableau.filter(colonne => (
          !CLES_SAISIES_SYNTHESE.has(colonne.cle)
          || blocsDeLaSynthese.some(b => valeurDeCellule(b, colonne)?.valeur != null)
        ));

        /**
         * L'en-tête d'une colonne de métrique. Rendu **hors** du corps du tableau, comme
         * toutes les rangées figées de cet écran : voir `BarreFigee`.
         */
        const enteteDeColonne = (colonne) => (
          <div key={colonne.cle} style={{
            width: LARGEUR_SYNTHESE, minWidth: LARGEUR_SYNTHESE, height: HAUTEUR_ENTETE * 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 8px', boxSizing: 'border-box', textAlign: 'center',
            fontSize: '0.6875rem', fontWeight: 600, color: '#7c2d12',
            backgroundColor: colonne.ratio ? FOND_RATIO_ENTETE : FOND_ENTETE,
            lineHeight: 1.2
          }}>
            {colonne.titre}
          </div>
        );

        return (
          <div style={{ paddingBottom: '12px', marginBottom: '48px' }}>

            <BarreFigee>
              {/* La case d'angle, tenue à gauche comme la colonne qu'elle coiffe. Opaque :
                  les noms de leviers passent dessous. */}
              <div style={{
                position: 'sticky', left: 0, zIndex: 2,
                backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
              }}>
                <div style={{
                  width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_ENTETE * 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  padding: '0 10px', boxSizing: 'border-box',
                  fontSize: '0.75rem', fontWeight: 600, color: '#7c2d12'
                }}>
                  Levier
                </div>
              </div>
              {colonnesDeLaSynthese.map(colonne => enteteDeColonne(colonne))}
            </BarreFigee>

            <CorpsDefilant>
              <div style={{ display: 'flex', width: 'max-content' }}>

                {/* Colonne des leviers, tenue à gauche comme dans les deux autres vues */}
                <div style={{
                  position: 'sticky', left: 0, zIndex: 2,
                  backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
                }}>
                  {blocsDeLaSynthese.map(bloc => {
                    const rang = profondeur(bloc);
                    return (
                      <div
                        key={bloc.cle}
                        {...proprietesTitreBloc(bloc, replieIci)}
                        style={{
                          width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_LIGNE,
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: `0 10px 0 ${10 + rang * 18}px`, boxSizing: 'border-box',
                          fontSize: '0.8125rem', fontWeight: rang === 0 ? 700 : 400,
                          color: rang >= 2 ? '#64748b' : '#1f2937',
                          backgroundColor: rang === 0 ? 'rgba(148, 163, 184, 0.12)' : 'transparent',
                          borderBottom: '1px solid #f1f5f9',
                          cursor: replieIci(bloc) ? 'pointer' : 'default', userSelect: 'none',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}
                      >
                        {replieIci(bloc) && (estReplie(bloc.cle) ? <ChevronRight size={14} /> : <ChevronDown size={14} />)}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{bloc.titre}</span>
                        {/* Sans ce repère, rien ne dirait qu'il y a quelque chose à survoler. */}
                        {marqueurAide(bloc)}
                      </div>
                    );
                  })}
                </div>

                {/* Une colonne par métrique, son en-tête vivant au-dessus */}
                {colonnesDeLaSynthese.map(colonne => (
                  <div key={colonne.cle}>
                    {blocsDeLaSynthese.map(bloc => {
                      // Nommée `contenu` et non `cellule` : ce dernier est le helper de rendu
                      // du tableau chronologique, que masquer ici prêterait à confusion.
                      const contenu = valeurDeCellule(bloc, colonne);
                      const rang = profondeur(bloc);
                      return (
                        <div key={bloc.cle} style={{
                          width: LARGEUR_SYNTHESE, minWidth: LARGEUR_SYNTHESE, height: HAUTEUR_LIGNE,
                          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                          padding: '0 8px', boxSizing: 'border-box',
                          fontSize: '0.8125rem',
                          fontWeight: rang === 0 || colonne.fort ? 600 : 400,
                          color: '#1f2937',
                          backgroundColor: rang === 0 ? 'rgba(148, 163, 184, 0.12)' : 'transparent',
                          /**
                           * Le fond des rapports est peint **par-dessus** la couleur de fond,
                           * et non à sa place : la ligne des totaux porte déjà son propre gris,
                           * que remplacer romprait la bande en son milieu. Un dégradé d'une
                           * seule teinte est la façon la plus simple de superposer deux fonds.
                           */
                          ...(colonne.ratio ? {
                            backgroundImage: `linear-gradient(${FOND_RATIO_CELLULE}, ${FOND_RATIO_CELLULE})`
                          } : {}),
                          borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap'
                        }}>
                          {/**
                            * La valeur, et sous elle son écart avec la période de référence.
                            *
                            * Les trois colonnes **dérivées** — poids du CA, poids du budget, coût
                            * par visite — en sont exclues : elles se calculent ici à partir de
                            * deux totaux du même tableau, et leur équivalent de référence
                            * n'existe pas côté serveur. Afficher « — » y vaut mieux qu'un écart
                            * reconstruit d'un côté seulement.
                            */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px', lineHeight: 1.15 }}>
                            <span>{contenu ? formater(contenu.valeur, contenu.format) : ''}</span>
                            {(() => {
                              if (!contenu || colonne.derivee) return null;
                              const ecart = ecartDeSynthese(bloc, colonne, contenu);
                              if (ecart === null) return null;
                              return (
                                <span style={{
                                  fontSize: '0.6875rem',
                                  ...styleEcart(colonne.cle, ecart)
                                }}>
                                  {ecart > 0 ? '+' : ''}{decimal.format(ecart)} %
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </CorpsDefilant>
          </div>
        );
      })()}

      {/**
        * Vue Budgets : ce qui a été dépensé, régie par régie et mois par mois.
        *
        * ## Pourquoi une vue à part
        *
        * Le budget est déjà une colonne de la synthèse et une ligne des tableaux
        * chronologiques, mais jamais les deux à la fois : on y lit soit le total d'une régie
        * sur la période, soit le détail d'un mois pour toutes les métriques. La question
        * « combien ai-je dépensé sur Google Ads en août » demandait donc de croiser deux
        * écrans. C'est le seul objet de celui-ci.
        *
        * ## Quelles lignes
        *
        * Les régies, c'est-à-dire les blocs de **premier niveau** qui portent un budget :
        * Google Ads, Microsoft Ads, Meta, Awin… Les sous-leviers — Shopping, Performance Max,
        * Brand — sont volontairement écartés : ils détaillent une régie, ils ne s'ajoutent pas
        * à la liste des plateformes, et les afficher doublerait chaque montant.
        *
        * Un levier gratuit auquel un coût mensuel a été déclaré y figure au même titre : c'est
        * un budget consommé, et l'omettre ferait mentir le total.
        *
        * ## Ce qui est masqué, et ce qui ne l'est pas
        *
        * Une régie sans le moindre budget sur la période ne prend pas de ligne — Instagram et
        * LinkedIn n'en ont jamais, Meta ne facturant pas Instagram à part et Kokpit n'ayant pas
        * de connecteur LinkedIn. En revanche, un mois sans dépense sur une régie active reste
        * **vide** et non à zéro : la régie n'a peut-être rien dépensé, ou la donnée manque, et
        * un zéro trancherait à tort.
        */}
      {vue === 'budgets' && donneesAffichables && (() => {
        const ligneCout = (bloc) => (bloc.lignes || []).find(l => l.cle === 'cost');

        const plateformes = blocs
          .filter(b => profondeur(b) === 1 && ligneCout(b))
          // Une régie sans budget sur la période n'apporte qu'une ligne vide.
          .filter(b => (ligneCout(b).valeurs || []).some(v => v !== null && v !== undefined));

        if (plateformes.length === 0) {
          return (
            <div style={{
              border: '1px dashed #e5e7eb', borderRadius: '12px', padding: '32px',
              textAlign: 'center', color: '#6b7280', marginBottom: '48px'
            }}>
              <div style={{ fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                Aucun budget sur cette période
              </div>
              <div style={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                Le budget vient des régies rattachées à cette propriété. Rattachez un compte
                publicitaire depuis le gestionnaire, ou déclarez un coût mensuel dans la saisie
                manuelle.
              </div>
            </div>
          );
        }

        /**
         * Le total d'un mois se somme sur les plateformes affichées, et non sur le bloc
         * « TOTAL Leviers payants » : celui-ci ignore les coûts déclarés sur un levier
         * gratuit, qui figurent pourtant dans ce tableau. Sommer ce qu'on montre est aussi
         * la seule façon dont le lecteur puisse vérifier la colonne.
         */
        const totalDuMois = (index) => {
          const valeurs = plateformes
            .map(b => ligneCout(b).valeurs?.[index])
            .filter(v => v !== null && v !== undefined);
          return valeurs.length === 0 ? null : valeurs.reduce((t, v) => t + v, 0);
        };
        const totalGeneral = plateformes.reduce((t, b) => t + (ligneCout(b).total || 0), 0);

        const LARGEUR_MOIS = 128;
        const fondEntete = { backgroundColor: 'rgba(251, 146, 60, 0.12)' };
        const celluleStyle = (largeur, options = {}) => ({
          width: largeur, minWidth: largeur, height: HAUTEUR_LIGNE,
          display: 'flex', alignItems: 'center', justifyContent: options.gauche ? 'flex-start' : 'flex-end',
          padding: '0 10px', boxSizing: 'border-box',
          fontSize: '0.8125rem', fontWeight: options.fort ? 600 : 400, color: '#1f2937',
          backgroundColor: options.fond || 'transparent',
          borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap'
        });

        const FOND_TOTAL = 'rgba(148, 163, 184, 0.12)';

        return (
          <div style={{ overflowX: 'auto', paddingBottom: '12px', marginBottom: '48px' }}>
            <div style={{ display: 'flex', width: 'max-content' }}>

              {/* Les plateformes, tenues à gauche pendant le défilement des mois. */}
              <div style={{
                position: 'sticky', left: 0, zIndex: 2,
                backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
              }}>
                <div style={{
                  width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_ENTETE * 2,
                  // Aligné à gauche, comme les noms de régie qu'il coiffe. Les autres
                  // tableaux calent leur en-tête à droite parce que leur colonne de gauche
                  // porte des libellés de métriques, eux-mêmes alignés à droite contre les
                  // chiffres ; ici la colonne porte des noms, et la règle s'inverse.
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                  padding: '0 10px', boxSizing: 'border-box',
                  fontSize: '0.75rem', fontWeight: 600, color: '#7c2d12',
                  ...fondEntete
                }}>
                  Plateforme
                </div>
                {plateformes.map(bloc => (
                  <div key={bloc.cle} style={celluleStyle(LARGEUR_LIBELLES, { gauche: true })}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{bloc.titre}</span>
                  </div>
                ))}
                <div style={celluleStyle(LARGEUR_LIBELLES, { gauche: true, fort: true, fond: FOND_TOTAL })}>
                  TOTAL
                </div>
              </div>

              {/* Un mois par colonne */}
              {colonnes.map((colonne, index) => (
                <div key={colonne.cle}>
                  <div style={{
                    width: LARGEUR_MOIS, minWidth: LARGEUR_MOIS, height: HAUTEUR_ENTETE * 2,
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: '1px',
                    padding: '0 8px', boxSizing: 'border-box',
                    fontSize: '0.75rem', fontWeight: 600, color: '#7c2d12', ...fondEntete
                  }}>
                    {/* L'année est rappelée : une période à cheval sur deux ans afficherait
                        sinon deux « Janvier » impossibles à distinguer. */}
                    <span>{colonne.mois} {colonne.annee}</span>
                    {/**
                      * Les jours réellement couverts, sous le nom du mois.
                      *
                      * Un mois n'est presque jamais entier : le premier et le dernier de la
                      * période sont tronqués par les dates choisies. « Septembre 2026 » à
                      * 13 841 € sur une période de quatorze jours se lit comme un mois
                      * complet, et le budget mensuel paraît deux fois moindre qu'il n'est.
                      *
                      * Affiché sur **toutes** les colonnes, y compris les mois entiers :
                      * ne le montrer que sur les mois tronqués obligerait le lecteur à
                      * déduire de son absence que le mois est complet — une information qui
                      * ne se lit pas, et qu'il est plus honnête d'écrire.
                      */}
                    <span style={{ fontSize: '0.625rem', fontWeight: 400, color: '#9a6b4f' }}>
                      {joursCouverts(colonne)}
                    </span>
                  </div>
                  {plateformes.map(bloc => (
                    <div key={bloc.cle} style={celluleStyle(LARGEUR_MOIS)}>
                      {formater(ligneCout(bloc).valeurs?.[index], 'devise')}
                    </div>
                  ))}
                  <div style={celluleStyle(LARGEUR_MOIS, { fort: true, fond: FOND_TOTAL })}>
                    {formater(totalDuMois(index), 'devise')}
                  </div>
                </div>
              ))}

              {/* Le total de chaque plateforme, tenu à droite comme dans les autres tableaux. */}
              <div style={{
                position: 'sticky', right: 0, zIndex: 2,
                backgroundColor: 'white', borderLeft: '2px solid #e5e7eb'
              }}>
                <div style={{
                  width: LARGEUR_TOTAL, minWidth: LARGEUR_TOTAL, height: HAUTEUR_ENTETE * 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  padding: '0 10px', boxSizing: 'border-box',
                  fontSize: '0.6875rem', fontWeight: 600, color: '#9ca3af',
                  textTransform: 'uppercase', letterSpacing: '0.02em', ...fondEntete
                }}>
                  Total
                </div>
                {plateformes.map(bloc => (
                  <div key={bloc.cle} style={celluleStyle(LARGEUR_TOTAL, { fort: true })}>
                    {formater(ligneCout(bloc).total, 'devise')}
                  </div>
                ))}
                <div style={celluleStyle(LARGEUR_TOTAL, { fort: true, fond: FOND_TOTAL })}>
                  {formater(totalGeneral, 'devise')}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Aucune opération sur la période : un tableau sans colonne se lit comme une panne.
          On dit ce qui manque, et où le renseigner. */}
      {donneesAffichables && vue === 'operations' && (tableau?.colonnes || []).length === 0 && (
        <div style={{
          border: '1px dashed #e5e7eb', borderRadius: '12px', padding: '32px',
          textAlign: 'center', color: '#6b7280', marginBottom: '48px'
        }}>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            Aucune opération commerciale sur cette période
          </div>
          <div style={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
            Une opération est une note dont le sujet est «&nbsp;Opération commerciale&nbsp;».
            Sa date — ou sa plage de dates — devient une colonne de ce tableau.
            <br />
            Créez-en une depuis la page Notes, puis revenez ici.
          </div>
        </div>
      )}

      {/* Le tableau. Libellés à gauche, un jour par colonne, total à droite.
          `paddingBottom` laisse la place à la barre de défilement horizontale, qui
          viendrait sinon se coller à la dernière ligne ; `marginBottom` détache le tableau
          du bas de la page, où « TT » se retrouvait à ras bord. */}
      {donneesAffichables && (vue === 'tranches' || (vue === 'operations' && (tableau?.colonnes || []).length > 0)) && (() => {
        /**
         * Une colonne de tranche ne couvre pas forcément toute sa tranche.
         *
         * « Année » sur les sept derniers jours rend **une** colonne titrée 2026, qui ne porte
         * pourtant que ces sept jours — et un mois affiché sur une période qui commence le 21
         * s'arrête au 21, pas au 1er. Le titre seul se lit alors comme un total d'année ou de
         * mois entier, ce qu'il n'est pas.
         *
         * Les bornes réelles sont déjà dans la réponse (`debut`, `fin`, intersectées avec la
         * période) : il suffisait de les montrer. Elles s'affichent **toujours**, même quand la
         * tranche est complète — une absence qui signifierait « complet » obligerait à connaître
         * la règle pour lire le tableau.
         *
         * Le jour en est dispensé : sa colonne **est** une date, la répéter n'apprendrait rien.
         * Les opérations aussi, leur seconde ligne d'en-tête portant déjà leur plage.
         */
        const datesDesTranches = vue === 'tranches' && granularite !== 'jour';

        /** Les bornes d'une colonne, en JJ/MM — la forme des en-têtes de ce tableau. */
        const plageDeColonne = (c) => {
          if (!c.debut || !c.fin) return '';
          const court = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
          return c.debut === c.fin ? court(c.debut) : `${court(c.debut)} → ${court(c.fin)}`;
        };

        /**
         * Les en-têtes se répartissent en deux barres, et c'est une décision de lecture.
         *
         * Seules la ligne qui **nomme** la colonne — la dernière — et les dates couvertes
         * restent en haut de l'écran, avec les notes. Les repères intermédiaires — année,
         * mois, semaine, jour de semaine — se déduisent tous de la date : les figer coûterait
         * cent soixante-huit pixels de hauteur pour redire ce que la dernière ligne dit déjà.
         * Ils suivent donc le tableau et disparaissent sous les notes (`rang={null}`).
         *
         * Les dates couvertes n'existent que sur une tranche, où elles **sont** l'identité de
         * la colonne, et c'est alors la ligne qui porte le mot « Total ».
         */
        const enteteDuBas = entetes[entetes.length - 1];
        const entetesHautes = entetes.slice(0, -1);

        /**
         * Le bandeau des notes ne s'affiche que s'il a quelque chose à montrer.
         *
         * Une rangée de pastilles vide occupe la même hauteur qu'une rangée pleine, se teinte
         * comme un en-tête et ne dit rien — sur un compte sans note, c'est une ligne de
         * vingt-huit pixels qui annonce un contenu inexistant, et elle est figée, donc présente
         * en permanence. La question posée se lit à l'envers : on ne cherche pas s'il existe des
         * notes, mais si l'une d'elles **recoupe une colonne affichée** — le serveur en joint
         * qui peuvent tomber hors des tranches rendues.
         */
        const aDesNotes = colonnes.some(c => notesDeLaColonne(c).length > 0);

        /**
         * Un libellé de rangée d'en-tête, dans la colonne de gauche. Opaque : la barre est
         * figée, les lignes du tableau passent dessous.
         */
        const libelleEntete = (cle, libelle, discret) => (
          <div key={cle} style={{
            width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_ENTETE,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            padding: '0 10px', boxSizing: 'border-box',
            fontSize: discret ? '0.6875rem' : '0.75rem',
            fontWeight: discret ? 500 : 600,
            color: discret ? '#9ca3af' : '#7c2d12',
            backgroundColor: 'white', whiteSpace: 'nowrap'
          }}>
            {libelle}
          </div>
        );

        /**
         * L'infobulle d'une cellule d'en-tête.
         *
         * L'intitulé d'une opération est tronqué côté serveur pour tenir dans la colonne :
         * l'infobulle rend le texte entier, sans quoi deux opérations au nom proche
         * deviendraient indiscernables. Elle vaut pour **toute** rangée qui porte le nom —
         * en vue Opérations, c'est la première, la seconde portant la plage de dates.
         */
        const titreEntete = (e, c) => (
          e.cle === 'operation' ? (c.operationComplet || c.operation) : undefined
        );

        /** Une cellule d'en-tête de colonne, dans la teinte de sa journée. */
        const celluleEntete = (cle, c, contenu, titre) => (
          <div key={cle} title={titre} style={{
            width: largeurColonne, minWidth: largeurColonne, height: HAUTEUR_ENTETE,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxSizing: 'border-box', fontSize: '0.75rem', fontWeight: 500,
            color: '#7c2d12',
            // Le week-end est teinté : la saisonnalité hebdomadaire est la première chose
            // qu'on lit dans ce tableau.
            backgroundColor: c.weekend ? FOND_ENTETE_WEEKEND : FOND_ENTETE,
            whiteSpace: 'nowrap'
          }}>
            {contenu}
          </div>
        );

        /** La case de la colonne des totaux en face d'une rangée d'en-tête. */
        const enteteTotal = (cle, libelle) => (
          <div key={cle} style={{
            width: LARGEUR_TOTAL, minWidth: LARGEUR_TOTAL, height: HAUTEUR_ENTETE,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            padding: '0 10px', boxSizing: 'border-box',
            fontSize: '0.6875rem', fontWeight: 600, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.02em',
            backgroundColor: 'white'
          }}>
            {libelle}
          </div>
        );

        /** Les deux colonnes de bord, qui coiffent celles du corps. */
        const bordGauche = (enfants) => (
          <div style={{
            position: 'sticky', left: 0, zIndex: 2,
            backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
          }}>
            {enfants}
          </div>
        );
        const bordDroit = (enfants) => (
          <div style={{
            position: 'sticky', right: 0, zIndex: 2,
            backgroundColor: 'white', borderLeft: '2px solid #e5e7eb'
          }}>
            {enfants}
          </div>
        );

        return (
        <div style={{ paddingBottom: '12px', marginBottom: '48px' }}>

          {/* Les notes, en tête : une pastille par note, et le texte en infobulle. */}
          {aDesNotes && (
            <BarreFigee>
              {bordGauche(libelleEntete('notes', 'Notes'))}
              {colonnes.map(c => {
                const notes = notesDeLaColonne(c);
                return (
                  <div
                    key={c.date}
                    onMouseEnter={(e) => {
                      if (notes.length === 0) return;
                      const zone = e.currentTarget.getBoundingClientRect();
                      setNoteSurvolee({ notes, colonne: c, x: zone.left, bas: zone.bottom });
                    }}
                    onMouseLeave={() => setNoteSurvolee(null)}
                    style={{
                      width: largeurColonne, minWidth: largeurColonne, height: HAUTEUR_ENTETE,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
                      boxSizing: 'border-box', overflow: 'hidden',
                      backgroundColor: c.weekend ? FOND_ENTETE_WEEKEND : FOND_ENTETE,
                      cursor: notes.length > 0 ? 'help' : 'default'
                    }}
                  >
                    {/* Une pastille par note, sa couleur reprise de la note elle-même.
                        Le texte tient dans l'infobulle : une colonne de cent pixels ne
                        peut pas afficher une phrase, mais elle peut signaler qu'il y en
                        a une — et c'est déjà ce qui manque quand une courbe décroche. */}
                    {notes.slice(0, 4).map(n => (
                      <span key={n.id} style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        backgroundColor: couleurDeNote(n), flexShrink: 0
                      }} />
                    ))}
                    {notes.length > 4 && (
                      <span style={{ fontSize: '0.625rem', color: '#7c2d12' }}>+{notes.length - 4}</span>
                    )}
                  </div>
                );
              })}
              {bordDroit(<div style={{
                width: LARGEUR_TOTAL, minWidth: LARGEUR_TOTAL, height: HAUTEUR_ENTETE,
                backgroundColor: 'white'
              }} />)}
            </BarreFigee>
          )}

          {/* Les repères intermédiaires : ils suivent le tableau sans se figer, et passent
              sous les notes. Voir `entetesHautes` pour la raison. */}
          {entetesHautes.length > 0 && (
            <BarreFigee rang={null}>
              {bordGauche(entetesHautes.map(e => libelleEntete(e.cle, e.libelle)))}
              {colonnes.map((c, index) => (
                <div key={c.date}>
                  {entetesHautes.map(e => celluleEntete(
                    e.cle, c,
                    /* Les repères plus larges que la colonne ne se répètent qu'au changement
                       de valeur ; la ligne du bas, elle, identifie la colonne et s'écrit
                       toujours. Le repliement ne vaut que pour un calendrier, où « Août »
                       coiffe trente et une colonnes : deux opérations homonymes restent deux
                       opérations, et masquer le nom de la seconde la rendrait illisible. */
                    vue !== 'operations' && index > 0 && colonnes[index - 1][e.cle] === c[e.cle]
                      ? ''
                      : c[e.cle],
                    titreEntete(e, c)
                  ))}
                </div>
              ))}
              {bordDroit(entetesHautes.map(e => enteteTotal(e.cle, '')))}
            </BarreFigee>
          )}

          {/* La ligne qui nomme la colonne, et les dates qu'elle couvre : figées sous les
              notes, au contact des chiffres qu'elles coiffent. */}
          <BarreFigee rang={aDesNotes ? 1 : 0}>
            {bordGauche(
              <>
                {enteteDuBas && libelleEntete(enteteDuBas.cle, enteteDuBas.libelle)}
                {datesDesTranches && libelleEntete('periodeCouverte', 'Dates couvertes', true)}
              </>
            )}
            {colonnes.map(c => (
              <div key={c.date}>
                {enteteDuBas && celluleEntete(
                  enteteDuBas.cle, c, c[enteteDuBas.cle], titreEntete(enteteDuBas, c)
                )}
                {/* Les dates réellement couvertes par cette colonne. Jamais repliées d'une
                    colonne à l'autre, contrairement aux repères au-dessus : c'est précisément
                    ce qui change d'une tranche à la suivante. */}
                {datesDesTranches && (
                  <div style={{
                    width: largeurColonne, minWidth: largeurColonne, height: HAUTEUR_ENTETE,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxSizing: 'border-box', fontSize: '0.6875rem', fontWeight: 500,
                    color: '#a8785e',
                    backgroundColor: c.weekend ? FOND_ENTETE_WEEKEND : FOND_ENTETE,
                    whiteSpace: 'nowrap'
                  }}>
                    {plageDeColonne(c)}
                  </div>
                )}
              </div>
            ))}
            {bordDroit(
              <>
                {/* « Total » sur la dernière rangée d'en-tête — celle des dates quand elle
                    existe — pour qu'il reste au contact des chiffres qu'il coiffe. */}
                {enteteDuBas && enteteTotal(enteteDuBas.cle, datesDesTranches ? '' : 'Total')}
                {datesDesTranches && enteteTotal('periodeCouverte', 'Total')}
              </>
            )}
          </BarreFigee>

          <CorpsDefilant>
            <div style={{ display: 'flex', width: 'max-content' }}>

              {/* Colonne des libellés, tenue à gauche pendant le défilement des jours :
                  une valeur dont on ne lit plus la ligne ne veut rien dire. */}
              <div style={{
                position: 'sticky', left: 0, zIndex: 2,
                backgroundColor: 'white', borderRight: '2px solid #e5e7eb'
              }}>
                {blocsVisibles.map(bloc => (
                  <React.Fragment key={bloc.cle}>
                    <div
                      {...proprietesTitreBloc(bloc)}
                      style={{
                        width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_TITRE,
                        display: 'flex', alignItems: 'center', gap: '4px',
                        // Le retrait remplace le chevron, qui n'a plus lieu d'être sur un
                        // sous-levier : seuls les totaux se replient.
                        padding: `0 10px 0 ${10 + profondeur(bloc) * 18}px`,
                        boxSizing: 'border-box',
                        fontSize: '0.75rem', fontWeight: profondeur(bloc) === 0 ? 700 : 600,
                        color: profondeur(bloc) >= 2 ? '#a98a76' : (bloc.parent ? '#9a6b4f' : '#7c2d12'),
                        textTransform: 'uppercase', letterSpacing: '0.03em',
                        borderTop: bordureBloc(bloc),
                        cursor: estRepliable(bloc) ? 'pointer' : 'default', userSelect: 'none',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}
                    >
                      {estRepliable(bloc) && (estReplie(bloc.cle) ? <ChevronRight size={14} /> : <ChevronDown size={14} />)}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{bloc.titre}</span>
                      {/* Sans ce repère, rien ne dirait qu'il y a quelque chose à survoler. */}
                      {marqueurAide(bloc)}
                    </div>
                    {lignesRetenues(bloc).map(l => (
                      <div
                        key={l.cle}
                        title={l.aide || l.libelle}
                        style={{
                          width: LARGEUR_LIBELLES, minWidth: LARGEUR_LIBELLES, height: HAUTEUR_LIGNE,
                          display: 'flex', alignItems: 'center',
                          padding: '0 10px', boxSizing: 'border-box',
                          fontSize: '0.8125rem', fontWeight: l.fort ? 600 : 400,
                          // Les lignes calculées se distinguent des données brutes
                          color: couleurLibelle(l.source),
                          borderBottom: '1px solid #f1f5f9',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                        }}
                      >
                        {libelleDeLigne(l)}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>

              {/* Une colonne par jour */}
              {colonnes.map((c, index) => (
                <div key={c.date}>
                  {blocsVisibles.map(bloc => (
                    <React.Fragment key={bloc.cle}>
                      {/* Bande vide en face de l'intertitre : sans elle, les deux moitiés du
                          tableau se décaleraient d'une ligne à partir du second bloc. */}
                      <div style={{
                        width: largeurColonne, minWidth: largeurColonne, height: HAUTEUR_TITRE,
                        boxSizing: 'border-box', borderTop: bordureBloc(bloc),
                        backgroundColor: c.weekend ? 'rgba(251, 146, 60, 0.16)' : 'transparent'
                      }} />
                      {lignesRetenues(bloc).map(l => {
                        /**
                         * L'écart d'une colonne avec la colonne **de même rang** de la référence.
                         *
                         * Par le rang et non par la date : c'est la seule façon de faire coïncider
                         * ce que la comparaison désigne. Sur « sept derniers jours contre les sept
                         * d'avant », le premier jour affiché fait face au premier jour de la
                         * référence ; sur « année précédente », septembre 2026 fait face à
                         * septembre 2025, jour pour jour. Apparier par date ne rendrait jamais
                         * rien, les deux plages étant disjointes par construction.
                         *
                         * Quand les deux plages n'ont pas le même nombre de colonnes — février
                         * contre un mois de trente et un jours —, les colonnes surnuméraires
                         * restent sans écart plutôt que de se voir attribuer un vis-à-vis
                         * arbitraire.
                         *
                         * Les opérations en sont exclues : leurs colonnes ne sont pas des tranches
                         * de calendrier mais des événements nommés, et la troisième opération
                         * d'une période n'a rien à voir avec la troisième de la précédente.
                         */
                        const valeurPrecedente = vue === 'tranches'
                          ? lignesPrecedentesParBloc.get(bloc.cle)?.get(l.cle)?.valeurs?.[index]
                          : undefined;
                        const ecart = valeurPrecedente === undefined
                          ? null
                          : evolution(valeurPrecedente, l.valeurs[index]);

                        return (
                          <div key={l.cle}>
                            {ecart === null
                              ? cellule(formater(l.valeurs[index], l.format), {
                                largeur: largeurColonne,
                                weekend: c.weekend,
                                fort: l.fort
                              })
                              : celluleComparee(formater(l.valeurs[index], l.format), ecart, l.cle, {
                                largeur: largeurColonne,
                                weekend: c.weekend,
                                fort: l.fort
                              })}
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              ))}

              {/* Total, en dernière colonne et tenu à droite : sur quatre-vingt-dix jours,
                  un total qu'il faut aller chercher au bout du défilement n'est plus lu. */}
              <div style={{
                position: 'sticky', right: 0, zIndex: 2,
                backgroundColor: 'white', borderLeft: '2px solid #e5e7eb'
              }}>
                {blocsVisibles.map(bloc => (
                  <React.Fragment key={bloc.cle}>
                    <div style={{
                      width: LARGEUR_TOTAL, minWidth: LARGEUR_TOTAL, height: HAUTEUR_TITRE,
                      boxSizing: 'border-box', borderTop: bordureBloc(bloc)
                    }} />
                    {lignesRetenues(bloc).map(l => {
                      /**
                       * Le total de la période, et son écart avec la référence.
                       *
                       * C'est ici et nulle part ailleurs dans ce tableau : les colonnes sont des
                       * tranches, et la question posée est « cette période face à la précédente »,
                       * pas « ce mardi face au mardi d'avant ». Un écart sous chaque tranche
                       * demanderait une référence à la même échelle — le croisement que le serveur
                       * écarte, et dix-huit écrans de large en granularité jour.
                       */
                      const precedente = lignesPrecedentesParBloc.get(bloc.cle)?.get(l.cle);
                      const ecart = precedente ? evolution(precedente.total, l.total) : null;
                      return (
                        <div key={l.cle}>
                          {celluleComparee(formater(l.total, l.format), ecart, l.cle, {
                            largeur: LARGEUR_TOTAL,
                            fort: l.fort
                          })}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </CorpsDefilant>
        </div>
        );
      })()}
    </div>
  );
};

export default ConsolidatedDashboardView;
